import { useState, useCallback, useRef, useEffect, useMemo, useId, lazy, Suspense, Fragment } from "react";
import { MyBundlesPage, ShareModal, UserSettingsPage } from "./components/AccountPages";
import { CommunityIntelPage, PublishIntelModal } from "./components/CommunityIntel";
import { HomePage } from "./components/HomePage";
import { KnowledgePage, parseKbHash } from "./components/KnowledgeBase";
import { DashboardsPage, parseDashHash } from "./components/Dashboards";
import { SearchPalette, SearchLauncher, useSearchHotkey } from "./components/GlobalSearch";
import { finishBundle } from "./lib/stix21";
import { parseGraphHash } from "./components/graph/handoff";
import { parseCreateHash, parseCommunityHash } from "./components/create/route";
// The graph (Cytoscape) is only downloaded when a graph is opened.
const StixGraph = lazy(() => import("./components/graph/StixGraph"));
const GraphPage = lazy(() => import("./components/graph/GraphPage"));
const CreatePage = lazy(() => import("./components/create/CreatePage"));
const GeoView = lazy(() => import("./components/GeoView").then(m => ({ default: m.GeoView })));

// ─── Brand ───
let B = {
  primary: "#E4571E", primaryDark: "#C2410C",
  accent: "#A855F7", accentDark: "#7C3AED",
  bg: "#1C1C1E", bgCard: "#252528", bgPanel: "#1F1F22",
  border: "#323235", borderLight: "#3e3e42",
  text: "#F0EFED", textMuted: "#b0b0ba", textDim: "#74747e",
  white: "#F0EFED",
};

const DARK_B = { ...B };

// Soft mid-gray light palette — avoids harsh white, easier on the eyes
const LIGHT_B = {
  primary: "#E4571E", primaryDark: "#C2410C",
  accent: "#9333ea", accentDark: "#7C3AED",
  bg: "#d1d5db", bgCard: "#e5e7eb", bgPanel: "#c4c8d0",
  border: "#9ca3af", borderLight: "#b0b7c3",
  text: "#111827", textMuted: "#374151", textDim: "#6b7280",
  white: "#111827",
};

const UI_FONT_SCALE = 1.1;
const fs = (size) => Math.round(size * UI_FONT_SCALE);

const getDisplayName = (obj) => {
  if (!obj) return "";
  return obj.name || obj.value || obj.id || obj.type || "";
};

const getObjectDate = (obj) => {
  if (!obj) return null;
  const raw = obj.first_seen || obj.valid_from || obj.published || obj.first_observed || obj.created;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isFinite(date.getTime()) ? date : null;
};

// ─── API Backend Proxy ───
// The frontend calls /api/ask and /api/parse. The server holds the Anthropic secret.

// ─── AI Chat Panel ───
function AIChatPanel({ objects, relationships, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey! Ask me anything about this STIX bundle — threat actors, malware, indicators, relationships, whatever." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const buildContext = () => {
    const nonRels = objects.filter(o => o.type !== "relationship");
    const summary = nonRels.map(o => {
      const lines = [`[${o.type}] ${o.name || o.id}`];
      if (o.description) lines.push(`  Description: ${o.description}`);
      if (o.pattern) lines.push(`  Pattern: ${o.pattern}`);
      if (o.aliases) lines.push(`  Aliases: ${o.aliases.join(", ")}`);
      if (o.external_references) lines.push(`  Refs: ${o.external_references.map(r => r.external_id || r.url).join(", ")}`);
      return lines.join("\n");
    }).join("\n\n");

    const relSummary = relationships.map(r =>
      `${r.source_ref} --[${r.relationship_type}]--> ${r.target_ref}`
    ).join("\n");

    return `STIX Bundle Objects:\n${summary}\n\nRelationships:\n${relSummary}`;
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const context = buildContext();
      const res = await fetch("/api/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: userMsg,
          context,
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "No response received.";
      setMessages(prev => [...prev, { role: "assistant", text: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Error reaching the API. Check your key and network connection." }]);
    }
    setLoading(false);
  };

  return (
    <div style={CH.panel}>
      {/* Header */}
      <div style={CH.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={CH.aiDot} />
          <span style={{ fontWeight: 700, fontSize: fs(13), color: B.text }}>Ask AI</span>
          <span style={{ fontSize: fs(10), color: B.textDim, marginLeft: 2 }}>powered by Claude</span>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(16) }}>✕</button>
      </div>

      {/* Messages */}
      <div style={CH.messages}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10 }}>
            <div style={m.role === "user" ? CH.userBubble : CH.aiBubble}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
            <div style={CH.aiBubble}>
              <span style={{ opacity: 0.5 }}>Thinking…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={CH.inputRow}>
        <input
          style={CH.input}
          placeholder="Ask about this bundle…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          disabled={loading}
        />
        <button onClick={send} disabled={loading || !input.trim()} style={CH.sendBtn}>↑</button>
      </div>
    </div>
  );
}

const CH = {
  panel: { position: "absolute", bottom: 80, right: 20, width: 380, maxHeight: "70vh", background: B.bgPanel, border: `1px solid ${B.border}`, borderRadius: 12, display: "flex", flexDirection: "column", zIndex: 100, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${B.border}` },
  aiDot: { width: 8, height: 8, borderRadius: "50%", background: `linear-gradient(135deg, ${B.primary}, ${B.accent})` },
  keyWarning: { padding: "8px 14px", background: "#f59e0b11", borderBottom: `1px solid #f59e0b22` },
  messages: { flex: 1, overflowY: "auto", padding: "14px 14px 6px" },
  userBubble: { background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, color: B.white, borderRadius: "12px 12px 2px 12px", padding: "8px 12px", fontSize: fs(12), maxWidth: "80%", lineHeight: 1.5 },
  aiBubble: { background: B.bgCard, border: `1px solid ${B.border}`, color: B.text, borderRadius: "12px 12px 12px 2px", padding: "8px 12px", fontSize: fs(12), maxWidth: "85%", lineHeight: 1.6, whiteSpace: "pre-wrap" },
  inputRow: { display: "flex", gap: 8, padding: "10px 12px", borderTop: `1px solid ${B.border}` },
  input: { flex: 1, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "8px 12px", color: B.text, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" },
  sendBtn: { background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", borderRadius: 8, color: B.white, width: 34, height: 34, cursor: "pointer", fontSize: fs(16), fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
};

// ─── Global destinations, reachable from every page ───
// Sidebar order: what's new, then under headings (`under`) the knowledge itself, topics that cut
// across it, tools, your own work, and reference.
const APP_SECTIONS = [
  { l: "Home", p: "home" },
  { l: "Reports", p: "kb:reports", kb: "reports", hash: "#/kb/reports" },
  { l: "Threats", p: "kb:threats", kb: "threats", hash: "#/kb/groups", under: "Knowledge" },
  { l: "Arsenal", p: "kb:arsenal", kb: "arsenal", hash: "#/kb/malware", under: "Knowledge" },
  { l: "Frameworks", p: "kb:techniques", kb: "techniques", hash: "#/kb/frameworks", under: "Knowledge" },
  { l: "Indicators", p: "kb:indicators", kb: "indicators", hash: "#/kb/ips", under: "Knowledge" },
  { l: "Internet Watch", p: "kb:internet", kb: "internet", hash: "#/kb/ip-lookup", newUntil: "2026-12-03", under: "Knowledge" },
  { l: "Detection Rules", p: "kb:detections", kb: "detections", hash: "#/kb/rules", under: "Knowledge" },
  { l: "Geopolitics", p: "kb:geopolitics", kb: "geopolitics", hash: "#/kb/countries", newUntil: "2026-11-28", under: "Topics" },
  { l: "Critical Infrastructure", p: "kb:infrastructure", kb: "infrastructure", hash: "#/kb/ics", newUntil: "2026-12-01", under: "Topics" },
  { l: "Follow the Money", p: "kb:money", kb: "money", hash: "#/kb/wallets", newUntil: "2026-11-10", under: "Topics" },
  { l: "Dashboards", p: "dashboards", under: "Tools" },
  { l: "Graph", p: "graph", hash: "#/graph", newUntil: "2026-11-10", under: "Tools" },
  { l: "Analyze", p: "analyze", group: "analyze", landing: "pcap", under: "Tools" },
  { l: "Create", p: "create", hash: "#/create", newUntil: "2026-11-11", under: "Tools" },
  { l: "Community Intel", p: "community", newUntil: "2026-11-29", under: "Tools" },
  { l: "Workspace", p: "workspace", group: "workspace", landing: "viewer", under: "Your work" },
  { l: "OSINT Tools", p: "darkweb", under: "Your work" },
  { l: "STIX & TAXII", p: "kb:stix", kb: "stix", hash: "#/kb/stix", under: "Reference" },
];

// Pages that share one sidebar entry, with a tab strip across the top to move between them
// (like OpenCTI's Investigations, or VirusTotal's single "submit" box for files and URLs).
const PAGE_GROUPS = {
  workspace: { label: "Workspace", blurb: "Your STIX bundles and investigations", tabs: [
    { p: "viewer", l: "Viewer" },
    { p: "import", l: "Import", also: ["preview"] },
    { p: "bundles", l: "Saved" },
  ] },
  analyze: { label: "Analyze", blurb: "Submit a capture, file or URL", tabs: [
    { p: "pcap", l: "Network capture (PCAP)" },
    { p: "sandbox", l: "File & URL sandbox" },
  ] },
};
const GROUP_OF = Object.fromEntries(Object.entries(PAGE_GROUPS).flatMap(([g, def]) => def.tabs.flatMap(t => [t.p, ...(t.also || [])].map(p => [p, g]))));

function GroupTabs({ page, onNavigate, style }) {
  const group = PAGE_GROUPS[GROUP_OF[page]];
  if (!group || (LOCKDOWN && GROUP_OF[page] !== "analyze")) return null; // the public preview has no workspace to move around in
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "flex-end", gap: 4, padding: "0 20px", background: B.bgPanel, borderBottom: `1px solid ${B.border}`, flexShrink: 0, overflowX: "auto", ...style }}>
      <div style={{ padding: "10px 14px 9px 0", marginRight: 6, borderRight: `1px solid ${B.border}`, whiteSpace: "nowrap" }}>
        <div style={{ fontSize: fs(13), fontWeight: 900, color: B.text }}>{group.label}</div>
        <div style={{ fontSize: fs(10), color: B.textDim }}>{group.blurb}</div>
      </div>
      {group.tabs.map(t => {
        const on = t.p === page || (t.also || []).includes(page);
        return (
          <button key={t.p} onClick={() => onNavigate(t.p)} aria-current={on ? "page" : undefined}
            style={{ background: "none", border: "none", borderBottom: `2px solid ${on ? B.primary : "transparent"}`, color: on ? B.text : B.textDim, padding: "12px 14px 10px", cursor: "pointer", fontSize: fs(13), fontWeight: on ? 700 : 500, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
            {t.l}
          </button>
        );
      })}
    </div>
  );
}

// Pages that need an account. In the public preview (LOCKDOWN on the server) they
// can't do anything, so the sidebar leaves them out. Analyze stays visible but
// locked: its tools are shown, and uploads are turned off (the server refuses them too).
const ACCOUNT_PAGES = new Set(["workspace", "darkweb"]);
let LOCKDOWN = false; // set from /api/me in App, like B
let ANALYZE_LOCKED = false; // Analyze uploads off: the preview, unless the server opens them (ANALYZE_PUBLIC)
let ANALYZE_MAX_MB = 10; // the server's upload limit for Analyze
let ANALYZE_PER_HOUR = 20; // analyses per address per hour
const visibleSections = (user) => APP_SECTIONS.filter(s => !(LOCKDOWN && !user && ACCOUNT_PAGES.has(s.p)));
// A section with newUntil gets a "New" tag in the sidebar until that date, then it goes by itself.
const isNewSection = (s) => !!s.newUntil && Date.now() < Date.parse(`${s.newUntil}T00:00:00Z`);

// Shown in place of an analysis tool's upload controls while the site is locked down.
function ToolLocked({ what, style }) {
  return (
    <div role="status" style={{ border: `2px dashed ${B.border}`, borderRadius: 16, padding: "56px 24px", textAlign: "center", background: B.bgCard, ...style }}>
      <svg xmlns="http://www.w3.org/2000/svg" width={34} height={34} viewBox="0 0 24 24" fill="none" stroke={B.textDim} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      <div style={{ fontSize: fs(17), fontWeight: 800, color: B.text, marginTop: 12 }}>Locked in the public preview</div>
      <div style={{ fontSize: fs(13), color: B.textMuted, margin: "8px auto 0", maxWidth: 560, lineHeight: 1.6 }}>
        {what} Uploads and submissions are turned off on this site for now, so nothing can be analysed here yet.
      </div>
    </div>
  );
}

function SectionIcon({ page, color, size = 16 }) {
  const p = { xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", style: { flexShrink: 0 } };
  switch (page) {
    case "home": return <svg {...p}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>;
    case "graph":
    case "viewer": return <svg {...p}><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="8" r="2.5"/><circle cx="10" cy="18" r="2.5"/><line x1="8.2" y1="7" x2="15.6" y2="7.6"/><line x1="7" y1="8.3" x2="9.2" y2="15.6"/><line x1="16.3" y1="10" x2="11.8" y2="16.2"/></svg>;
    case "create": return <svg {...p}><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>;
    case "import": return <svg {...p}><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M4 21h16"/></svg>;
    case "analyze":
    case "pcap": return <svg {...p}><polyline points="2 12 6 12 9 4 15 20 18 12 22 12"/></svg>;
    case "sandbox": return <svg {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z"/></svg>;
    case "darkweb": return <svg {...p}><circle cx="6.5" cy="15.5" r="3.5"/><circle cx="17.5" cy="15.5" r="3.5"/><path d="M10 15.5h4"/><path d="M5 12V7.5L7.5 5h1.5v7"/><path d="M19 12V7.5L16.5 5H15v7"/></svg>;
    case "dashboards": return <svg {...p}><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>;
    case "kb:reports": return <svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h5"/></svg>;
    case "kb:threats": return <svg {...p}><path d="M12 3a7 7 0 0 0-7 7c0 2.5 1.3 4.2 3 5.2V19h8v-3.8c1.7-1 3-2.7 3-5.2a7 7 0 0 0-7-7z"/><circle cx="9.5" cy="10.5" r="1.3"/><circle cx="14.5" cy="10.5" r="1.3"/><path d="M10 19v2M14 19v2"/></svg>;
    case "kb:arsenal": return <svg {...p}><path d="M8 9a4 4 0 0 1 8 0v5a4 4 0 0 1-8 0z"/><path d="M12 9v9"/><path d="M4 12h4M16 12h4M5 6l3 2M19 6l-3 2M5 19l3-2M19 19l-3-2"/></svg>;
    case "kb:techniques": return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18M15 3v18"/></svg>;
    case "kb:detections": return <svg {...p}><path d="M3 5h18M3 12h11M3 19h7"/><circle cx="17.5" cy="16.5" r="3.5"/><path d="M20 19l2 2"/></svg>;
    case "kb:indicators": return <svg {...p}><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="1.5"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/></svg>;
    case "kb:money": return <svg {...p}><path d="M3 7.5h15.5A2.5 2.5 0 0 1 21 10v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18z"/><path d="M3 7.5L15.5 3.5v4"/><circle cx="16.5" cy="14" r="1.4"/></svg>;
    case "kb:infrastructure": return <svg {...p}><path d="M3 21V11l5 3.5V11l5 3.5V4h5v17z"/><path d="M2 21h20"/><path d="M6.5 17.5h2M11.5 17.5h2"/></svg>;
    case "kb:internet": return <svg {...p}><circle cx="12" cy="12" r="2"/><path d="M8.5 15.5a5 5 0 0 1 0-7"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M5.6 18.4a9 9 0 0 1 0-12.8"/><path d="M18.4 5.6a9 9 0 0 1 0 12.8"/></svg>
    case "kb:geopolitics": return <svg {...p}><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z"/></svg>;
    case "kb:stix": return <svg {...p}><path d="M8 3H7a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h1"/><path d="M16 3h1a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-1"/></svg>;
    case "knowledge": return <svg {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;
    case "community": return <svg {...p}><circle cx="9" cy="8" r="3.5"/><path d="M2 20c0-3.9 3.1-7 7-7s7 3.1 7 7"/><path d="M16 4.5a3.5 3.5 0 0 1 0 7"/><path d="M19 13.5c1.8 1 3 3.2 3 6.5"/></svg>;
    case "workspace":
    case "bundles": return <svg {...p}><path d="M12 2l9 5-9 5-9-5z"/><path d="M3 12l9 5 9-5"/><path d="M3 17l9 5 9-5"/></svg>;
    case "settings": return <svg {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>;
    case "login": return <svg {...p}><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="M10 17l5-5-5-5"/><line x1="15" y1="12" x2="3" y2="12"/></svg>;
    default: return null;
  }
}

// ─── Login Page ───
function LoginPage({ onNavigate, user, onLogout, pcapSession, collapsed, onToggleCollapse, onAuth, onResendVerification, lockdown }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    const handler = () => {
      const result = window.__verifyResult;
      if (!result) return;
      delete window.__verifyResult;
      if (result.info) setInfo(result.info);
      if (result.error) setError(result.error);
    };
    window.addEventListener("verifyresult", handler);
    return () => window.removeEventListener("verifyresult", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please fill in all fields."); return; }
    const elapsed = Date.now() - formLoadTime.current;
    if (isSignup && elapsed < 1500) {
      setError("Please slow down.");
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const result = await onAuth({ email, password, signup: isSignup, honeypot, elapsed });
      if (result?.info) {
        setInfo(result.info);
      }
    } catch (err) {
      setError(err.message || "Auth failed.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email) {
      setError("Enter your email to resend verification.");
      return;
    }
    setResendLoading(true);
    setError("");
    setInfo("");
    try {
      const result = await onResendVerification(email);
      if (result?.info) {
        setInfo(result.info);
      }
    } catch (err) {
      setError(err.message || "Unable to resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="login" onNavigate={onNavigate} user={user} onLogin={() => onNavigate("login")} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column" }}>
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <Logo size={56} />
            <h1 style={{ fontSize: fs(24), fontWeight: 900, margin: "16px 0 6px" }}>{isSignup ? "Create an account" : "Welcome back"}</h1>
            <p style={{ fontSize: fs(13), color: B.textMuted, margin: 0 }}>{isSignup ? "Sign up for your private Idle Hour workspace." : "Sign in to your Idle Hour account."}</p>
          </div>
          {lockdown ? (
            <div style={{ fontSize: fs(13), color: B.textMuted, textAlign: "center", lineHeight: 1.6, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "18px 20px" }}>
              Idle Hour is in preview mode. Sign-in and account creation are disabled for now.
            </div>
          ) : (<>
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* honeypot — hidden from users, bots fill it in */}
            <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} />
            <div>
              <label style={LG.label}>Email</label>
              <input style={LG.input} type="email" placeholder="you@example.com" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} />
            </div>
            <div>
              <label style={LG.label}>Password</label>
              <input style={LG.input} type="password" placeholder="••••••••" value={password} onChange={e => { setPassword(e.target.value); setError(""); }} />
            </div>
            {error && <div style={{ fontSize: fs(11), color: "#ef4444", textAlign: "center" }}>{error}</div>}
            {info && (
              <div style={{ fontSize: fs(11), color: "#32c48d", textAlign: "center", lineHeight: 1.6 }}>
                {info.includes("http") ? (
                  <>
                    {info.split(/(https?:\/\/\S+)/g).map((part, i) =>
                      part.startsWith("http")
                        ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: "#32c48d", wordBreak: "break-all" }}>{part}</a>
                        : <span key={i}>{part}</span>
                    )}
                  </>
                ) : info}
              </div>
            )}
            <button type="submit" disabled={loading} style={{ ...HP.primaryBtn, width: "100%", marginTop: 6, padding: "13px" }}>{isSignup ? "Create Account" : "Sign In"}</button>
            {!isSignup && error === "Email not verified. Please check your email for a verification link." && (
              <button type="button" disabled={resendLoading} onClick={resend} style={{ ...HP.secondaryBtn, width: "100%", marginTop: 8, padding: "13px" }}>
                {resendLoading ? "Resending…" : "Resend verification email"}
              </button>
            )}
          </form>
          <div style={{ textAlign: "center", marginTop: 20, display: "flex", justifyContent: "center", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: fs(11), color: B.textDim }}>{isSignup ? "Already have an account?" : "New to Idle Hour?"}</span>
            <button type="button" onClick={() => { setIsSignup(!isSignup); setError(""); setInfo(""); }} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(11), padding: 0 }}>{isSignup ? "Sign in" : "Create account"}</button>
          </div>
          </>)}
        </div>
      </div>
      </div>
    </div>
  );
}

const LG = {
  label: { fontSize: fs(11), fontWeight: 600, color: B.textMuted, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 6 },
  input: { width: "100%", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "10px 14px", color: B.text, fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", outline: "none", boxSizing: "border-box" },
};

// ─── Import Page ───
const INGEST_TYPES = [
  { stixType: "indicator", subtype: "ipv4",   label: "IPv4" },
  { stixType: "indicator", subtype: "ipv6",   label: "IPv6" },
  { stixType: "indicator", subtype: "domain", label: "Domain" },
  { stixType: "indicator", subtype: "url",    label: "URL" },
  { stixType: "indicator", subtype: "sha256", label: "SHA-256" },
  { stixType: "indicator", subtype: "sha1",   label: "SHA-1" },
  { stixType: "indicator", subtype: "md5",    label: "MD5" },
  { stixType: "indicator", subtype: "email",  label: "Email" },
  { stixType: "vulnerability",  subtype: "cve",   label: "CVE" },
  { stixType: "vulnerability",  subtype: "name",  label: "Vulnerability" },
  { stixType: "attack-pattern", subtype: "mitre", label: "ATT&CK" },
  { stixType: "attack-pattern", subtype: "name",  label: "Attack Pattern" },
  { stixType: "malware",        subtype: "name",  label: "Malware" },
  { stixType: "threat-actor",   subtype: "name",  label: "Threat Actor" },
  { stixType: "tool",           subtype: "name",  label: "Tool" },
  { stixType: "campaign",       subtype: "name",  label: "Campaign" },
  { stixType: "autonomous-system", subtype: "asn", label: "ASN" },
  { stixType: "unknown",        subtype: "unknown", label: "Unknown" },
];

function buildBundleFromSuggestions(suggestions) {
  const now = new Date().toISOString();
  const uid = () => crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 18).padEnd(36, "0");
  const objects = [];
  for (const s of suggestions.filter(s => s.include && s.stixType !== "unknown")) {
    if (s.stixType === "indicator" && s.pattern) {
      const subtypeMap = { ipv4: "malicious-activity", ipv6: "malicious-activity", domain: "malicious-activity", url: "malicious-activity", sha256: "malicious-activity", sha1: "malicious-activity", md5: "malicious-activity", email: "malicious-activity" };
      objects.push({ type: "indicator", spec_version: "2.1", id: `indicator--${uid()}`, created: now, modified: now, name: s.value, pattern: s.pattern, pattern_type: "stix", valid_from: now, indicator_types: [subtypeMap[s.subtype] || "unknown"] });
    } else if (s.stixType === "vulnerability") {
      const obj = { type: "vulnerability", spec_version: "2.1", id: `vulnerability--${uid()}`, created: now, modified: now, name: s.name || s.value };
      if (s.subtype === "cve") obj.external_references = [{ source_name: "cve", external_id: s.value.toUpperCase(), url: `https://nvd.nist.gov/vuln/detail/${s.value.toUpperCase()}` }];
      objects.push(obj);
    } else if (s.stixType === "attack-pattern") {
      const obj = { type: "attack-pattern", spec_version: "2.1", id: `attack-pattern--${uid()}`, created: now, modified: now, name: s.externalId || s.name || s.value };
      if (s.externalId) obj.external_references = [{ source_name: "mitre-attack", external_id: s.externalId, url: `https://attack.mitre.org/techniques/${s.externalId.replace(".", "/")}` }];
      objects.push(obj);
    } else if (s.stixType === "malware") {
      objects.push({ type: "malware", spec_version: "2.1", id: `malware--${uid()}`, created: now, modified: now, name: s.name || s.value, is_family: false, malware_types: ["unknown"] });
    } else if (s.stixType === "threat-actor") {
      objects.push({ type: "threat-actor", spec_version: "2.1", id: `threat-actor--${uid()}`, created: now, modified: now, name: s.name || s.value, threat_actor_types: ["unknown"] });
    } else if (["tool", "campaign", "intrusion-set"].includes(s.stixType)) {
      objects.push({ type: s.stixType, spec_version: "2.1", id: `${s.stixType}--${uid()}`, created: now, modified: now, name: s.name || s.value });
    } else if (s.stixType === "autonomous-system") {
      objects.push({ type: "autonomous-system", spec_version: "2.1", id: `autonomous-system--${uid()}`, number: s.number || parseInt(s.value.replace(/^AS/i, "")), name: s.value });
    }
  }
  return { type: "bundle", id: `bundle--${uid()}`, objects };
}

function ImportPage({ onNavigate, user, onLogin, onLogout, pcapSession, collapsed, onToggleCollapse, onLoad }) {
  const [tab, setTab] = useState("bundle");
  const [jsonText, setJsonText] = useState("");
  const [docText, setDocText] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [parseError, setParseError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [iocDragOver, setIocDragOver] = useState(false);
  const [iocLoading, setIocLoading] = useState(false);
  const [iocError, setIocError] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const jsonRef = useRef(null);
  const docRef = useRef(null);
  const iocRef = useRef(null);

  const loadBundle = () => {
    try {
      const d = JSON.parse(jsonText);
      if (d.type !== "bundle" || !d.objects) { setJsonError("Not a valid STIX 2.1 bundle."); return; }
      onLoad(d);
    } catch { setJsonError("Invalid JSON — check formatting."); }
  };

  const handleJsonFile = (file) => {
    const r = new FileReader();
    r.onload = (e) => { setJsonText(e.target.result); setJsonError(""); };
    r.readAsText(file);
  };

  const handleDocFile = (file) => {
    if (file.type === "application/pdf") {
      setParseError("PDF support coming soon — paste the report text below instead.");
      return;
    }
    const r = new FileReader();
    r.onload = (e) => { setDocText(e.target.result); setParseError(""); };
    r.readAsText(file);
  };

  const parseWithAI = async () => {
    if (!docText.trim()) { setParseError("No text to parse."); return; }
    setParsing(true); setParseError(""); setPreview(null);
    try {
      const res = await fetch("/api/parse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docText }),
      });
      const data = await res.json();
      if (data.error) { setParseError(data.error); setParsing(false); return; }
      const raw = data.content?.[0]?.text || "";
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      if (!jsonMatch) { setParseError("Could not extract valid JSON from response."); setParsing(false); return; }
      const bundle = JSON.parse(jsonMatch[0]);
      if (!bundle.objects) { setParseError("Parsed bundle has no objects."); setParsing(false); return; }
      setPreview(bundle);
    } catch (e) {
      setParseError("Parsing failed: " + e.message);
    }
    setParsing(false);
  };

  const nonRels = preview?.objects?.filter(o => o.type !== "relationship") || [];

  const handleIocFile = async (file) => {
    if (!file) return;
    setIocError(""); setSuggestions(null); setIocLoading(true); setTypeFilter("all");
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch("/api/ingest", { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) { setIocError(data.error || "Failed to parse file."); return; }
      setSuggestions(data.suggestions);
    } catch (e) {
      setIocError("Upload failed: " + e.message);
    } finally {
      setIocLoading(false);
      if (iocRef.current) iocRef.current.value = "";
    }
  };

  const toggleInclude = (id) => setSuggestions(prev => prev.map(s => s.id === id ? { ...s, include: !s.include } : s));
  const setType = (id, stixType, subtype, label) => setSuggestions(prev => prev.map(s => s.id === id ? { ...s, stixType, subtype, label, include: stixType !== "unknown" } : s));
  const toggleAll = (val) => setSuggestions(prev => prev.map(s => ({ ...s, include: s.stixType !== "unknown" ? val : false })));

  const filteredSuggestions = suggestions?.filter(s => typeFilter === "all" || (typeFilter === "unknown" ? s.stixType === "unknown" : s.stixType === typeFilter)) || [];
  const includedCount = suggestions?.filter(s => s.include).length || 0;
  const typeSummary = suggestions ? Object.entries(suggestions.reduce((acc, s) => { acc[s.stixType] = (acc[s.stixType] || 0) + 1; return acc; }, {})).sort((a, b) => b[1] - a[1]) : [];
  const CONF_COLOR = { high: "#22c55e", medium: "#f59e0b", low: "#f97316", none: "#ef4444" };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="import" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column" }}>
      <GroupTabs page="import" onNavigate={onNavigate} />
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>

      <div style={{ flex: 1, padding: "40px", maxWidth: suggestions ? 1100 : 780, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: "0 0 6px" }}>Import Intelligence</h1>
        <p style={{ fontSize: fs(13), color: B.textMuted, margin: "0 0 32px" }}>Load a STIX bundle, paste a threat report, or drop a CSV/XLSX/TXT IOC file.</p>

        {/* Tabs */}
        <div style={{ display: "flex", background: B.bgCard, borderRadius: 8, padding: 3, gap: 2, marginBottom: 28, width: "fit-content" }}>
          {[["bundle", "STIX Bundle"], ["parse", "Parse Document"], ["ioc", "IOC File"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ ...HP.primaryBtn, ...(tab === k ? {} : { background: "transparent", color: B.textDim, fontWeight: 500 }), padding: "7px 20px", fontSize: fs(12) }}>{l}</button>
          ))}
        </div>

        {tab === "bundle" && (
          <div>
            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleJsonFile(f); }}
              onClick={() => jsonRef.current?.click()}
              style={{ border: `2px dashed ${dragOver ? B.accent : B.border}`, borderRadius: 10, padding: "32px 24px", textAlign: "center", cursor: "pointer", marginBottom: 16, background: dragOver ? B.accent + "08" : "transparent", transition: "all 0.15s" }}
            >
              <TypeIcon type="report" color={B.textDim} size={28} />
              <div style={{ marginTop: 10, fontSize: fs(13), color: B.textMuted }}>Drop a STIX JSON file here</div>
              <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4 }}>or click to browse</div>
              <input ref={jsonRef} type="file" accept=".json" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleJsonFile(e.target.files[0]); }} />
            </div>
            <div style={{ textAlign: "center", color: B.textDim, fontSize: fs(11), marginBottom: 16 }}>— or paste JSON below —</div>
            <textarea value={jsonText} onChange={e => { setJsonText(e.target.value); setJsonError(""); }} placeholder='{"type": "bundle", "objects": [...]}' style={{ width: "100%", height: 200, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14, color: B.text, fontSize: fs(11), fontFamily: "monospace", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            {jsonError && <div style={{ fontSize: fs(11), color: "#ef4444", marginTop: 6 }}>{jsonError}</div>}
            <button onClick={loadBundle} disabled={!jsonText.trim()} style={{ ...HP.primaryBtn, marginTop: 14, opacity: jsonText.trim() ? 1 : 0.4 }}>Load Bundle →</button>
          </div>
        )}

        {tab === "parse" && (
          <div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleDocFile(f); }}
              onClick={() => docRef.current?.click()}
              style={{ border: `2px dashed ${dragOver ? B.accent : B.border}`, borderRadius: 10, padding: "32px 24px", textAlign: "center", cursor: "pointer", marginBottom: 16, background: dragOver ? B.accent + "08" : "transparent", transition: "all 0.15s" }}
            >
              <TypeIcon type="note" color={B.textDim} size={28} />
              <div style={{ marginTop: 10, fontSize: fs(13), color: B.textMuted }}>Drop a .txt or .md threat report here</div>
              <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4 }}>PDF: paste text below instead</div>
              <input ref={docRef} type="file" accept=".txt,.md" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleDocFile(e.target.files[0]); }} />
            </div>
            <div style={{ textAlign: "center", color: B.textDim, fontSize: fs(11), marginBottom: 16 }}>— or paste report text below —</div>
            <textarea value={docText} onChange={e => { setDocText(e.target.value); setParseError(""); setPreview(null); }} placeholder="Paste threat report text here — APT names, malware, IPs, domains, T-codes, CVEs…" style={{ width: "100%", height: 180, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14, color: B.text, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            {parseError && <div style={{ fontSize: fs(11), color: "#ef4444", marginTop: 6 }}>{parseError}</div>}
            <button onClick={parseWithAI} disabled={parsing || !docText.trim()} style={{ ...HP.primaryBtn, marginTop: 14, opacity: (!parsing && docText.trim()) ? 1 : 0.4 }}>
              {parsing ? "Parsing…" : "✦ Parse with AI"}
            </button>

            {preview && (
              <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: fs(13), fontWeight: 700, color: B.text, marginBottom: 12 }}>
                  Extracted {nonRels.length} objects · {preview.objects.filter(o => o.type === "relationship").length} relationships
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
                  {nonRels.map(o => { const c = getConf(o.type); return (
                    <span key={o.id} style={{ fontSize: fs(10), padding: "3px 10px", borderRadius: 10, background: c.color + "15", color: c.color, border: `1px solid ${c.color}20`, display: "flex", alignItems: "center", gap: 4 }}>
                      <TypeIcon type={o.type} color={c.color} size={10} />{o.name || o.id}
                    </span>
                  ); })}
                </div>
                <button onClick={() => onLoad(preview)} style={{ ...HP.primaryBtn }}>Load into Viewer →</button>
              </div>
            )}
          </div>
        )}

        {tab === "ioc" && (
          <div>
            {!suggestions && (
              <div
                onDragOver={e => { e.preventDefault(); setIocDragOver(true); }}
                onDragLeave={() => setIocDragOver(false)}
                onDrop={e => { e.preventDefault(); setIocDragOver(false); handleIocFile(e.dataTransfer.files[0]); }}
                onClick={() => iocRef.current?.click()}
                style={{ border: `2px dashed ${iocDragOver ? B.accent : B.border}`, borderRadius: 10, padding: "40px 24px", textAlign: "center", cursor: "pointer", background: iocDragOver ? B.accent + "08" : "transparent", transition: "all 0.15s" }}
              >
                <TypeIcon type="observed-data" color={B.textDim} size={30} />
                <div style={{ marginTop: 12, fontSize: fs(14), color: B.text, fontWeight: 700 }}>Drop an IOC file here</div>
                <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 6 }}>CSV, XLSX, or TXT — IPs, domains, hashes, CVEs, URLs, T-codes</div>
                <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4 }}>or click to browse</div>
                <input ref={iocRef} type="file" accept=".csv,.xlsx,.xls,.txt" style={{ display: "none" }} onChange={e => handleIocFile(e.target.files[0])} />
              </div>
            )}
            {iocLoading && <div style={{ textAlign: "center", padding: 40, color: B.textMuted, fontSize: fs(13) }}>Parsing file…</div>}
            {iocError && <div style={{ fontSize: fs(12), color: "#ef4444", marginTop: 10 }}>{iocError}</div>}

            {suggestions && (
              <div>
                {/* Summary bar */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                  <button onClick={() => { setSuggestions(null); setIocError(""); }} style={{ ...HP.navBtn, fontSize: fs(11), padding: "5px 12px" }}>← New File</button>
                  <div style={{ flex: 1, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[["all", "All", suggestions.length], ...typeSummary.map(([t, n]) => [t, getConf(t).label || t, n])].map(([k, l, n]) => (
                      <button key={k} onClick={() => setTypeFilter(k)} style={{ fontSize: fs(10), padding: "4px 10px", borderRadius: 999, fontWeight: 700, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", background: typeFilter === k ? (k === "all" ? B.primary : getConf(k).color) : B.bgCard, color: typeFilter === k ? "#fff" : B.textMuted, border: `1px solid ${typeFilter === k ? "transparent" : B.border}` }}>
                        {l} {n}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => toggleAll(true)} style={{ ...HP.navBtn, fontSize: fs(11), padding: "5px 10px" }}>All</button>
                    <button onClick={() => toggleAll(false)} style={{ ...HP.navBtn, fontSize: fs(11), padding: "5px 10px" }}>None</button>
                  </div>
                </div>

                {/* Table */}
                <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 100px 140px 70px", background: B.bgPanel, borderBottom: `1px solid ${B.border}`, padding: "8px 14px", gap: 12 }}>
                    {["", "Value", "Source Col", "Type", "Conf"].map((h, i) => (
                      <div key={i} style={{ fontSize: fs(10), fontWeight: 800, color: B.textDim, textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</div>
                    ))}
                  </div>
                  <div style={{ maxHeight: 480, overflowY: "auto" }}>
                    {filteredSuggestions.slice(0, 500).map(s => {
                      const c = getConf(s.stixType);
                      return (
                        <div key={s.id} style={{ display: "grid", gridTemplateColumns: "36px 1fr 100px 140px 70px", padding: "7px 14px", gap: 12, borderBottom: `1px solid ${B.border}33`, alignItems: "center", background: s.stixType === "unknown" ? "#ef444408" : "transparent" }}>
                          <input type="checkbox" checked={s.include} onChange={() => toggleInclude(s.id)} style={{ cursor: "pointer", accentColor: B.primary }} />
                          <div style={{ fontSize: fs(11), color: B.text, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.value}>{s.value}</div>
                          <div style={{ fontSize: fs(10), color: B.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.column}</div>
                          <div style={{ position: "relative" }}>
                            {editingId === s.id ? (
                              <select
                                autoFocus
                                defaultValue={`${s.stixType}|${s.subtype}`}
                                onBlur={() => setEditingId(null)}
                                onChange={e => { const [st, sub] = e.target.value.split("|"); const t = INGEST_TYPES.find(t => t.stixType === st && t.subtype === sub); if (t) setType(s.id, t.stixType, t.subtype, t.label); setEditingId(null); }}
                                style={{ fontSize: fs(10), background: B.bgPanel, border: `1px solid ${B.border}`, color: B.text, borderRadius: 6, padding: "3px 6px", width: "100%", fontFamily: "'Satoshi', sans-serif" }}
                              >
                                {INGEST_TYPES.map(t => <option key={`${t.stixType}|${t.subtype}`} value={`${t.stixType}|${t.subtype}`}>{t.label}</option>)}
                              </select>
                            ) : (
                              <button onClick={() => setEditingId(s.id)} style={{ fontSize: fs(10), padding: "3px 9px", borderRadius: 999, background: c.color + "18", color: c.color, border: `1px solid ${c.color}40`, cursor: "pointer", fontWeight: 700, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }}>
                                {s.label} ▾
                              </button>
                            )}
                          </div>
                          <div style={{ fontSize: fs(10), color: CONF_COLOR[s.confidence] || B.textDim, fontWeight: 700 }}>{s.confidence}</div>
                        </div>
                      );
                    })}
                    {filteredSuggestions.length > 500 && (
                      <div style={{ padding: "10px 14px", fontSize: fs(11), color: B.textDim }}>Showing first 500 of {filteredSuggestions.length} — use the type filters above to narrow down</div>
                    )}
                    {filteredSuggestions.length === 0 && (
                      <div style={{ padding: "24px 14px", fontSize: fs(12), color: B.textDim, textAlign: "center" }}>No items match this filter.</div>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ fontSize: fs(12), color: B.textMuted }}>{includedCount} of {suggestions.length} objects selected</div>
                  <button
                    disabled={includedCount === 0}
                    onClick={() => onLoad(buildBundleFromSuggestions(suggestions))}
                    style={{ ...HP.primaryBtn, opacity: includedCount ? 1 : 0.4 }}
                  >
                    Build Bundle → ({includedCount})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ─── Home layout styles ───
const HP = {
  navBtn: { background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "6px 16px", borderRadius: 6, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 },
  hero: { textAlign: "center", padding: "52px 40px 52px", maxWidth: 720, margin: "0 auto" },
  heroBadge: { display: "inline-block", fontSize: fs(10), fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: B.accent, border: `1px solid ${B.accent}44`, borderRadius: 20, padding: "4px 14px", marginBottom: 24 },
  heroTitle: { fontSize: fs(52), fontWeight: 900, lineHeight: 1.1, margin: "0 0 20px 0", color: B.text },
  heroSub: { fontSize: fs(15), color: B.textMuted, lineHeight: 1.7, margin: "0 auto 36px", maxWidth: 520 },
  primaryBtn: { background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, padding: "12px 28px", borderRadius: 8, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 700, letterSpacing: 0.3 },
  secondaryBtn: { background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "12px 28px", borderRadius: 8, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 },
  statsRow: { display: "flex", justifyContent: "center", gap: 2, padding: "0 40px 60px", flexWrap: "wrap", maxWidth: 960, margin: "0 auto" },
  statCard: { flex: "1 1 180px", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: "24px 28px", textAlign: "center" },
  sectionLabel: { fontSize: fs(10), fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: B.primary, marginBottom: 10 },
  sectionTitle: { fontSize: fs(26), fontWeight: 900, color: B.text, margin: 0 },
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 },
  featureCard: { background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: "24px 20px" },
  cta: { background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, margin: "0 40px 60px", padding: "48px 40px", textAlign: "center", maxWidth: 880, marginLeft: "auto", marginRight: "auto" },
};

// ─── Sample Bundle ───
const SAMPLE_BUNDLE = {
  type: "bundle", id: "bundle--a1b2c3d4",
  objects: [
    { type: "threat-actor", id: "threat-actor--1", name: "APT Phantom Bear", description: "A sophisticated nation-state threat actor targeting critical infrastructure in Western Europe. Known for spear-phishing campaigns and custom malware deployment.", threat_actor_types: ["nation-state"], aliases: ["PHANTOM-01", "GhostPaw"], first_seen: "2019-03-15T00:00:00Z", sophistication: "expert", resource_level: "government", primary_motivation: "organizational-gain", created: "2024-01-10T08:00:00Z", modified: "2024-06-20T14:30:00Z" },
    { type: "threat-actor", id: "threat-actor--12", name: "Silent Chollima", description: "State-sponsored threat actor focused on financial theft and cryptocurrency exchange targeting.", threat_actor_types: ["nation-state"], aliases: ["SILENT-C"], first_seen: "2017-09-01T00:00:00Z", sophistication: "expert", resource_level: "government", created: "2024-03-01T08:00:00Z", modified: "2024-08-15T10:00:00Z" },
    { type: "intrusion-set", id: "intrusion-set--13", name: "Volt Surge Cluster", description: "Intrusion set associated with APT Phantom Bear, targeting European energy SCADA systems.", first_seen: "2024-01-20T00:00:00Z", last_seen: "2024-09-15T00:00:00Z", created: "2024-10-05T08:00:00Z", modified: "2024-10-05T08:00:00Z" },
    { type: "malware", id: "malware--2", name: "ShadowLoader", description: "A multi-stage loader malware used to deliver secondary payloads. Communicates over HTTPS with encrypted C2 channels.", malware_types: ["loader", "backdoor"], is_family: true, first_seen: "2020-07-22T00:00:00Z", created: "2024-02-05T10:00:00Z", modified: "2024-07-01T09:15:00Z" },
    { type: "malware", id: "malware--14", name: "VoltWorm", description: "Wiper malware designed to destroy SCADA configuration files.", malware_types: ["wiper"], first_seen: "2024-04-10T00:00:00Z", created: "2024-05-01T10:00:00Z", modified: "2024-05-01T10:00:00Z" },
    { type: "indicator", id: "indicator--3", name: "ShadowLoader C2 Domain", description: "Known command and control domain used by ShadowLoader.", pattern: "[domain-name:value = 'update-service.shadow-infra.net']", pattern_type: "stix", valid_from: "2024-01-01T00:00:00Z", valid_until: "2025-06-01T00:00:00Z", indicator_types: ["malicious-activity"], created: "2024-03-12T12:00:00Z", modified: "2024-03-12T12:00:00Z" },
    { type: "indicator", id: "indicator--4", name: "ShadowLoader Hash (SHA-256)", description: "File hash of known ShadowLoader dropper executable.", pattern: "[file:hashes.'SHA-256' = 'a3f2b8c1d4e5...']", pattern_type: "stix", valid_from: "2024-02-15T00:00:00Z", indicator_types: ["malicious-activity"], created: "2024-03-15T08:30:00Z", modified: "2024-03-15T08:30:00Z" },
    { type: "indicator", id: "indicator--15", name: "VoltWorm C2 IP", description: "Command and control IP address for VoltWorm wiper.", pattern: "[ipv4-addr:value = '185.220.101.45']", pattern_type: "stix", valid_from: "2024-04-15T00:00:00Z", indicator_types: ["malicious-activity"], created: "2024-04-20T10:00:00Z", modified: "2024-04-20T10:00:00Z" },
    { type: "attack-pattern", id: "attack-pattern--5", name: "Spear-Phishing with Macro Doc", description: "Delivers a weaponized Office document via targeted email with obfuscated VBA macros.", external_references: [{ source_name: "mitre-attack", external_id: "T1566.001" }], created: "2024-01-20T06:00:00Z", modified: "2024-01-20T06:00:00Z" },
    { type: "attack-pattern", id: "attack-pattern--9", name: "Scheduled Task Persistence", description: "Creates a scheduled task for persistence. Executes ShadowLoader DLL every 4 hours.", external_references: [{ source_name: "mitre-attack", external_id: "T1053.005" }], created: "2024-02-10T06:00:00Z", modified: "2024-02-10T06:00:00Z" },
    { type: "attack-pattern", id: "attack-pattern--16", name: "Domain Fronting", description: "Uses CDN domain fronting to disguise C2 traffic as legitimate HTTPS requests.", external_references: [{ source_name: "mitre-attack", external_id: "T1090.004" }], created: "2024-03-01T06:00:00Z", modified: "2024-03-01T06:00:00Z" },
    { type: "vulnerability", id: "vulnerability--6", name: "CVE-2024-21412", description: "Internet Shortcut Files Security Feature Bypass in Microsoft Windows. Exploited by APT Phantom Bear.", external_references: [{ source_name: "cve", external_id: "CVE-2024-21412" }], created: "2024-02-14T00:00:00Z", modified: "2024-02-14T00:00:00Z" },
    { type: "identity", id: "identity--7", name: "European Energy Sector", description: "Critical infrastructure operators in the European energy sector.", identity_class: "class", sectors: ["energy"], created: "2024-01-10T08:00:00Z", modified: "2024-01-10T08:00:00Z" },
    { type: "identity", id: "identity--17", name: "ACME Threat Research", description: "Internal threat intelligence team.", identity_class: "organization", created: "2024-01-01T08:00:00Z", modified: "2024-01-01T08:00:00Z" },
    { type: "campaign", id: "campaign--8", name: "Operation Volt Surge", description: "Sustained cyber espionage campaign targeting European energy infrastructure Q1-Q3 2024.", first_seen: "2024-01-15T00:00:00Z", last_seen: "2024-09-30T00:00:00Z", created: "2024-10-01T10:00:00Z", modified: "2024-10-01T10:00:00Z" },
    { type: "infrastructure", id: "infrastructure--10", name: "Shadow C2 Network", description: "Distributed C2 infrastructure using compromised WordPress sites and bulletproof hosting.", infrastructure_types: ["command-and-control"], created: "2024-04-01T00:00:00Z", modified: "2024-04-01T00:00:00Z" },
    { type: "tool", id: "tool--11", name: "Mimikatz", description: "Open-source credential harvesting tool for lateral movement.", tool_types: ["credential-exploitation"], created: "2024-01-10T08:00:00Z", modified: "2024-01-10T08:00:00Z" },
    { type: "location", id: "location--18", name: "Western Europe", description: "Geographic region of Operation Volt Surge targets.", region: "western-europe", created: "2024-01-10T08:00:00Z", modified: "2024-01-10T08:00:00Z" },
    { type: "note", id: "note--19", name: "Analyst Note: ShadowLoader Variant", description: "New variant uses XOR obfuscation instead of AES.", content: "Captured in honeypot June 15. XOR-based string obfuscation, removed anti-VM checks, new mutex name. Possibly rushed dev cycle or separate operator.", object_refs: ["malware--2"], created: "2024-06-16T10:00:00Z", modified: "2024-06-16T10:00:00Z" },
    { type: "report", id: "report--20", name: "Operation Volt Surge — Full Analysis", description: "Comprehensive report covering initial access, lateral movement, impact, and attribution.", report_types: ["threat-report"], published: "2024-10-15T00:00:00Z", object_refs: ["threat-actor--1","malware--2","malware--14","campaign--8","attack-pattern--5","attack-pattern--9","vulnerability--6","identity--7","infrastructure--10","indicator--3","indicator--4"], created: "2024-10-15T10:00:00Z", modified: "2024-10-15T10:00:00Z" },
    { type: "report", id: "report--21", name: "Silent Chollima — Q2 2024 Summary", description: "Quarterly summary including new TTPs targeting crypto exchanges.", report_types: ["threat-report"], published: "2024-07-01T00:00:00Z", object_refs: ["threat-actor--12","tool--11"], created: "2024-07-02T10:00:00Z", modified: "2024-07-02T10:00:00Z" },
    { type: "report", id: "report--22", name: "CVE-2024-21412 Exploitation in the Wild", description: "Technical analysis of CVE-2024-21412 exploitation across multiple threat actors.", report_types: ["threat-report"], published: "2024-03-20T00:00:00Z", object_refs: ["vulnerability--6","threat-actor--1","malware--2"], created: "2024-03-20T10:00:00Z", modified: "2024-03-20T10:00:00Z" },
    { type: "relationship", id: "r--1", relationship_type: "uses", source_ref: "threat-actor--1", target_ref: "malware--2" },
    { type: "relationship", id: "r--2", relationship_type: "indicates", source_ref: "indicator--3", target_ref: "malware--2" },
    { type: "relationship", id: "r--3", relationship_type: "indicates", source_ref: "indicator--4", target_ref: "malware--2" },
    { type: "relationship", id: "r--4", relationship_type: "uses", source_ref: "threat-actor--1", target_ref: "attack-pattern--5" },
    { type: "relationship", id: "r--5", relationship_type: "targets", source_ref: "threat-actor--1", target_ref: "identity--7" },
    { type: "relationship", id: "r--6", relationship_type: "exploits", source_ref: "malware--2", target_ref: "vulnerability--6" },
    { type: "relationship", id: "r--7", relationship_type: "attributed-to", source_ref: "campaign--8", target_ref: "threat-actor--1" },
    { type: "relationship", id: "r--8", relationship_type: "uses", source_ref: "campaign--8", target_ref: "malware--2" },
    { type: "relationship", id: "r--9", relationship_type: "uses", source_ref: "threat-actor--1", target_ref: "attack-pattern--9" },
    { type: "relationship", id: "r--10", relationship_type: "uses", source_ref: "malware--2", target_ref: "infrastructure--10" },
    { type: "relationship", id: "r--11", relationship_type: "uses", source_ref: "threat-actor--1", target_ref: "tool--11" },
    { type: "relationship", id: "r--12", relationship_type: "targets", source_ref: "campaign--8", target_ref: "identity--7" },
    { type: "relationship", id: "r--13", relationship_type: "uses", source_ref: "campaign--8", target_ref: "malware--14" },
    { type: "relationship", id: "r--14", relationship_type: "indicates", source_ref: "indicator--15", target_ref: "malware--14" },
    { type: "relationship", id: "r--15", relationship_type: "uses", source_ref: "infrastructure--10", target_ref: "attack-pattern--16" },
    { type: "relationship", id: "r--16", relationship_type: "attributed-to", source_ref: "intrusion-set--13", target_ref: "threat-actor--1" },
    { type: "relationship", id: "r--17", relationship_type: "targets", source_ref: "threat-actor--1", target_ref: "location--18" },
    { type: "relationship", id: "r--18", relationship_type: "located-at", source_ref: "identity--7", target_ref: "location--18" },
  ],
};

const CATEGORIES = [
  { name: "Analysis", types: ["report", "note", "grouping", "case-incident", "x-case"] },
  { name: "Threats", types: ["threat-actor", "intrusion-set", "campaign"] },
  { name: "Arsenal", types: ["malware", "tool", "vulnerability"] },
  { name: "Techniques", types: ["attack-pattern"] },
  { name: "Entities", types: ["identity", "infrastructure"] },
  { name: "Locations", types: ["location"] },
  { name: "Indicators", types: ["indicator", "observed-data", "sighting"] },
  { name: "Observables", types: ["ipv4-addr", "ipv6-addr", "domain-name", "url", "email-addr", "file", "software", "user-account", "network-traffic", "process"] },
];

const TYPE_CONFIG = {
  "threat-actor":   { color: "#E4571E", label: "Threat Actor" },
  "intrusion-set":  { color: "#C2410C", label: "Intrusion Set" },
  "malware":        { color: "#A855F7", label: "Malware" },
  "indicator":      { color: "#f59e0b", label: "Indicator" },
  "attack-pattern": { color: "#ef4444", label: "Attack Pattern" },
  "vulnerability":  { color: "#f97316", label: "Vulnerability" },
  "identity":       { color: "#7C3AED", label: "Identity" },
  "campaign":       { color: "#E4571E", label: "Campaign" },
  "infrastructure": { color: "#A855F7", label: "Infrastructure" },
  "tool":           { color: "#C2410C", label: "Tool" },
  "course-of-action": { color: "#10b981", label: "Mitigation" },
  "x-ih-detection-rule": { color: "#0ea5e9", label: "Detection Rule" },
  "cryptocurrency-wallet": { color: "#eab308", label: "Wallet" },
  "cryptocurrency-transaction": { color: "#fb7185", label: "Transaction" },
  "report":         { color: "#7C3AED", label: "Report" },
  "note":           { color: "#a78bfa", label: "Note" },
  "grouping":       { color: "#A855F7", label: "Grouping" },
  "location":       { color: "#E4571E", label: "Location" },
  "observed-data":  { color: "#fbbf24", label: "Observed Data" },
  "ipv4-addr":        { color: "#06b6d4", label: "IPv4 Address" },
  "ipv6-addr":        { color: "#0891b2", label: "IPv6 Address" },
  "domain-name":      { color: "#22d3ee", label: "Domain Name" },
  "url":              { color: "#0ea5e9", label: "URL" },
  "email-addr":       { color: "#38bdf8", label: "Email Address" },
  "file":             { color: "#84cc16", label: "File" },
  "software":         { color: "#65a30d", label: "Software" },
  "user-account":     { color: "#4ade80", label: "User Account" },
  "network-traffic":  { color: "#2dd4bf", label: "Network Traffic" },
  "process":          { color: "#a3e635", label: "Process" },
  "sighting":         { color: "#f43f5e", label: "Sighting" },
  "case-incident":    { color: "#e879f9", label: "Case / Incident" },
  "x-case":           { color: "#e879f9", label: "Case / Incident" },
};
const CUSTOM_PALETTE = ["#06b6d4","#84cc16","#f43f5e","#14b8a6","#fb923c","#e879f9","#38bdf8","#a3e635"];
const customCache = {};
const getConf = (t) => {
  if (!t) return { color: "#8a8a8e", label: "Unknown" };
  if (TYPE_CONFIG[t]) return TYPE_CONFIG[t];
  if (!customCache[t]) {
    const idx = Object.keys(customCache).length % CUSTOM_PALETTE.length;
    customCache[t] = { color: CUSTOM_PALETTE[idx], label: t.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()), custom: true };
  }
  return customCache[t];
};

const TLP_DEFS = {
  "marking-definition--613f2e26-407d-48c7-9eca-b8e91ba519ae": { name: "TLP:WHITE", color: "#e5e7eb" },
  "marking-definition--34098fce-860f-48ae-8e50-ebd3cc5e41da": { name: "TLP:GREEN", color: "#16a34a" },
  "marking-definition--f88d31f6-1088-4354-a0ec-f64c4e9a2ddf": { name: "TLP:AMBER", color: "#f59e0b" },
  "marking-definition--5e57c739-391a-4eb3-b6be-7d15ca92d5ed": { name: "TLP:RED", color: "#ef4444" },
};
const MARKING_COLOR_MAP = {
  "TLP:RED": "#ef4444",
  "TLP:AMBER": "#f59e0b",
  "TLP:GREEN": "#16a34a",
  "TLP:WHITE": "#9ca3af",
  "TLP:CLEAR": "#9ca3af",
};

// A marking definition's label, always a string: TLP 1.0 ("definition": {"tlp": "white"}),
// TLP 2.0 (the level is in its extension), a statement (copyright, terms) or a name.
const getMarkingLabel = (def) => {
  if (!def) return null;
  const tlp2 = Object.values(def.extensions || {}).find(e => e && e.tlp_2_0)?.tlp_2_0;
  if (tlp2) return `TLP:${String(tlp2).toUpperCase()}`;
  if (def.definition_type === "tlp") {
    const raw = String(def.definition?.tlp || (typeof def.definition === "string" ? def.definition : "") || def.name || "").toUpperCase();
    return raw.startsWith("TLP:") ? raw : `TLP:${raw}`;
  }
  if (def.name) return String(def.name);
  const statement = def.definition?.statement || (typeof def.definition === "string" ? def.definition : null);
  if (statement) return statement.length > 48 ? `${statement.slice(0, 47)}…` : statement;
  return def.id || null;
};

const getMarkings = (obj, markingDefs = {}) => {
  const markings = [];
  for (const ref of (obj.object_marking_refs || [])) {
    if (TLP_DEFS[ref]) {
      markings.push(TLP_DEFS[ref]);
      continue;
    }
    const def = markingDefs[ref];
    if (!def) continue;
    const label = getMarkingLabel(def);
    if (!label) continue;
    markings.push({ name: label, color: def.color || MARKING_COLOR_MAP[label] || B.accent, id: def.id });
  }
  return markings;
};

const getTLP = (obj, markingDefs = {}) => {
  return getMarkings(obj, markingDefs).find((m) => m.name.startsWith("TLP:")) || null;
};
const getConfidenceInfo = (c) => {
  if (c == null) return null;
  if (c >= 70) return { label: `${c}%`, color: "#16a34a" };
  if (c >= 40) return { label: `${c}%`, color: "#f59e0b" };
  return { label: `${c}%`, color: "#ef4444" };
};

// ─── Name normalization & dedup utilities ───
const canonicalizeName = (name) => {
  if (!name) return name;
  return name.trim().replace(/\s+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const normForCompare = (name) => {
  if (!name) return "";
  return name.toLowerCase().replace(/[-_\s]+/g, "").trim();
};

const deduplicateBundle = (bundle) => {
  const objects = bundle.objects || [];
  const byId = new Map();
  let idDupes = 0;

  // Pass 1: dedup by ID, keep latest modified
  for (const obj of objects) {
    if (!byId.has(obj.id)) {
      byId.set(obj.id, { ...obj });
    } else {
      idDupes++;
      const existing = byId.get(obj.id);
      if (new Date(obj.modified || 0) > new Date(existing.modified || 0)) {
        byId.set(obj.id, { ...obj });
      }
    }
  }

  // Pass 2: dedup non-relationship objects by type + normalized name
  const nonRels = [...byId.values()].filter(o => o.type !== "relationship");
  const rels = [...byId.values()].filter(o => o.type === "relationship");
  const nameMap = new Map(); // "type::normname" -> canonical object
  let nameDupes = 0;
  const idRemap = new Map(); // old id -> canonical id (for relationship rewriting)

  for (const obj of nonRels) {
    if (!obj.name) { nameMap.set(obj.id, obj); continue; }
    const key = `${obj.type}::${normForCompare(obj.name)}`;
    if (!nameMap.has(key)) {
      nameMap.set(key, { ...obj, name: canonicalizeName(obj.name) });
    } else {
      nameDupes++;
      const existing = nameMap.get(key);
      idRemap.set(obj.id, existing.id);
      if (new Date(obj.modified || 0) > new Date(existing.modified || 0)) {
        nameMap.set(key, { ...obj, id: existing.id, name: canonicalizeName(obj.name) });
      }
    }
  }

  // Rewrite relationship refs to point to canonical IDs
  const finalNonRels = [...nameMap.values()];
  const finalRels = rels
    .map(r => ({
      ...r,
      source_ref: idRemap.get(r.source_ref) || r.source_ref,
      target_ref: idRemap.get(r.target_ref) || r.target_ref,
    }))
    .filter(r => r.source_ref !== r.target_ref);

  // Dedup relationships themselves
  const relSeen = new Set();
  const dedupedRels = finalRels.filter(r => {
    const key = `${r.source_ref}::${r.relationship_type}::${r.target_ref}`;
    if (relSeen.has(key)) return false;
    relSeen.add(key); return true;
  });

  return {
    bundle: { ...bundle, objects: [...finalNonRels, ...dedupedRels] },
    idDupes,
    nameDupes,
  };
};

// ─── SVG icons ───
function TypeIcon({ type, color, size = 14 }) {
  const s = size;
  const p = { xmlns: "http://www.w3.org/2000/svg", width: s, height: s, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (type) {
    case "threat-actor": return <svg {...p}><circle cx="12" cy="8" r="4"/><path d="M6 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><line x1="12" y1="1" x2="12" y2="3"/></svg>;
    case "intrusion-set": return <svg {...p}><circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="12" cy="18" r="2"/><line x1="7.5" y1="7.5" x2="10.5" y2="16.5"/><line x1="16.5" y1="7.5" x2="13.5" y2="16.5"/><line x1="8" y1="6" x2="16" y2="6"/></svg>;
    case "malware": return <svg {...p}><path d="M8 2l1.5 3M16 2l-1.5 3M3 10h2M19 10h2M3 14h2M19 14h2M8 22l1.5-3M16 22l-1.5-3"/><rect x="6" y="5" width="12" height="14" rx="3"/></svg>;
    case "indicator": return <svg {...p}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    case "attack-pattern": return <svg {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill={color} stroke="none"/></svg>;
    case "vulnerability": return <svg {...p}><path d="M12 2l7 4v5c0 5-3.5 9.7-7 11-3.5-1.3-7-6-7-11V6l7-4z"/><line x1="12" y1="8" x2="12" y2="13"/><circle cx="12" cy="16" r="0.5" fill={color}/></svg>;
    case "identity": return <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H11a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case "campaign": return <svg {...p}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
    case "infrastructure": return <svg {...p}><rect x="2" y="2" width="20" height="6" rx="1"/><rect x="2" y="10" width="20" height="6" rx="1"/><circle cx="17" cy="5" r="1" fill={color}/><circle cx="17" cy="13" r="1" fill={color}/><line x1="6" y1="18" x2="6" y2="22"/><line x1="18" y1="18" x2="18" y2="22"/></svg>;
    case "tool": return <svg {...p}><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
    case "report": return <svg {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>;
    case "note": return <svg {...p}><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>;
    case "location": return <svg {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
    case "ipv4-addr": case "ipv6-addr": return <svg {...p}><rect x="3" y="4" width="18" height="7" rx="1.5"/><rect x="3" y="13" width="18" height="7" rx="1.5"/><line x1="7" y1="7.5" x2="7.01" y2="7.5"/><line x1="7" y1="16.5" x2="7.01" y2="16.5"/></svg>;
    case "domain-name": return <svg {...p}><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z"/></svg>;
    case "url": return <svg {...p}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></svg>;
    case "file": return <svg {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>;
    case "course-of-action": return <svg {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z"/><path d="M9 12l2 2 4-4"/></svg>;
    case "x-ih-detection-rule": return <svg {...p}><path d="M3 5h18M3 12h11M3 19h7"/><circle cx="17.5" cy="16.5" r="3.5"/><path d="M20 19l2 2"/></svg>;
    case "cryptocurrency-wallet": return <svg {...p}><path d="M3 7.5h15.5A2.5 2.5 0 0 1 21 10v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18z"/><path d="M3 7.5L15.5 3.5v4"/><circle cx="16.5" cy="14" r="1.4"/></svg>;
    case "cryptocurrency-transaction": return <svg {...p}><path d="M4 8h14l-3.5-3.5"/><path d="M20 16H6l3.5 3.5"/></svg>;
    case "software": return <svg {...p}><path d="M21 8l-9-5-9 5 9 5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/><path d="M7.5 5.5l9 5"/></svg>;
    default: return <svg {...p}><rect x="4" y="4" width="16" height="16" rx="2"/></svg>;
  }
}

// ─── Logo ───
// The Idle Hour mark (October 2026): an hourglass of falling data in a ring, with a network
// through its waist, purple to orange. Drawn for small sizes; public/favicon.svg is the same.
const LOGO_PURPLE = "#6a3df2", LOGO_ORANGE = "#f7931e";
function Logo({ size = 28 }) {
  const g = `ih-logo-${useId().replace(/:/g, "")}`;
  const grad = `url(#${g})`;
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" role="img" aria-label="Idle Hour">
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="40" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={LOGO_PURPLE} /><stop offset="0.5" stopColor="#b24de6" /><stop offset="1" stopColor={LOGO_ORANGE} />
        </linearGradient>
      </defs>
      <path d="M10.6 33.2 A16.5 16.5 0 1 1 29.4 33.2" stroke={grad} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M2.6 21.4 L9 19.6 L17 20 M23 20 L31 19.8 L37.4 18" stroke={grad} strokeWidth="1" strokeLinecap="round" opacity="0.9" />
      <circle cx="5.2" cy="20.7" r="2.1" fill={LOGO_PURPLE} /><circle cx="9" cy="19.6" r="1.3" fill="#7d43ef" />
      <circle cx="31" cy="19.8" r="1.3" fill="#f4a03a" /><circle cx="35" cy="18.7" r="2.1" fill={LOGO_ORANGE} />
      <rect x="10.5" y="7" width="19" height="3.2" rx="1.6" fill={grad} />
      <rect x="10.5" y="29.8" width="19" height="3.2" rx="1.6" fill={grad} />
      <path d="M13 10.2 C13 15.6 18.4 17.4 18.4 20 C18.4 22.6 13 24.4 13 29.8 M27 10.2 C27 15.6 21.6 17.4 21.6 20 C21.6 22.6 27 24.4 27 29.8" stroke={grad} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M20 17.5 V25.5" stroke={grad} strokeWidth="1" strokeDasharray="1.2 1.1" strokeLinecap="round" />
      <path d="M15.2 28.6 C16.4 25.8 18.4 25.2 20 25.2 C21.6 25.2 23.6 25.8 24.8 28.6 Z" fill={grad} opacity="0.85" />
      <circle cx="17" cy="13.2" r="0.8" fill="#8a4ff0" /><circle cx="20" cy="12.6" r="0.8" fill="#b24de6" /><circle cx="23" cy="13.2" r="0.8" fill="#e6862e" />
      <circle cx="18.6" cy="15.2" r="0.7" fill="#a04ce8" /><circle cx="21.4" cy="15.2" r="0.7" fill="#d37a3a" />
    </svg>
  );
}

// "IDLE HOUR" with its O as a ring, half purple, half orange, as in the logo.
function Wordmark({ fs, color, size = 13 }) {
  const o = Math.round(fs(size) * 0.78);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: fs(size), fontWeight: 700, letterSpacing: size * 0.17, color, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" }} aria-label="Idle Hour">
      IDLE H
      <svg width={o} height={o} viewBox="0 0 20 20" style={{ margin: "0 2.2px 0 0.5px" }} aria-hidden="true">
        <path d="M9.4 2.1 A8 8 0 0 0 9.4 17.9" stroke={LOGO_PURPLE} strokeWidth="3.2" fill="none" />
        <path d="M10.6 2.1 A8 8 0 0 1 10.6 17.9" stroke={LOGO_ORANGE} strokeWidth="3.2" fill="none" />
      </svg>
      UR
    </span>
  );
}

// ─── Relationship Create Modal ───
function RelCreateModal({ source, target, onConfirm, onClose }) {
  const REL_TYPES = ["related-to","uses","targets","attributed-to","indicates","mitigates","derived-from","controls","delivers","drops","exploits","originates-from","variant-of","communicates-with","has","impersonates","based-on","consists-of","investigates","owns"];
  const [relType, setRelType] = useState("related-to");
  const [custom, setCustom] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const src = swapped ? target : source;
  const tgt = swapped ? source : target;
  const sc = getConf(src.type), tc = getConf(tgt.type);
  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 14, padding: 24, width: 400, boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: fs(14), fontWeight: 800, color: B.text, marginBottom: 16 }}>Create Relationship</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, padding: "10px 14px", background: B.bg, borderRadius: 8 }}>
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <TypeIcon type={src.type} color={sc.color} size={16} />
            <div style={{ fontSize: fs(10), color: sc.color, marginTop: 3, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{src.name || src.id.slice(0,10)}</div>
          </div>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: fs(10), color: B.textDim }}>────</div>
            <div style={{ fontSize: fs(11), color: B.accent, fontWeight: 600, margin: "2px 0" }}>{useCustom ? (custom || "?") : relType}</div>
            <div style={{ fontSize: fs(10), color: B.textDim }}>────▶</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <TypeIcon type={tgt.type} color={tc.color} size={16} />
            <div style={{ fontSize: fs(10), color: tc.color, marginTop: 3, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tgt.name || tgt.id.slice(0,10)}</div>
          </div>
          <button onClick={() => setSwapped(p => !p)} title="Swap source and target"
            style={{ width: 28, height: 28, borderRadius: 6, background: "transparent", border: `1px solid ${B.border}`, color: B.textDim, cursor: "pointer", fontSize: fs(14), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.color = B.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.color = B.textDim; }}>
            ⇄
          </button>
        </div>
        {!useCustom ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
            {REL_TYPES.map(t => (
              <button key={t} onClick={() => setRelType(t)} style={{ padding: "4px 10px", borderRadius: 10, fontSize: fs(11), border: `1px solid ${relType === t ? B.accent : B.border}`, background: relType === t ? B.accent + "18" : "transparent", color: relType === t ? B.accent : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>{t}</button>
            ))}
          </div>
        ) : (
          <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="custom-relationship-type" autoFocus
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
        )}
        <button onClick={() => setUseCustom(p => !p)} style={{ background: "none", border: "none", color: B.textDim, fontSize: fs(11), cursor: "pointer", fontFamily: "'Satoshi', sans-serif", padding: 0, marginBottom: 18, display: "block" }}>
          {useCustom ? "← Use preset types" : "Use custom type →"}
        </button>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 20px", borderRadius: 8, background: "transparent", border: `1px solid ${B.border}`, color: B.textDim, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>Cancel</button>
          <button onClick={() => onConfirm(useCustom ? custom.trim() || "related-to" : relType, src.id, tgt.id)}
            style={{ padding: "8px 20px", borderRadius: 8, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 }}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// The kind of node a community suggestion (ghost) is drawn next to.
const GHOST_PREF = {
  "threat-actor": ["malware","campaign","tool","attack-pattern"],
  "malware": ["indicator","attack-pattern","vulnerability"],
  "indicator": ["malware","attack-pattern","threat-actor"],
  "campaign": ["threat-actor","intrusion-set","malware"],
  "vulnerability": ["attack-pattern","malware"],
  "intrusion-set": ["campaign","malware","attack-pattern"],
  "tool": ["threat-actor","attack-pattern"],
  "identity": ["threat-actor","intrusion-set","campaign"],
  "domain-name": ["malware","indicator"],
  "attack-pattern": ["malware","tool"],
};
const ghostTarget = (ghost, objs) => { const pref = GHOST_PREF[ghost?.type] || []; return (objs.find(o => pref.includes(o.type)) || objs[0])?.id || null; };
// ─── Timeline ───
function TimelineView({ objects, scrubberObjects = objects, onSelect, selectedId, dateWindow, onDateWindowChange }) {
  const items = useMemo(() => objects.filter(o => o.type !== "relationship").map(o => ({ obj: o, date: getObjectDate(o) })).filter(i => i.date).sort((a, b) => a.date - b.date), [objects]);
  const scrubberItems = useMemo(() => scrubberObjects.filter(o => o.type !== "relationship").map(o => ({ obj: o, date: getObjectDate(o) })).filter(i => i.date).sort((a, b) => a.date - b.date), [scrubberObjects]);
  const bars = useMemo(() => {
    if (!scrubberItems.length) return [];
    const min = scrubberItems[0].date.getTime();
    const max = scrubberItems[scrubberItems.length - 1].date.getTime();
    const count = Math.max(8, Math.min(28, Math.ceil(Math.sqrt(scrubberItems.length) * 4)));
    const span = Math.max(1, max - min + 1);
    const bins = Array.from({ length: count }, (_, i) => {
      const start = min + (span * i) / count;
      const end = min + (span * (i + 1)) / count - 1;
      return { start, end, value: 0 };
    });
    scrubberItems.forEach(item => {
      const idx = Math.max(0, Math.min(count - 1, Math.floor(((item.date.getTime() - min) / span) * count)));
      bins[idx].value++;
    });
    const high = Math.max(1, ...bins.map(b => b.value));
    return bins.map(b => ({ ...b, pct: b.value / high }));
  }, [scrubberItems]);
  const fmtShort = (ms) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
  if (!scrubberItems.length) return <div style={{ padding: 40, color: B.textDim, textAlign: "center" }}>No temporal data</div>;
  return (
    <div style={{ padding: 24, overflow: "auto", height: "100%" }}>
      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: "14px 16px 12px", marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.4 }}>Date Density</div>
          {dateWindow && (
            <button onClick={() => onDateWindowChange?.(null)} style={{ marginLeft: "auto", fontSize: fs(10), padding: "4px 9px", borderRadius: 7, border: `1px solid ${B.border}`, background: "transparent", color: B.textMuted, cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>
              Clear {fmtShort(new Date(dateWindow.start).getTime())} - {fmtShort(new Date(dateWindow.end).getTime())}
            </button>
          )}
        </div>
        <div style={{ height: 86, display: "flex", alignItems: "flex-end", gap: 4 }}>
          {bars.map((bar, i) => {
            const active = dateWindow && bar.start <= new Date(dateWindow.end).getTime() && bar.end >= new Date(dateWindow.start).getTime();
            return (
              <button
                key={i}
                title={`${fmtShort(bar.start)} - ${fmtShort(bar.end)}: ${bar.value} object${bar.value === 1 ? "" : "s"}`}
                onClick={() => onDateWindowChange?.({ start: new Date(bar.start).toISOString(), end: new Date(bar.end).toISOString() })}
                disabled={bar.value === 0}
                style={{ flex: 1, minWidth: 8, height: `${Math.max(8, bar.pct * 76)}px`, borderRadius: "5px 5px 2px 2px", border: `1px solid ${active ? B.primary : "rgba(255,255,255,0.12)"}`, background: active ? `linear-gradient(to top, ${B.primary}, ${B.accent})` : bar.value ? "rgba(168,85,247,0.34)" : "rgba(255,255,255,0.04)", cursor: bar.value ? "pointer" : "default", opacity: bar.value ? 1 : 0.4 }}
              />
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", color: B.textDim, fontSize: fs(10), marginTop: 8, fontFamily: "monospace" }}>
          <span>{fmtShort(bars[0].start)}</span>
          <span>{items.length} shown</span>
          <span>{fmtShort(bars[bars.length - 1].end)}</span>
        </div>
      </div>
      {!items.length ? (
        <div style={{ padding: 40, color: B.textDim, textAlign: "center" }}>No objects in the selected time window</div>
      ) : (
      <div style={{ position: "relative", paddingLeft: 140 }}>
        <div style={{ position: "absolute", left: 130, top: 0, bottom: 0, width: 2, background: `linear-gradient(to bottom, ${B.primary}, ${B.accent})` }} />
        {items.map(item => { const c = getConf(item.obj.type), isSel = item.obj.id === selectedId; return (
          <div key={item.obj.id} onClick={() => onSelect(item.obj)} style={{ position: "relative", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center" }}>
            <div style={{ position: "absolute", left: -140, width: 120, textAlign: "right", fontSize: fs(11), color: B.textDim, fontFamily: "monospace" }}>{item.date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: isSel ? c.color : B.bgCard, border: `2px solid ${c.color}`, marginRight: 14, zIndex: 1, flexShrink: 0 }} />
            <div style={{ flex: 1, background: isSel ? c.color + "0c" : B.bgCard, border: `1px solid ${isSel ? c.color : B.border}`, borderRadius: 6, padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <TypeIcon type={item.obj.type} color={c.color} size={13} />
                <span style={{ fontSize: fs(10), color: c.color, fontWeight: 500 }}>{c.label}</span>
              </div>
              <div style={{ fontSize: fs(13), fontWeight: 600, color: B.text }}>{item.obj.name}</div>
              {item.obj.description && <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 3, lineHeight: 1.4 }}>{item.obj.description.slice(0, 100)}…</div>}
            </div>
          </div>
        ); })}
      </div>
      )}
    </div>
  );
}

// ─── Reports ───
function ReportsView({ objects, allObjects, onSelect, selectedId }) {
  const reports = objects.filter(o => o.type === "report");
  if (!reports.length) return <div style={{ padding: 40, color: B.textDim, textAlign: "center" }}>No reports</div>;
  return (
    <div style={{ padding: 24, overflow: "auto", height: "100%" }}>
      {reports.map(rpt => { const isSel = rpt.id === selectedId; const refs = (rpt.object_refs || []).map(id => allObjects.find(o => o.id === id)).filter(Boolean); return (
        <div key={rpt.id} onClick={() => onSelect(rpt)} style={{ background: isSel ? B.accent + "0c" : B.bgCard, border: `1px solid ${isSel ? B.accent : B.border}`, borderRadius: 8, padding: 18, marginBottom: 12, cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><TypeIcon type="report" color={isSel ? B.accent : "#7C3AED"} size={16} /><div style={{ fontSize: fs(15), fontWeight: 700, color: B.text }}>{rpt.name}</div></div>
          {rpt.published && <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 8 }}>Published {new Date(rpt.published).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>}
          {rpt.description && <p style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5, margin: "0 0 12px 0" }}>{rpt.description}</p>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{refs.map(obj => { const c = getConf(obj.type); return <span key={obj.id} onClick={e => { e.stopPropagation(); onSelect(obj); }} style={{ fontSize: fs(10), padding: "2px 8px", borderRadius: 10, background: c.color + "12", color: c.color, cursor: "pointer", border: `1px solid ${c.color}15`, display: "flex", alignItems: "center", gap: 3 }}><TypeIcon type={obj.type} color={c.color} size={10} />{obj.name}</span>; })}</div>
        </div>
      ); })}
    </div>
  );
}

// ─── Translate Button ───
function TranslateButton({ text }) {
  const LANGS = ["English", "Spanish", "French", "German", "Chinese (Simplified)", "Japanese", "Korean", "Arabic", "Russian", "Portuguese"];
  const [open, setOpen] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [loading, setLoading] = useState(false);

  const translate = async (lang) => {
    setOpen(false);
    setLoading(true);
    setTranslated(null);
    try {
      const res = await fetch("/api/translate", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, targetLanguage: lang }),
      });
      const data = await res.json();
      if (res.ok) setTranslated({ lang, text: data.translated });
      else setTranslated({ lang, text: data.error || "Translation failed." });
    } catch (e) { setTranslated({ lang, text: "Translation failed." }); }
    setLoading(false);
  };

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ fontSize: fs(9), padding: "1px 7px", borderRadius: 6, border: `1px solid ${B.border}`, background: "transparent", color: B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", marginLeft: 6 }}>
        {loading ? "…" : "Translate"}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "110%", left: 0, background: B.bgPanel, border: `1px solid ${B.border}`, borderRadius: 8, zIndex: 100, minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
          {LANGS.map(l => (
            <button key={l} onClick={() => translate(l)} style={{ display: "block", width: "100%", background: "none", border: "none", padding: "8px 14px", textAlign: "left", color: B.textMuted, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", cursor: "pointer" }}>{l}</button>
          ))}
        </div>
      )}
      {translated && (
        <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 7, background: B.bgCard, border: `1px solid ${B.accent}33`, fontSize: fs(12), color: B.text, lineHeight: 1.5 }}>
          <div style={{ fontSize: fs(9), color: B.accent, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>{translated.lang}</div>
          {translated.text}
          <button onClick={() => setTranslated(null)} style={{ float: "right", background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(12) }}>✕</button>
        </div>
      )}
    </span>
  );
}

// ─── PCAP Page ───
// ── Helpers ──────────────────────────────────────────────────────
function fmtBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function IOTimeline({ timeline }) {
  if (!timeline?.length) return null;
  const maxCount = Math.max(...timeline.map(t => t.count), 1);
  const w = 600, h = 56, pad = 4;
  const barW = Math.max(1, (w - pad * 2) / timeline.length - 1);
  return (
    <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12 }}>Traffic Timeline</div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: h, display: "block" }}>
        {timeline.map((bucket, i) => {
          const bh = Math.max(1, ((bucket.count / maxCount) * (h - pad * 2)));
          const x = pad + i * ((w - pad * 2) / timeline.length);
          const y = h - pad - bh;
          const intense = bucket.count > maxCount * 0.7;
          return <rect key={i} x={x} y={y} width={barW} height={bh} fill={intense ? B.primary : B.accent} opacity={0.75} rx={1} />;
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: fs(9), color: B.textDim, marginTop: 4 }}>
        <span>0s</span>
        <span style={{ color: B.textMuted }}>{timeline.length} time buckets · peak {Math.max(...timeline.map(t => t.count))} pkts</span>
        <span>{timeline[timeline.length - 1] ? `${(timeline[timeline.length - 1].t - timeline[0].t).toFixed(1)}s` : ""}</span>
      </div>
    </div>
  );
}

const SEVERITY_COLOR = { high: "#ef4444", medium: "#f59e0b", low: "#10b981" };
const ANOMALY_LABEL = { port_scan: "Port Scan", beaconing: "Beaconing / C2", dga: "DGA Candidate", dns_tunnel: "DNS Tunneling", suspicious_port: "Suspicious Port" };

function PCAPPage({ onNavigate, onLogin, onLogout, user, onAddToBundle, session, onSessionUpdate, onSessionReset, collapsed, onToggleCollapse }) {
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const PAGE_SIZE = 50;
  const { packets, stats, total, analysis, suggestions, selectedSuggestionIds, page } = session;
  const selectedSuggestions = useMemo(() => new Set(selectedSuggestionIds), [selectedSuggestionIds]);
  const [tab, setTab] = useState("summary");
  const [filterText, setFilterText] = useState("");
  const [convSort, setConvSort] = useState("bytes");

  const normalizedFilter = filterText.trim().toLowerCase();
  const filteredPackets = useMemo(() => {
    if (!packets?.length || !normalizedFilter) return packets || [];
    const tokens = normalizedFilter.split(/\s+/);
    return packets.filter(p => tokens.every(token => {
      if (token.startsWith("src:")) return `${p.srcIp}:${p.srcPort || ""}`.toLowerCase().includes(token.slice(4));
      if (token.startsWith("dst:")) return `${p.dstIp}:${p.dstPort || ""}`.toLowerCase().includes(token.slice(4));
      if (token.startsWith("ip:")) return `${p.srcIp} ${p.dstIp}`.toLowerCase().includes(token.slice(3));
      if (token.startsWith("port:")) return `${p.srcPort || ""} ${p.dstPort || ""}`.toLowerCase().includes(token.slice(5));
      if (token.startsWith("proto:")) return `${p.proto} ${p.service}`.toLowerCase().includes(token.slice(6));
      if (token.startsWith("host:")) return `${p.httpHost || ""} ${p.dnsQuery || ""} ${p.tlsSni || ""}`.toLowerCase().includes(token.slice(5));
      return [p.srcIp, p.dstIp, p.srcPort?.toString(), p.dstPort?.toString(), p.service, p.proto, p.httpHost, p.dnsQuery, p.tlsSni]
        .some(v => v && v.toString().toLowerCase().includes(token));
    }));
  }, [packets, normalizedFilter]);

  const sortedConversations = useMemo(() => {
    const convs = stats?.conversations || [];
    return [...convs].sort((a, b) => b[convSort] - a[convSort]);
  }, [stats?.conversations, convSort]);

  const isPremium = !!user;

  const safeJson = async (res) => {
    try { return await res.json(); }
    catch { throw new Error("Backend server not reachable. Run: node server.js"); }
  };

  const uploadFile = async (file) => {
    if (!file || ANALYZE_LOCKED) return;
    if (file.size > ANALYZE_MAX_MB * 1048576) { alert(`That file is ${(file.size / 1048576).toFixed(1)} MB; the limit here is ${ANALYZE_MAX_MB} MB.`); return; }
    setLoading(true);
    setTab("summary");
    onSessionUpdate({ packets: [], stats: null, analysis: null, suggestions: null, selectedSuggestionIds: [], page: 0 });
    const form = new FormData();
    form.append("pcap", file);
    try {
      const res = await fetch("/api/pcap/parse", { method: "POST", credentials: "include", body: form });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || "Parse failed.");
      onSessionUpdate({ packets: data.packets || [], stats: data.stats, total: data.total || 0 });
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  const runAnalysis = async () => {
    if (!stats) return;
    setAnalysisLoading(true);
    onSessionUpdate({ analysis: null });
    try {
      const res = await fetch("/api/pcap/analyze", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ stats, packets }) });
      const data = await safeJson(res);
      if (res.ok) onSessionUpdate({ analysis: data.analysis });
      else onSessionUpdate({ analysis: data.error || "Analysis failed." });
    } catch (e) { onSessionUpdate({ analysis: e.message || "Analysis failed." }); }
    setAnalysisLoading(false);
  };

  const runExtract = async () => {
    if (!stats) return;
    setSuggestionsLoading(true);
    onSessionUpdate({ suggestions: null });
    try {
      const res = await fetch("/api/pcap/extract", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ packets, stats }) });
      const data = await safeJson(res);
      if (res.ok) onSessionUpdate({ suggestions: data, selectedSuggestionIds: [...data.objects.map(o => o.id), ...data.relationships.map(r => r.id)] });
    } catch (e) { alert(e.message); }
    setSuggestionsLoading(false);
  };

  const addSelected = () => {
    if (!suggestions) return;
    const objs = [...suggestions.objects.filter(o => selectedSuggestions.has(o.id)), ...suggestions.relationships.filter(r => selectedSuggestions.has(r.id))];
    onAddToBundle(objs);
  };

  const totalPages = Math.max(1, Math.ceil(filteredPackets.length / PAGE_SIZE));
  const setPageIndex = (nextPage) => onSessionUpdate({ page: nextPage });
  const protoColor = { TCP: "#3b82f6", UDP: "#10b981", ICMP: "#f59e0b", HTTP: "#E4571E", HTTPS: "#A855F7", DNS: "#06b6d4", SSH: "#16a34a", SMB: "#ef4444", RDP: "#dc2626" };
  const getPC = (p) => protoColor[p] || B.textDim;
  const protoCols = stats ? Object.entries(stats.protoCounts).sort((a, b) => b[1] - a[1]) : [];
  const maxProto = protoCols[0]?.[1] || 1;

  const TABS = [
    { id: "summary", label: "Summary" },
    { id: "conversations", label: `Conversations${stats?.conversations?.length ? ` (${stats.conversations.length})` : ""}` },
    { id: "anomalies", label: `Anomalies${stats?.anomalies?.length ? ` ·${stats.anomalies.length}` : ""}`, alert: stats?.anomalies?.some(a => a.severity === "high") },
    { id: "http", label: "HTTP / DNS" },
    { id: "packets", label: "Packets" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="pcap" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={session} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, color: B.text, fontFamily: "'Satoshi', sans-serif" }}>
      <GroupTabs page="pcap" onNavigate={onNavigate} />
      {/* Top nav */}
      <div style={{ padding: "12px 28px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 16, background: B.bgPanel }}>
        <div style={{ fontWeight: 800, fontSize: fs(18) }}>PCAP Analyzer</div>
        {stats && <div style={{ fontSize: fs(12), color: B.textMuted }}>{total.toLocaleString()} packets · {stats.uniqueIps.length} IPs · {stats.duration.toFixed(1)}s</div>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {stats && !LOCKDOWN && <button onClick={runAnalysis} disabled={analysisLoading} style={{ ...HP2.accentBtn, opacity: analysisLoading ? 0.6 : 1 }}>{analysisLoading ? "Analyzing…" : "AI Analysis"}</button>}
          {stats && (isPremium
            ? <button onClick={runExtract} disabled={suggestionsLoading} style={{ ...HP2.premiumBtn, opacity: suggestionsLoading ? 0.6 : 1 }}>{suggestionsLoading ? "Extracting…" : "✦ Extract STIX"}</button>
            : <PremiumGate><button disabled style={{ ...HP2.premiumBtn, opacity: 0.5, cursor: "not-allowed" }}>✦ Extract STIX</button></PremiumGate>
          )}
        </div>
      </div>

      {/* Tabs */}
      {stats && (
        <div style={{ padding: "0 28px", borderBottom: `1px solid ${B.border}`, display: "flex", gap: 2, background: B.bgPanel }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "none", border: "none", borderBottom: `2px solid ${tab === t.id ? B.primary : "transparent"}`,
              color: tab === t.id ? B.primary : B.textMuted, padding: "10px 14px", cursor: "pointer",
              fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: tab === t.id ? 700 : 500,
              display: "flex", alignItems: "center", gap: 6, transition: "color 0.15s",
            }}>
              {t.alert && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />}
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding: "28px 28px" }}>
        {!stats && !loading && ANALYZE_LOCKED && (
          <ToolLocked what="The network capture analyzer reads a .pcap or .pcapng file, summarises hosts, protocols, DNS, TLS and HTTP traffic, and suggests STIX 2.1 indicators and observables from it." />
        )}

        {/* Upload zone */}
        {!stats && !loading && !ANALYZE_LOCKED && (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); uploadFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? B.accent : B.border}`, borderRadius: 16, padding: 80, textAlign: "center", cursor: "pointer", background: dragOver ? B.accent + "08" : "transparent", transition: "all 0.2s" }}
          >
            <div style={{ fontSize: fs(40), marginBottom: 16 }}>📡</div>
            <div style={{ fontSize: fs(18), fontWeight: 700, marginBottom: 8 }}>Drop a PCAP file here</div>
            <div style={{ fontSize: fs(13), color: B.textMuted }}>or click to browse · .pcap and .pcapng · max {ANALYZE_MAX_MB} MB</div>
            <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6 }}>Read in memory on the server and not kept · {ANALYZE_PER_HOUR} analyses an hour per address</div>
            <input ref={fileRef} type="file" accept=".pcap,.pcapng" onChange={e => uploadFile(e.target.files[0])} style={{ display: "none" }} />
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: 80, fontSize: fs(16), color: B.textMuted }}>Parsing packets…</div>}

        {stats && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── SUMMARY TAB ─────────────────────────────────── */}
            {tab === "summary" && (<>
              {/* Stat cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
                {[
                  { label: "Total Packets", value: total.toLocaleString(), color: B.primary },
                  { label: "Unique IPs", value: stats.uniqueIps.length, color: "#06b6d4" },
                  { label: "Duration", value: `${stats.duration.toFixed(1)}s`, color: "#10b981" },
                  { label: "Conversations", value: stats.conversations?.length || 0, color: "#f59e0b" },
                  { label: "Protocols", value: Object.keys(stats.protoCounts).length, color: "#A855F7" },
                  { label: "DNS Queries", value: stats.dnsQueries?.length || 0, color: "#06b6d4" },
                  { label: "HTTP URLs", value: stats.httpUrls?.length || 0, color: B.primary },
                  { label: "Anomalies", value: stats.anomalies?.length || 0, color: stats.anomalies?.some(a => a.severity === "high") ? "#ef4444" : "#f59e0b" },
                ].map(s => (
                  <div key={s.label} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: fs(22), fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: fs(10), color: B.textMuted, marginTop: 2, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <IOTimeline timeline={stats.timeline} />

              {/* Protocol + top talkers */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 20 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 14 }}>Protocol Breakdown</div>
                  {protoCols.map(([proto, count]) => (
                    <div key={proto} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <span style={{ width: 56, fontSize: fs(11), color: getPC(proto), fontWeight: 600 }}>{proto}</span>
                      <div style={{ flex: 1, background: B.bg, borderRadius: 3, height: 14 }}>
                        <div style={{ width: `${(count / maxProto) * 100}%`, height: "100%", background: getPC(proto), borderRadius: 3, minWidth: 2 }} />
                      </div>
                      <span style={{ width: 40, fontSize: fs(11), color: B.textDim, textAlign: "right" }}>{count}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 20 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 14 }}>Top Talkers (by bytes)</div>
                  {(stats.topTalkersByBytes || []).map(({ ip, bytes }) => (
                    <div key={ip} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                      <span style={{ flex: 1, fontSize: fs(11), color: B.text, fontFamily: "monospace" }}>{ip}</span>
                      {stats.osHints?.[ip] && <span style={{ fontSize: fs(9), padding: "1px 5px", borderRadius: 4, background: B.border, color: B.textDim }}>{stats.osHints[ip]}</span>}
                      <span style={{ fontSize: fs(11), color: B.accent, minWidth: 60, textAlign: "right" }}>{fmtBytes(bytes)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* DNS / HTTP / TLS previews */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 18 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>DNS Queries</div>
                  {(stats.dnsFreq || stats.dnsQueries || []).slice(0, 8).map((q, i) => {
                    const query = q.query || q; const count = q.count;
                    return <div key={i} style={{ fontSize: fs(11), color: B.text, marginBottom: 5, display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ flex: 1, wordBreak: "break-all" }}>{query}</span>
                      {count > 1 && <span style={{ fontSize: fs(9), color: B.textDim, flexShrink: 0 }}>×{count}</span>}
                    </div>;
                  })}
                  {!(stats.dnsFreq?.length || stats.dnsQueries?.length) && <div style={{ fontSize: fs(11), color: B.textDim }}>None detected.</div>}
                  {(stats.dnsFreq?.length || stats.dnsQueries?.length) > 8 && <button onClick={() => setTab("http")} style={{ fontSize: fs(10), color: B.accent, background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4 }}>See all →</button>}
                </div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 18 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>HTTP Hosts</div>
                  {(stats.httpHosts || []).slice(0, 8).map(h => <div key={h} style={{ fontSize: fs(11), color: B.text, marginBottom: 5, wordBreak: "break-all" }}>{h}</div>)}
                  {!(stats.httpHosts?.length) && <div style={{ fontSize: fs(11), color: B.textDim }}>None detected.</div>}
                  {stats.httpHosts?.length > 8 && <button onClick={() => setTab("http")} style={{ fontSize: fs(10), color: B.accent, background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4 }}>See all →</button>}
                </div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 18 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>TLS SNI</div>
                  {(stats.tlsSnis || []).slice(0, 8).map(s => <div key={s} style={{ fontSize: fs(11), color: B.text, marginBottom: 5, wordBreak: "break-all" }}>{s}</div>)}
                  {!(stats.tlsSnis?.length) && <div style={{ fontSize: fs(11), color: B.textDim }}>None detected.</div>}
                </div>
              </div>

              {/* Anomaly teaser */}
              {stats.anomalies?.length > 0 && (
                <div style={{ background: "#1c0a0a", border: `1px solid #ef444444`, borderRadius: 12, padding: 18, display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{ fontSize: fs(20) }}>⚠</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: fs(13), fontWeight: 700, color: "#ef4444", marginBottom: 4 }}>{stats.anomalies.filter(a => a.severity === "high").length} high-severity anomalies detected</div>
                    <div style={{ fontSize: fs(11), color: B.textMuted }}>{stats.anomalies[0].detail}</div>
                  </div>
                  <button onClick={() => setTab("anomalies")} style={{ ...HP2.navBtn, borderColor: "#ef444466", color: "#ef4444" }}>View All →</button>
                </div>
              )}

              {/* AI analysis */}
              {analysis && (
                <div style={{ background: B.bgCard, border: `1px solid ${B.accent}44`, borderRadius: 12, padding: 22 }}>
                  <div style={{ fontSize: fs(11), color: B.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>AI Analysis</div>
                  <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{analysis}</div>
                </div>
              )}

              {/* STIX suggestions */}
              {suggestions && (
                <div style={{ background: B.bgCard, border: `1px solid ${B.primary}44`, borderRadius: 12, padding: 22 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: fs(11), color: B.primary, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>Suggested STIX Objects</div>
                    <button onClick={addSelected} style={{ marginLeft: "auto", ...HP2.accentBtn }}>Add Selected to Bundle →</button>
                  </div>
                  {suggestions.flaggedIps?.length > 0 && (
                    <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "#ef444422", border: "1px solid #ef444444", fontSize: fs(12), color: "#ef4444" }}>
                      ⚠ Suspicious ports on: {suggestions.flaggedIps.join(", ")}
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[...suggestions.objects, ...suggestions.relationships].map(obj => {
                      const sel = selectedSuggestions.has(obj.id);
                      const c = getConf(obj.type);
                      return (
                        <div key={obj.id} onClick={() => { const next = new Set(selectedSuggestions); sel ? next.delete(obj.id) : next.add(obj.id); onSessionUpdate({ selectedSuggestionIds: [...next] }); }}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: sel ? c.color + "12" : B.bg, border: `1px solid ${sel ? c.color : B.border}`, cursor: "pointer" }}>
                          <TypeIcon type={obj.type} color={c.color} size={12} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: fs(11), color: c.color, fontWeight: 600 }}>{c.label}</div>
                            <div style={{ fontSize: fs(11), color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{obj.value || obj.description || `${obj.source_ref?.split("--")[0]} → ${obj.target_ref?.split("--")[0]}`}</div>
                          </div>
                          {sel && <span style={{ color: c.color, fontSize: fs(14) }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>)}

            {/* ── CONVERSATIONS TAB ────────────────────────────── */}
            {tab === "conversations" && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "14px 20px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>Conversations</div>
                  <span style={{ fontSize: fs(11), color: B.textMuted }}>{sortedConversations.length} flows</span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: fs(10), color: B.textDim }}>Sort by</span>
                    {["bytes", "packets", "duration"].map(s => (
                      <button key={s} onClick={() => setConvSort(s)} style={{ ...HP2.navBtn, padding: "3px 10px", fontSize: fs(10), borderColor: convSort === s ? B.primary : B.border, color: convSort === s ? B.primary : B.textMuted, background: convSort === s ? B.primary + "12" : "transparent" }}>{s}</button>
                    ))}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(11), fontFamily: "monospace" }}>
                    <thead>
                      <tr style={{ background: B.bgPanel }}>
                        {["Source", "Destination", "Port", "Proto", "Pkts", "Bytes", "Duration", "State"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: B.textDim, fontWeight: 600, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${B.border}`, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedConversations.slice(0, 200).map((c, i) => {
                        const stateColor = c.state === "established" ? "#10b981" : c.state === "attempted" ? "#f59e0b" : B.textDim;
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${B.border}22`, background: i % 2 === 0 ? "transparent" : B.bgPanel + "60" }}>
                            <td style={{ padding: "6px 12px", color: B.primary }}>{c.src}</td>
                            <td style={{ padding: "6px 12px", color: B.accent }}>{c.dst}</td>
                            <td style={{ padding: "6px 12px", color: B.textMuted }}>{c.dstPort || "—"}</td>
                            <td style={{ padding: "6px 12px" }}><span style={{ color: getPC(c.service || c.proto), fontWeight: 600 }}>{c.service || c.proto}</span></td>
                            <td style={{ padding: "6px 12px", color: B.textDim }}>{c.packets.toLocaleString()}</td>
                            <td style={{ padding: "6px 12px", color: B.text }}>{fmtBytes(c.bytes)}</td>
                            <td style={{ padding: "6px 12px", color: B.textDim }}>{c.duration > 0 ? `${c.duration.toFixed(2)}s` : "—"}</td>
                            <td style={{ padding: "6px 12px" }}><span style={{ fontSize: fs(10), padding: "2px 6px", borderRadius: 4, background: stateColor + "22", color: stateColor, fontWeight: 600 }}>{c.state}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── ANOMALIES TAB ────────────────────────────────── */}
            {tab === "anomalies" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {!stats.anomalies?.length && (
                  <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, padding: 40, textAlign: "center", color: B.textMuted, fontSize: fs(13) }}>
                    No anomalies detected in this capture.
                  </div>
                )}
                {(stats.anomalies || []).map((a, i) => {
                  const sev = SEVERITY_COLOR[a.severity] || B.textDim;
                  const label = ANOMALY_LABEL[a.type] || a.type;
                  return (
                    <div key={i} style={{ background: B.bgCard, border: `1px solid ${sev}44`, borderRadius: 10, padding: "14px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: sev, marginTop: 4, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                          <span style={{ fontSize: fs(12), fontWeight: 700, color: sev }}>{label}</span>
                          <span style={{ fontSize: fs(10), padding: "1px 6px", borderRadius: 4, background: sev + "22", color: sev, textTransform: "uppercase", letterSpacing: 0.5 }}>{a.severity}</span>
                        </div>
                        <div style={{ fontSize: fs(12), color: B.text, lineHeight: 1.5 }}>{a.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── HTTP / DNS TAB ───────────────────────────────── */}
            {tab === "http" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* HTTP URLs */}
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>
                    HTTP URLs ({stats.httpUrls?.length || 0})
                  </div>
                  <div style={{ maxHeight: 400, overflowY: "auto", padding: "10px 0" }}>
                    {(stats.httpUrls || []).map((u, i) => (
                      <div key={i} style={{ padding: "6px 18px", borderBottom: `1px solid ${B.border}22`, fontSize: fs(11) }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
                          <span style={{ color: B.primary, fontWeight: 600, fontFamily: "monospace", fontSize: fs(10) }}>{u.method}</span>
                          <span style={{ color: B.text, wordBreak: "break-all" }}>{u.host}</span>
                        </div>
                        <div style={{ color: B.textMuted, fontFamily: "monospace", wordBreak: "break-all" }}>{u.path}</div>
                        <div style={{ color: B.textDim, fontSize: fs(10), marginTop: 2 }}>{u.srcIp} → {u.dstIp}</div>
                      </div>
                    ))}
                    {!stats.httpUrls?.length && <div style={{ padding: "20px 18px", color: B.textDim, fontSize: fs(12) }}>No HTTP URLs captured.</div>}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* DNS frequency */}
                  <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>
                      DNS Queries ({stats.dnsFreq?.length || stats.dnsQueries?.length || 0})
                    </div>
                    <div style={{ maxHeight: 200, overflowY: "auto", padding: "10px 0" }}>
                      {(stats.dnsFreq || (stats.dnsQueries || []).map(q => ({ query: q, count: 1 }))).map((d, i) => (
                        <div key={i} style={{ padding: "5px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${B.border}22`, fontSize: fs(11) }}>
                          <span style={{ color: B.text, wordBreak: "break-all", fontFamily: "monospace" }}>{d.query}</span>
                          {d.count > 1 && <span style={{ fontSize: fs(10), color: B.textDim, flexShrink: 0, marginLeft: 8 }}>×{d.count}</span>}
                        </div>
                      ))}
                      {!stats.dnsFreq?.length && !stats.dnsQueries?.length && <div style={{ padding: "20px 18px", color: B.textDim, fontSize: fs(12) }}>No DNS queries captured.</div>}
                    </div>
                  </div>

                  {/* User agents */}
                  {stats.userAgents?.length > 0 && (
                    <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>
                        User Agents ({stats.userAgents.length})
                      </div>
                      <div style={{ maxHeight: 180, overflowY: "auto", padding: "10px 0" }}>
                        {stats.userAgents.map((ua, i) => (
                          <div key={i} style={{ padding: "5px 18px", fontSize: fs(11), color: B.text, borderBottom: `1px solid ${B.border}22`, wordBreak: "break-all", fontFamily: "monospace" }}>{ua}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TLS SNI */}
                  <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2 }}>
                      TLS / SNI ({stats.tlsSnis?.length || 0})
                    </div>
                    <div style={{ maxHeight: 180, overflowY: "auto", padding: "10px 0" }}>
                      {(stats.tlsSnis || []).map((s, i) => (
                        <div key={i} style={{ padding: "5px 18px", fontSize: fs(11), color: B.text, borderBottom: `1px solid ${B.border}22`, wordBreak: "break-all", fontFamily: "monospace" }}>{s}</div>
                      ))}
                      {!stats.tlsSnis?.length && <div style={{ padding: "20px 18px", color: B.textDim, fontSize: fs(12) }}>No TLS SNI detected.</div>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── PACKETS TAB ──────────────────────────────────── */}
            {tab === "packets" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                  <input
                    value={filterText}
                    onChange={e => { setFilterText(e.target.value); onSessionUpdate({ page: 0 }); }}
                    placeholder="ip:10.0.0.1  src:192.168  dst:8.8.8.8  port:443  proto:tls  host:example.com"
                    style={{ flex: "1 1 300px", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, color: B.text, padding: "9px 12px", fontSize: fs(12), outline: "none", fontFamily: "monospace" }}
                  />
                  {filterText && <button onClick={() => { setFilterText(""); onSessionUpdate({ page: 0 }); }} style={HP2.navBtn}>Clear</button>}
                  <span style={{ fontSize: fs(11), color: B.textDim }}>{filteredPackets.length.toLocaleString()} packets{normalizedFilter ? " (filtered)" : ""}</span>
                </div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ padding: "10px 18px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: fs(11), color: B.textDim }}>showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredPackets.length)} of {filteredPackets.length}</span>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button onClick={() => setPageIndex(Math.max(0, page - 1))} disabled={page === 0} style={HP2.pageBtn}>‹</button>
                      <span style={{ fontSize: fs(11), color: B.textDim, padding: "0 4px", alignSelf: "center" }}>{page + 1}/{totalPages}</span>
                      <button onClick={() => setPageIndex(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={HP2.pageBtn}>›</button>
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(11), fontFamily: "monospace" }}>
                      <thead>
                        <tr style={{ background: B.bgPanel }}>
                          {["#", "Time", "Src IP", "Src Port", "Dst IP", "Dst Port", "Proto", "Len", "TTL", "Info"].map(h => (
                            <th key={h} style={{ padding: "7px 12px", textAlign: "left", color: B.textDim, fontWeight: 600, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1, borderBottom: `1px solid ${B.border}`, whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPackets.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((p, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${B.border}22`, background: i % 2 === 0 ? "transparent" : B.bgPanel + "40" }}>
                            <td style={{ padding: "5px 12px", color: B.textDim }}>{page * PAGE_SIZE + i + 1}</td>
                            <td style={{ padding: "5px 12px", color: B.textDim, whiteSpace: "nowrap" }}>{p.ts.toFixed(3)}</td>
                            <td style={{ padding: "5px 12px", color: B.primary }}>{p.srcIp}</td>
                            <td style={{ padding: "5px 12px", color: B.textMuted }}>{p.srcPort ?? "—"}</td>
                            <td style={{ padding: "5px 12px", color: B.accent }}>{p.dstIp}</td>
                            <td style={{ padding: "5px 12px", color: B.textMuted }}>{p.dstPort ?? "—"}</td>
                            <td style={{ padding: "5px 12px" }}><span style={{ color: getPC(p.service), fontWeight: 600 }}>{p.service}</span></td>
                            <td style={{ padding: "5px 12px", color: B.textDim }}>{p.len}</td>
                            <td style={{ padding: "5px 12px", color: B.textDim }}>{p.ttl ?? "—"}</td>
                            <td style={{ padding: "5px 12px", color: B.textMuted }}>{p.userAgent || p.httpHost || p.dnsQuery || p.tlsSni || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <button onClick={() => onSessionReset()} style={{ ...HP2.navBtn, alignSelf: "flex-start", marginTop: 8 }}>← Upload New File</button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

const HP2 = {
  navBtn: { background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 },
  accentBtn: { background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 },
  premiumBtn: { background: `linear-gradient(135deg, #f59e0b, ${B.primary})`, border: "none", color: B.white, padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 },
  pageBtn: { background: B.bgCard, border: `1px solid ${B.border}`, color: B.textMuted, padding: "5px 12px", borderRadius: 5, cursor: "pointer", fontSize: fs(15), fontFamily: "'Satoshi', sans-serif" },
};

// ─── Premium Gate ───
function PremiumGate({ children, onUpgrade, above }) {
  const [hovered, setHovered] = useState(false);
  const tipPos = above
    ? { bottom: "calc(100% + 8px)", top: "auto", left: "50%", transform: "translateX(-50%)" }
    : { top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" };
  const arrowPos = above
    ? { bottom: -6, top: "auto", borderTop: "none", borderLeft: "none", borderBottom: `1px solid ${B.accent}55`, borderRight: `1px solid ${B.accent}55` }
    : { top: -6, borderBottom: "none", borderRight: "none" };
  return (
    <div style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {children}
      {hovered && (
        <div style={{
          position: "absolute", ...tipPos,
          background: "#0e0e10", border: `1px solid ${B.accent}55`, borderRadius: 10, padding: "12px 16px",
          width: 220, zIndex: 200, boxShadow: "0 8px 32px rgba(0,0,0,0.6)", pointerEvents: "none",
        }}>
          <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%) rotate(45deg)", width: 10, height: 10, background: "#0e0e10", ...arrowPos }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: fs(16) }}>✦</span>
            <span style={{ fontSize: fs(12), fontWeight: 800, color: B.white, fontFamily: "'Satoshi', sans-serif" }}>Idle Hour Premium</span>
          </div>
          <div style={{ fontSize: fs(11), color: B.textMuted, lineHeight: 1.5, marginBottom: 10, fontFamily: "'Satoshi', sans-serif" }}>
            Unlock STIX export, PCAP extraction, and threat feed object creation.
          </div>
          <div style={{ fontSize: fs(11), fontWeight: 700, color: B.accent, fontFamily: "'Satoshi', sans-serif", textAlign: "center" }}>
            Upgrade to unlock →
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dark Web Intelligence Page ───
function MalwareSandboxPage({ onNavigate, onLogin, onLogout, user, onAddToBundle, pcapSession, collapsed, onToggleCollapse }) {
  const [sampleFile, setSampleFile] = useState(null);
  const [family, setFamily] = useState("");
  const [campaign, setCampaign] = useState("");
  const [notes, setNotes] = useState("");
  const [zipPassword, setZipPassword] = useState("");
  const [sessions, setSessions] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const fileRef = useRef(null);
  const isPremium = !!user;
  const isZipFile = sampleFile?.name?.toLowerCase().endsWith(".zip");
  const activeSession = sessions.find(s => s.id === activeTab) || null;
  const [dragTabId, setDragTabId] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState(null);
  const [inputMode, setInputMode] = useState("file");
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState(null);

  const reorderTabs = (fromId, toId) => {
    if (fromId === toId) return;
    setSessions(prev => {
      const from = prev.findIndex(s => s.id === fromId);
      const to = prev.findIndex(s => s.id === toId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
  };

  const newUUID = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const now = () => new Date().toISOString();
  const esc = (value) => String(value || "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  const uniq = (items) => [...new Set((items || []).map(v => String(v || "").trim()).filter(Boolean))];
  const short = (value, max = 72) => {
    const s = String(value || "");
    return s.length > max ? `${s.slice(0, max - 3)}...` : s;
  };
  const hashAlg = (hash = "") => hash.length === 32 ? "MD5" : hash.length === 40 ? "SHA-1" : "SHA-256";
  const rel = (relationshipType, sourceRef, targetRef, description, created) => ({
    type: "relationship", id: `relationship--${newUUID()}`, spec_version: "2.1",
    relationship_type: relationshipType, source_ref: sourceRef, target_ref: targetRef,
    description, created, modified: now(),
  });

  const runAnalysis = async () => {
    if (!sampleFile || ANALYZE_LOCKED) return;
    if (sampleFile.size > ANALYZE_MAX_MB * 1048576) { setError(`That file is ${(sampleFile.size / 1048576).toFixed(1)} MB; the limit here is ${ANALYZE_MAX_MB} MB.`); return; }
    setLoading(true); setError(null); setNeedsPassword(false);
    try {
      const form = new FormData();
      form.append("sample", sampleFile);
      form.append("family", family.trim());
      form.append("campaign", campaign.trim());
      form.append("notes", notes.trim());
      if (isZipFile) form.append("zipPassword", zipPassword.trim());
      const r = await fetch("/api/sandbox/analyze", { method: "POST", credentials: "include", body: form });
      const d = await r.json();
      if (!r.ok) {
        if (d.needsPassword) setNeedsPassword(true);
        throw new Error(d.error || "Sandbox analysis failed.");
      }
      const sid = Date.now();
      const newSession = { id: sid, filename: d.analysis.file.name, analysis: d.analysis, zipExtraction: d.zipExtraction || null };
      setSessions(prev => { const u = [...prev, newSession]; return u.length > 10 ? u.slice(-10) : u; });
      setActiveTab(sid);
      setSampleFile(null); setFamily(""); setCampaign(""); setNotes(""); setZipPassword(""); setError(null); setNeedsPassword(false); setDragOver(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const clear = () => {
    setSampleFile(null); setFamily(""); setCampaign(""); setNotes(""); setZipPassword("");
    setError(null); setNeedsPassword(false); setDragOver(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const runUrlAnalysis = async () => {
    if (!urlInput.trim() || ANALYZE_LOCKED) return;
    setUrlLoading(true); setUrlError(null);
    try {
      const r = await fetch("/api/sandbox/url", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: urlInput.trim(), notes: notes.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "URL analysis failed.");
      const sid = Date.now();
      setSessions(prev => { const u = [...prev, { id: sid, filename: urlInput.trim(), analysis: d.analysis }]; return u.length > 10 ? u.slice(-10) : u; });
      setActiveTab(sid);
      setUrlInput(""); setUrlError(null);
    } catch (e) { setUrlError(e.message); }
    setUrlLoading(false);
  };

  const buildStixFromUrl = (a) => {
    const createdAt = a.submittedAt || now();
    const urlValue = a.finalUrl || a.url;
    const urlId = `url--${newUUID()}`;
    const indicatorId = `indicator--${newUUID()}`;
    const reportId = `report--${newUUID()}`;
    const objects = [];
    const relationships = [];
    objects.push({ type: "url", id: urlId, spec_version: "2.1", value: urlValue, labels: uniq(["sandbox", "url-analysis", a.verdict]), created: createdAt, modified: now() });
    objects.push({
      type: "indicator", id: indicatorId, spec_version: "2.1",
      name: `URL: ${short(urlValue, 80)}`,
      description: `URL analyzed by Idle Hour sandbox. Verdict: ${a.verdict}. Score: ${a.score}/100.`,
      pattern: `[url:value = '${esc(urlValue)}']`, pattern_type: "stix",
      indicator_types: [a.verdict === "malicious" ? "malicious-activity" : "anomalous-activity"],
      valid_from: createdAt, confidence: Math.min(100, a.score),
      labels: uniq(["sandbox", "url-analysis", a.verdict]),
      created: createdAt, modified: now(),
    });
    relationships.push(rel("indicates", indicatorId, urlId, "Indicator describes the analyzed URL.", createdAt));
    try {
      const domain = new URL(urlValue).hostname;
      if (domain && !/^\d+\.\d+\.\d+\.\d+$/.test(domain)) {
        const domainId = `domain-name--${newUUID()}`;
        objects.push({ type: "domain-name", id: domainId, spec_version: "2.1", value: domain, labels: ["sandbox", "extracted"], created: createdAt, modified: now() });
        relationships.push(rel("related-to", urlId, domainId, "URL is hosted on this domain.", createdAt));
      }
    } catch (_) {}
    (a.iocs?.ipv4 || []).slice(0, 10).forEach(ip => {
      const ipId = `ipv4-addr--${newUUID()}`;
      const indId = `indicator--${newUUID()}`;
      objects.push({ type: "ipv4-addr", id: ipId, spec_version: "2.1", value: ip, labels: ["sandbox", "extracted"], created: createdAt, modified: now() });
      objects.push({ type: "indicator", id: indId, spec_version: "2.1", name: `Extracted IP: ${ip}`, pattern: `[ipv4-addr:value = '${esc(ip)}']`, pattern_type: "stix", indicator_types: ["malicious-activity"], valid_from: createdAt, labels: ["sandbox", "extracted"], created: createdAt, modified: now() });
      relationships.push(rel("related-to", indId, indicatorId, "IP extracted from analyzed page content.", createdAt));
    });
    (a.iocs?.domains || []).slice(0, 10).forEach(d => {
      const dId = `domain-name--${newUUID()}`;
      objects.push({ type: "domain-name", id: dId, spec_version: "2.1", value: d, labels: ["sandbox", "extracted"], created: createdAt, modified: now() });
      relationships.push(rel("related-to", dId, indicatorId, "Domain extracted from analyzed page content.", createdAt));
    });
    const report = {
      type: "report", id: reportId, spec_version: "2.1",
      name: `URL Analysis — ${short(urlValue, 60)}`,
      description: a.report || `Idle Hour URL analysis. Verdict: ${a.verdict}.`,
      published: createdAt, created: createdAt, modified: now(),
      report_types: ["threat-report"],
      labels: uniq(["sandbox", "url-analysis", a.verdict]),
      object_refs: [...objects.map(o => o.id), ...relationships.map(r => r.id)],
    };
    return [report, ...objects, ...relationships];
  };

  const closeTab = (sid) => {
    const remaining = sessions.filter(x => x.id !== sid);
    setSessions(remaining);
    if (activeTab === sid) {
      const idx = sessions.findIndex(x => x.id === sid);
      setActiveTab(remaining[idx]?.id ?? remaining[idx - 1]?.id ?? null);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) { setSampleFile(file); setError(null); setNeedsPassword(false); setZipPassword(""); }
  };

  const buildStix = (a) => {
    if (!a?.file?.hashes) return [];
    const created = a.submittedAt || now();
    const familyName = family.trim() || a.family || "Unknown Malware";
    const labels = uniq(["sandbox", "automated-analysis", a.verdict, familyName, ...(a.fileType?.tags || [])]);
    const refs = [{ source_name: "Idle Hour Sandbox", external_id: a.id }];
    const malwareId = `malware--${newUUID()}`;
    const fileId = `file--${newUUID()}`;
    const hashIndicatorId = `indicator--${newUUID()}`;
    const observedId = `observed-data--${newUUID()}`;
    const objects = [];
    const relationships = [];
    const observableRefs = [fileId];

    objects.push({
      type: "malware", id: malwareId, spec_version: "2.1", name: familyName,
      description: `Malware family from sandbox triage for ${a.file.name}. Verdict: ${a.verdict}.`,
      malware_types: ["unknown"], is_family: true, labels, external_references: refs, created, modified: now(),
    });
    objects.push({
      type: "file", id: fileId, spec_version: "2.1", name: `${familyName} - ${short(a.file.hashes.sha256, 48)}`,
      hashes: { MD5: a.file.hashes.md5, "SHA-1": a.file.hashes.sha1, "SHA-256": a.file.hashes.sha256 },
      labels, external_references: refs, ...(a.file.mime ? { mime_type: a.file.mime } : {}), ...(a.file.name ? { x_file_name: a.file.name } : {}), ...(a.file.size ? { size: a.file.size } : {}), created, modified: now(),
    });
    objects.push({
      type: "indicator", id: hashIndicatorId, spec_version: "2.1", name: `${familyName} sample - ${short(a.file.hashes.sha256, 32)}`,
      description: `Hash indicator for sandboxed sample ${a.file.name}. Score: ${a.score}/100.`,
      pattern: `[file:hashes.'SHA-256' = '${esc(a.file.hashes.sha256)}']`, pattern_type: "stix",
      indicator_types: ["malicious-activity"], valid_from: created,
      confidence: Math.min(100, Math.max(0, Math.round(Number(a.score || 0)))),
      labels: uniq(["malicious-activity", ...labels]), external_references: refs, created, modified: now(),
      x_idle_hour_verdict: a.verdict, x_idle_hour_score: a.score,
    });
    relationships.push(
      rel("indicates", hashIndicatorId, malwareId, `${a.file.name} hash indicator indicates ${familyName}.`, created),
      rel("has-sample", malwareId, fileId, `${familyName} has sandboxed sample ${a.file.name}.`, created),
      rel("related-to", hashIndicatorId, fileId, "Hash indicator describes the sandboxed file object.", created)
    );

    const campaignName = campaign.trim() || a.campaign;
    if (campaignName) {
      const campaignId = `campaign--${newUUID()}`;
      objects.push({
        type: "campaign", id: campaignId, spec_version: "2.1", name: campaignName,
        description: `Campaign associated with sandbox analysis of ${a.file.name}.`,
        labels: uniq(["sandbox", campaignName, familyName]), created, modified: now(),
      });
      relationships.push(rel("uses", campaignId, malwareId, `${campaignName} uses ${familyName}.`, created));
    }

    const addIoc = (type, value) => {
      const indicatorId = `indicator--${newUUID()}`;
      const observableId = `${type}--${newUUID()}`;
      const kind = type === "url" ? "url" : type === "domain-name" ? "domain" : type === "ipv4-addr" ? "ip" : "hash";
      const pattern = type === "file" ? `[file:hashes.'${hashAlg(value)}' = '${esc(value)}']` : `[${type}:value = '${esc(value)}']`;
      objects.push({
        type: "indicator", id: indicatorId, spec_version: "2.1", name: `Sandbox ${kind}: ${short(value, 72)}`,
        description: `IOC extracted from sandbox triage of ${a.file.name}.`, pattern, pattern_type: "stix",
        indicator_types: ["malicious-activity"], valid_from: created,
        confidence: Math.min(100, Math.max(0, Math.round(Number(a.score || 0)))),
        labels: uniq(["malicious-activity", ...labels, kind]), external_references: refs, created, modified: now(),
      });
      objects.push(type === "file"
        ? { type: "file", id: observableId, spec_version: "2.1", name: `Extracted hash - ${short(value, 32)}`, hashes: { [hashAlg(value)]: value }, labels: uniq(["sandbox", familyName, "extracted-hash"]), created, modified: now() }
        : { type, id: observableId, spec_version: "2.1", value, labels: uniq(["sandbox", familyName, kind]), created, modified: now() });
      relationships.push(rel("indicates", indicatorId, malwareId, `Sandbox-extracted ${kind} indicates ${familyName}.`, created), rel("related-to", indicatorId, observableId, `Indicator is based on an extracted ${kind} observable.`, created));
      observableRefs.push(observableId);
    };

    (a.iocs?.urls || []).slice(0, 20).forEach(v => addIoc("url", v));
    (a.iocs?.domains || []).slice(0, 20).forEach(v => addIoc("domain-name", v));
    (a.iocs?.ipv4 || []).slice(0, 20).forEach(v => addIoc("ipv4-addr", v));
    (a.iocs?.hashes || []).slice(0, 20).forEach(v => addIoc("file", v));
    objects.push({ type: "observed-data", id: observedId, spec_version: "2.1", first_observed: created, last_observed: created, number_observed: 1, object_refs: observableRefs, labels: uniq(["sandbox", familyName]), created, modified: now() });

    const report = {
      type: "report", id: `report--${newUUID()}`, spec_version: "2.1", name: `Sandbox Analysis - ${a.file.name}`,
      description: a.report || `Automated sandbox triage for ${a.file.name}. Verdict: ${a.verdict}.`,
      published: created, created, modified: now(), report_types: ["malware"], labels, external_references: refs,
      object_refs: [...objects.map(o => o.id), ...relationships.map(r => r.id)],
      x_idle_hour_analysis_mode: a.mode, x_idle_hour_runtime_detonation: !!a.runtimeDetonation,
    };
    return [report, ...objects, ...relationships];
  };

  const selectedSize = sampleFile ? (sampleFile.size >= 1024 * 1024 ? `${(sampleFile.size / 1024 / 1024).toFixed(2)} MB` : `${(sampleFile.size / 1024).toFixed(1)} KB`) : "";

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="sandbox" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column" }}>
      <GroupTabs page="sandbox" onNavigate={onNavigate} />
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>
      <div style={{ padding: "14px 28px", borderBottom: `1px solid ${B.border}`, background: B.bgPanel, display: "flex", alignItems: "center", gap: 14 }}>
        <div>
          <div style={{ fontSize: fs(16), fontWeight: 900, color: B.white }}>Malware Sandbox</div>
          <div style={{ fontSize: fs(10), color: B.textDim, letterSpacing: 2, textTransform: "uppercase" }}>Sample Triage - IOCs - STIX Reporting</div>
        </div>
      </div>
      {sessions.length > 0 && (
        <div style={{ display: "flex", alignItems: "stretch", background: B.bgPanel, borderBottom: `1px solid ${B.border}`, overflowX: "auto", flexShrink: 0 }}>
          <button
            onClick={() => setActiveTab(null)}
            style={{ padding: "10px 18px", fontSize: fs(11), fontWeight: 700, background: "transparent", border: "none", borderBottom: activeTab === null ? "2px solid #6366f1" : "2px solid transparent", color: activeTab === null ? "#a5b4fc" : B.textDim, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
          >+ New</button>
          {sessions.map(s => {
            const isActive = activeTab === s.id;
            const isDragging = dragTabId === s.id;
            const isDropTarget = dragOverTabId === s.id && !isDragging;
            const vc = s.analysis.verdict === "malicious" ? "#ef4444" : s.analysis.verdict === "suspicious" ? "#f59e0b" : "#22c55e";
            return (
              <div
                key={s.id}
                draggable
                onDragStart={e => { e.dataTransfer.effectAllowed = "move"; setDragTabId(s.id); }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverTabId(s.id); }}
                onDragLeave={() => setDragOverTabId(null)}
                onDrop={e => { e.preventDefault(); reorderTabs(dragTabId, s.id); setDragTabId(null); setDragOverTabId(null); }}
                onDragEnd={() => { setDragTabId(null); setDragOverTabId(null); }}
                style={{ display: "flex", alignItems: "stretch", borderLeft: isDropTarget ? "2px solid #6366f1" : `1px solid ${B.border}`, flexShrink: 0, opacity: isDragging ? 0.4 : 1, transition: "opacity 0.15s, border-color 0.1s", cursor: "grab" }}
              >
                <button
                  onClick={() => setActiveTab(s.id)}
                  style={{ padding: "10px 6px 10px 14px", fontSize: fs(11), fontWeight: isActive ? 700 : 500, background: "transparent", border: "none", borderBottom: isActive ? `2px solid ${vc}` : "2px solid transparent", color: isActive ? B.white : B.textDim, cursor: "grab", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 7 }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: vc, flexShrink: 0 }} />
                  {s.filename}
                </button>
                <button
                  onClick={() => closeTab(s.id)}
                  style={{ padding: "10px 10px", fontSize: fs(13), background: "transparent", border: "none", borderBottom: isActive ? `2px solid ${vc}` : "2px solid transparent", color: B.textDim, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}
                >×</button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
        {!activeSession ? (
          <>
            {/* Mode toggle */}
            <div style={{ display: "flex", background: B.bgCard, borderRadius: 8, padding: 3, gap: 2, marginBottom: 20, width: "fit-content" }}>
              {[["file", "File Sample"], ["url", "URL Analysis"]].map(([mode, label]) => (
                <button key={mode} onClick={() => { setInputMode(mode); setError(null); setUrlError(null); }} style={{ ...HP2.accentBtn, ...(inputMode !== mode ? { background: "transparent", color: B.textDim, fontWeight: 500 } : {}), padding: "7px 20px", fontSize: fs(12) }}>{label}</button>
              ))}
            </div>

            {(error || urlError) && <div style={{ background: "#ef444422", border: "1px solid #ef444444", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: fs(12), marginBottom: 16 }}>{error || urlError}</div>}

            <div style={{ background: "#f59e0b14", border: "1px solid #f59e0b44", borderRadius: 8, padding: "12px 14px", color: "#f59e0b", fontSize: fs(12), lineHeight: 1.5, marginBottom: 16 }}>
              {inputMode === "file"
                ? "Static triage mode — the server hashes the file and scans readable strings for IOCs. No execution or detonation occurs."
                : "URL analysis mode — the server fetches the page and inspects HTML/JS for malicious patterns. No browser or JavaScript engine is used."}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, alignItems: "start" }}>
              <div style={{ background: B.bgPanel, border: `1px solid ${B.border}`, borderRadius: 8, padding: 16 }}>
                {ANALYZE_LOCKED ? (
                  <ToolLocked style={{ padding: "40px 18px" }} what={inputMode === "url"
                    ? "URL analysis fetches a page server-side (no browser, no JavaScript), inspects it for phishing and malicious patterns, checks it against URLhaus, and builds STIX indicators and a report."
                    : "File analysis is static triage: it hashes a sample, extracts URLs, domains, IPs and hashes from its strings, inspects PE imports, entropy and document markers, and builds a STIX report. Nothing is executed."} />
                ) : inputMode === "url" ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    <input
                      value={urlInput} onChange={e => setUrlInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !urlLoading && runUrlAnalysis()}
                      placeholder="https://suspicious-url.example.com/payload"
                      style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "10px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" }}
                    />
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Analyst notes (optional)" rows={3} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "9px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none", resize: "vertical" }} />
                    <button onClick={runUrlAnalysis} disabled={!urlInput.trim() || urlLoading} style={{ ...HP2.accentBtn, opacity: !urlInput.trim() || urlLoading ? 0.55 : 1, cursor: !urlInput.trim() || urlLoading ? "not-allowed" : "pointer" }}>{urlLoading ? "Analyzing URL..." : "Analyze URL"}</button>
                    <div style={{ fontSize: fs(10.5), color: B.textDim, lineHeight: 1.5 }}>Idle Hour's server fetches the page (no browser or JavaScript, public addresses only, the first 512 KB) and checks the URL against URLhaus. {ANALYZE_PER_HOUR} analyses an hour per address.</div>
                  </div>
                ) : (
                  <>
                    <input ref={fileRef} type="file" onChange={e => { const f = e.target.files?.[0]; if (f) { setSampleFile(f); setError(null); setNeedsPassword(false); setZipPassword(""); } }} style={{ display: "none" }} />
                    <button onClick={() => fileRef.current?.click()} style={{ ...HP2.accentBtn, width: "100%", marginBottom: 12 }}>Choose Sample</button>
                    <div
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleFileDrop}
                      onClick={() => !sampleFile && fileRef.current?.click()}
                      style={{ marginBottom: 12, cursor: sampleFile ? "default" : "pointer" }}
                    >
                      {sampleFile
                        ? <div style={{ background: B.bgCard, border: `1px solid ${isZipFile ? "#f59e0b88" : B.border}`, borderRadius: 8, padding: "18px 16px", minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {isZipFile && <span style={{ fontSize: fs(10), background: "#f59e0b22", color: "#f59e0b", borderRadius: 4, padding: "2px 7px", fontWeight: 700, flexShrink: 0 }}>ZIP</span>}
                              <div style={{ fontSize: fs(12), fontWeight: 700, color: B.white, wordBreak: "break-all", flex: 1 }}>{sampleFile.name}</div>
                            </div>
                            <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 5 }}>{selectedSize} · click "Choose Sample" to change</div>
                          </div>
                        : <div style={{ background: dragOver ? "#6366f122" : B.bgCard, border: `2px dashed ${dragOver ? "#6366f1" : B.borderLight}`, borderRadius: 8, padding: "80px 24px", textAlign: "center", transition: "all 0.15s" }}>
                            <div style={{ fontSize: fs(36), marginBottom: 12, opacity: 0.35 }}>⬆</div>
                            <div style={{ fontSize: fs(14), fontWeight: 700, color: dragOver ? "#6366f1" : B.white, marginBottom: 6 }}>{dragOver ? "Release to drop" : "Drop sample here"}</div>
                            <div style={{ fontSize: fs(11), color: B.textDim }}>or click to browse · any file type up to {ANALYZE_MAX_MB} MB · ZIP archives extracted automatically</div>
                            <div style={{ fontSize: fs(10.5), color: B.textDim, marginTop: 6 }}>Static triage only: never run, read in memory and not kept</div>
                          </div>
                      }
                    </div>
                    <div style={{ display: "grid", gap: 10 }}>
                      {isZipFile && (
                        <div style={{ background: "#f59e0b0a", border: `1px solid #f59e0b44`, borderRadius: 6, padding: "10px 12px" }}>
                          <div style={{ fontSize: fs(10), color: "#f59e0b", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>ZIP Archive Detected</div>
                          <input value={zipPassword} onChange={e => setZipPassword(e.target.value)} placeholder={needsPassword ? "Password required — enter ZIP password" : "ZIP password (optional — tries 'infected', 'malware', etc. automatically)"} style={{ background: B.bgCard, border: `1px solid ${needsPassword ? "#ef444466" : B.border}`, borderRadius: 6, color: B.text, padding: "8px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" }} />
                          <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 5 }}>The payload inside the ZIP will be analyzed, not the ZIP container itself.</div>
                        </div>
                      )}
                      <input value={family} onChange={e => setFamily(e.target.value)} placeholder="Malware family, if known" style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "9px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" }} />
                      <input value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="Campaign or case name" style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "9px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" }} />
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Analyst notes" rows={4} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "9px 12px", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none", resize: "vertical", minHeight: 92 }} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={runAnalysis} disabled={!sampleFile || loading} style={{ ...HP2.accentBtn, flex: 1, opacity: !sampleFile || loading ? 0.55 : 1, cursor: !sampleFile || loading ? "not-allowed" : "pointer" }}>{loading ? "Analyzing..." : "Analyze Sample"}</button>
                        <button onClick={clear} style={HP2.navBtn}>Clear</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
                {(inputMode === "url"
                  ? [["Fetch", "Server-side HTTP only — no JS engine"], ["Inspect", "HTML, iframes, eval, base64, phishing"], ["Cross-check", "URLhaus known-bad lookup"], ["STIX", "URL, domain, IP indicators + report"]]
                  : [["Hash", "MD5 / SHA-1 / SHA-256"], ["Extract", "URLs, domains, IPs, hashes"], ["Deep scan", "PE imports, entropy, PDF markers, scripts"], ["STIX", "Report, file, malware, rels"]]
                ).map(([title, desc]) => <div key={title} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14, minHeight: 88 }}><div style={{ fontSize: fs(12), fontWeight: 800, color: B.white, marginBottom: 6 }}>{title}</div><div style={{ fontSize: fs(10), color: B.textDim, lineHeight: 1.4 }}>{desc}</div></div>)}
              </div>
            </div>
            {(loading || urlLoading) && <div style={{ color: B.textDim, fontSize: fs(12), marginTop: 18 }}>{urlLoading ? "Fetching and analyzing URL..." : "Running static sandbox triage..."}</div>}
          </>
        ) : (() => {
          const analysis = activeSession.analysis;

          // ── URL analysis result view ──────────────────────────────
          if (analysis.mode === "url-analysis") {
            const vc = analysis.verdict === "malicious" ? "#ef4444" : analysis.verdict === "suspicious" ? "#f59e0b" : "#22c55e";
            const iocs = analysis.iocs || {};
            const allSignals = [...(analysis.urlStructure?.signals || []), ...(analysis.pageAnalysis?.signals || [])];
            const highSignals = allSignals.filter(s => s.severity === "high");
            const downloadReport = () => { const blob = new Blob([analysis.report || ""], { type: "text/markdown" }); const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `url-analysis-${Date.now()}.md` }); a.click(); URL.revokeObjectURL(a.href); };
            return <div style={{ display: "grid", gap: 16 }}>
              {/* Verdict banner */}
              <div style={{ background: vc + "14", border: `1px solid ${vc}55`, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: vc, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: fs(13), fontWeight: 800, color: B.white, wordBreak: "break-all" }}>{analysis.url}</div>
                  {analysis.finalUrl !== analysis.url && <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 2 }}>Resolved to: {analysis.finalUrl}</div>}
                  <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 2 }}>
                    {analysis.statusCode ? `HTTP ${analysis.statusCode}` : "No response"}{analysis.contentType ? ` · ${analysis.contentType.split(";")[0]}` : ""}{analysis.responseSize ? ` · ${(analysis.responseSize / 1024).toFixed(1)} KB` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: fs(14), fontWeight: 900, color: vc, textTransform: "uppercase" }}>{analysis.verdict}</div>
                  <div style={{ fontSize: fs(10), color: B.textDim }}>Score {analysis.score}/100</div>
                </div>
              </div>

              {/* URLhaus hit */}
              {analysis.urlhausResult?.found && (
                <div style={{ background: "#ef444422", border: "1px solid #ef444466", borderRadius: 8, padding: "10px 14px", display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: fs(10), background: "#ef444444", color: "#ef4444", borderRadius: 4, padding: "2px 7px", fontWeight: 700, flexShrink: 0 }}>URLhaus</span>
                  <div style={{ fontSize: fs(12), color: "#ef4444", fontWeight: 700 }}>Known malicious — Threat: {analysis.urlhausResult.threat || "unknown"} · Status: {analysis.urlhausResult.urlStatus || "unknown"}</div>
                </div>
              )}

              {/* Redirect chain */}
              {analysis.redirectChain?.length > 0 && (
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Redirect Chain ({analysis.redirectChain.length} hop{analysis.redirectChain.length !== 1 ? "s" : ""})</div>
                  {analysis.redirectChain.map((r, i) => (
                    <div key={i} style={{ fontSize: fs(11), color: B.textMuted, marginBottom: 5, display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span style={{ fontSize: fs(9), background: "#f59e0b22", color: "#f59e0b", borderRadius: 3, padding: "1px 5px", flexShrink: 0 }}>HTTP {r.status}</span>
                      <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{r.from} → {r.to}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
                {/* Signals */}
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Detection Signals ({allSignals.length})</div>
                  {allSignals.length > 0
                    ? allSignals.map((s, i) => (
                        <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${B.border}22` }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: fs(9), padding: "1px 5px", borderRadius: 3, fontWeight: 700, background: s.severity === "high" ? "#ef444422" : s.severity === "medium" ? "#f59e0b22" : "#22c55e22", color: s.severity === "high" ? "#ef4444" : s.severity === "medium" ? "#f59e0b" : "#22c55e" }}>{s.severity.toUpperCase()}</span>
                            <span style={{ fontSize: fs(11), fontWeight: 700, color: B.white }}>{s.label}</span>
                          </div>
                          <div style={{ fontSize: fs(10), color: B.textDim, lineHeight: 1.5 }}>{s.reason}</div>
                        </div>
                      ))
                    : <div style={{ fontSize: fs(11), color: B.textDim }}>No suspicious signals detected.</div>
                  }
                </div>

                {/* Page stats + IOCs */}
                <div style={{ display: "grid", gap: 12 }}>
                  {analysis.pageAnalysis && (
                    <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                      <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Page Structure</div>
                      {[
                        ["Scripts", analysis.pageAnalysis.scriptCount, `(${analysis.pageAnalysis.externalScripts} external)`],
                        ["Iframes", analysis.pageAnalysis.iframeCount, ""],
                        ["Forms", analysis.pageAnalysis.formCount, analysis.pageAnalysis.hasPasswordField ? "· password field present" : ""],
                      ].map(([label, val, note]) => (
                        <div key={label} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${B.border}22` }}>
                          <div style={{ width: 70, fontSize: fs(10), color: B.textDim }}>{label}</div>
                          <div style={{ fontSize: fs(11), color: B.text, fontWeight: 700 }}>{val}</div>
                          {note && <div style={{ fontSize: fs(10), color: B.textDim }}>{note}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {analysis.fetchError && (
                    <div style={{ background: "#f59e0b0a", border: "1px solid #f59e0b33", borderRadius: 8, padding: 12, fontSize: fs(11), color: "#f59e0b" }}>
                      Could not fetch page: {analysis.fetchError}
                    </div>
                  )}
                  <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Extracted IOCs</div>
                    {[["URLs", iocs.urls || [], "#ef4444"], ["Domains", iocs.domains || [], "#22d3ee"], ["IPv4", iocs.ipv4 || [], "#06b6d4"]].map(([label, values, color]) => (
                      <div key={label} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: fs(10), color, fontWeight: 800, marginBottom: 4 }}>{label} ({values.length})</div>
                        {values.slice(0, 8).map((v, i) => <div key={i} style={{ fontSize: fs(10), color: B.textMuted, fontFamily: "monospace", wordBreak: "break-all", background: B.bg, borderRadius: 4, padding: "3px 6px", marginBottom: 3 }}>{v}</div>)}
                        {!values.length && <div style={{ fontSize: fs(10), color: B.textDim }}>None</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Report */}
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700 }}>Automated Report</div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    {isPremium ? <button onClick={() => onAddToBundle(buildStixFromUrl(analysis))} style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "4px 10px" }}>Add to Bundle</button> : <PremiumGate above><button disabled style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "4px 10px", opacity: 0.5, cursor: "not-allowed" }}>Add to Bundle</button></PremiumGate>}
                    <button onClick={() => navigator.clipboard?.writeText(analysis.report || "")} style={{ ...HP2.navBtn, fontSize: fs(10), padding: "4px 10px" }}>Copy</button>
                    <button onClick={downloadReport} style={{ ...HP2.navBtn, fontSize: fs(10), padding: "4px 10px" }}>Save .md</button>
                  </div>
                </div>
                <pre style={{ margin: 0, padding: 14, maxHeight: 320, overflow: "auto", color: B.textMuted, fontSize: fs(11), lineHeight: 1.55, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{analysis.report}</pre>
              </div>
            </div>;
          }

          // ── File analysis result view ─────────────────────────────
          const zipExtraction = activeSession.zipExtraction;
          const iocs = analysis.iocs || {};
          const verdictColor = analysis.verdict === "malicious" ? "#ef4444" : analysis.verdict === "suspicious" ? "#f59e0b" : "#22c55e";
          const iocSections = [["URLs", iocs.urls || [], "#ef4444"], ["Domains", iocs.domains || [], "#22d3ee"], ["IPv4", iocs.ipv4 || [], "#06b6d4"], ["Hashes", iocs.hashes || [], "#a3e635"], ["Emails", iocs.emails || [], "#38bdf8"]];
          const totalIocs = iocSections.reduce((sum, [, values]) => sum + values.length, 0);
          const notable = analysis.notableStrings || (analysis.suspiciousStrings || []).map(value => ({ value, reasons: ["Matched a suspicious static string rule"], tags: ["Static match"] }));
          const downloadReport = () => { const blob = new Blob([analysis.report || ""], { type: "text/markdown" }); const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `sandbox-report-${(analysis.file?.hashes?.sha256 || "sample").slice(0, 12)}.md` }); a.click(); URL.revokeObjectURL(a.href); };
          return <div style={{ display: "grid", gap: 16 }}>
            {zipExtraction && (
              <div style={{ background: "#f59e0b0d", border: "1px solid #f59e0b44", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: fs(10), background: "#f59e0b22", color: "#f59e0b", borderRadius: 4, padding: "2px 7px", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>ZIP</span>
                <div>
                  <div style={{ fontSize: fs(12), color: B.white, fontWeight: 700 }}>Extracted from {zipExtraction.zipName}</div>
                  <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 3 }}>
                    {zipExtraction.wasEncrypted ? `Decrypted with password "${zipExtraction.usedPassword}" · ` : ""}
                    {zipExtraction.extractedCount} file{zipExtraction.extractedCount !== 1 ? "s" : ""} inside · analyzing largest payload
                  </div>
                  {zipExtraction.extractedCount > 1 && (
                    <div style={{ marginTop: 6, display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {zipExtraction.extractedFiles.map((f, i) => (
                        <span key={i} style={{ fontSize: fs(10), background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 4, padding: "2px 7px", color: B.textDim }}>
                          {f.name} ({f.size >= 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div style={{ background: verdictColor + "14", border: `1px solid ${verdictColor}55`, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: verdictColor, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: fs(14), fontWeight: 800, color: B.white, wordBreak: "break-all" }}>{analysis.file.name}</div>
                <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 3 }}>{analysis.fileType.label} · {analysis.file.sizeLabel} · {analysis.mode}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: fs(14), fontWeight: 900, color: verdictColor, textTransform: "uppercase" }}>{analysis.verdict}</div>
                <div style={{ fontSize: fs(10), color: B.textDim }}>Score {analysis.score}/100</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}><div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Sample Hashes</div>{Object.entries(analysis.file.hashes || {}).map(([k, v]) => <div key={k} style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 10, padding: "5px 0", borderBottom: `1px solid ${B.border}22` }}><div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase" }}>{k}</div><div style={{ fontSize: fs(11), color: B.text, fontFamily: "monospace", wordBreak: "break-all" }}>{v}</div></div>)}</div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}><div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}><div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700 }}>Extracted IOCs</div><span style={{ marginLeft: "auto", fontSize: fs(10), color: totalIocs ? "#ef4444" : "#22c55e", fontWeight: 800 }}>{totalIocs}</span></div><div style={{ display: "grid", gap: 10 }}>{iocSections.map(([label, values, color]) => <div key={label}><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}><div style={{ fontSize: fs(10), color, fontWeight: 800 }}>{label}</div><div style={{ fontSize: fs(10), color: B.textDim }}>{values.length}</div></div>{values.length > 0 ? <div style={{ display: "grid", gap: 4 }}>{values.slice(0, 10).map((value, i) => <div key={`${label}-${i}`} style={{ fontSize: fs(10), color: B.textMuted, fontFamily: "monospace", wordBreak: "break-all", background: B.bg, borderRadius: 5, padding: "4px 7px" }}>{value}</div>)}</div> : <div style={{ fontSize: fs(10), color: B.textDim }}>None extracted</div>}</div>)}</div></div>
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Signals</div>
                  {analysis.entropy != null && <div style={{ fontSize: fs(10), color: analysis.entropy > 7.2 ? "#ef4444" : analysis.entropy > 6.5 ? "#f59e0b" : B.textDim, marginBottom: 8, fontFamily: "monospace" }}>Entropy: {analysis.entropy} / 8.0</div>}
                  {analysis.capabilities?.length > 0 ? <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{analysis.capabilities.map(signal => <span key={signal} style={{ fontSize: fs(10), color: "#f59e0b", background: "#f59e0b22", borderRadius: 4, padding: "3px 7px", fontWeight: 700 }}>{signal}</span>)}</div> : <div style={{ fontSize: fs(11), color: B.textDim }}>No high-confidence behavior strings detected.</div>}
                  {analysis.deepSignals?.length > 0 && <div style={{ marginTop: 12, display: "grid", gap: 6 }}>{analysis.deepSignals.map((s, i) => <div key={i} style={{ fontSize: fs(10), color: B.textDim, lineHeight: 1.4 }}><span style={{ color: "#ef4444", fontWeight: 700 }}>{s.label}</span> — {s.reason}</div>)}</div>}
                </div>
                <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}><div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, marginBottom: 10 }}>Notable Strings</div><div style={{ maxHeight: 280, overflow: "auto", display: "grid", gap: 8 }}>{notable.slice(0, 18).map((item, i) => <div key={i} style={{ borderBottom: `1px solid ${B.border}22`, paddingBottom: 8 }}><div style={{ fontSize: fs(10), color: B.textMuted, fontFamily: "monospace", wordBreak: "break-all", marginBottom: 5 }}>{item.value}</div><div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>{(item.tags || []).map(tag => <span key={tag} style={{ fontSize: fs(9), background: "#f59e0b22", color: "#f59e0b", borderRadius: 3, padding: "1px 5px" }}>{tag}</span>)}</div>{(item.reasons || []).map((reason, j) => <div key={j} style={{ fontSize: fs(10), color: B.textDim, lineHeight: 1.4 }}>Why: {reason}</div>)}</div>)}{notable.length === 0 && <div style={{ fontSize: fs(11), color: B.textDim }}>No notable suspicious strings extracted.</div>}</div></div>
              </div>
            </div>
            <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700 }}>Automated Report</div>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  {isPremium ? <button onClick={() => onAddToBundle(buildStix(analysis))} style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "4px 10px" }}>Add to Bundle</button> : <PremiumGate><button disabled style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "4px 10px", opacity: 0.5, cursor: "not-allowed" }}>Add to Bundle</button></PremiumGate>}
                  <button onClick={() => navigator.clipboard?.writeText(analysis.report || "")} style={{ ...HP2.navBtn, fontSize: fs(10), padding: "4px 10px" }}>Copy</button>
                  <button onClick={downloadReport} style={{ ...HP2.navBtn, fontSize: fs(10), padding: "4px 10px" }}>Save .md</button>
                </div>
              </div>
              <pre style={{ margin: 0, padding: 14, maxHeight: 320, overflow: "auto", color: B.textMuted, fontSize: fs(11), lineHeight: 1.55, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{analysis.report}</pre>
            </div>
          </div>;
        })()}
      </div>
      </div>
    </div>
  );
}

function DarkWebPage({ onNavigate, onLogin, onLogout, user, onAddToBundle, pcapSession, collapsed, onToggleCollapse }) {
  const [activeTab, setActiveTab] = useState("enrich");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Per-tab data
  const [intelxResults, setIntelxResults] = useState(null);
  const [intelxStatus, setIntelxStatus] = useState(null);
  const [breachDomain, setBreachDomain] = useState("");
  const [breachResults, setBreachResults] = useState(null);
  const [breachStatus, setBreachStatus] = useState(null);
  const [otx, setOtx] = useState(null);
  const [otxStatus, setOtxStatus] = useState(null);
  const [enrichInput, setEnrichInput] = useState("");
  const [enrichResult, setEnrichResult] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [ahmia, setAhmia] = useState(null);
  const [ahmiaPage, setAhmiaPage] = useState(1);
  const [ahmiaMeta, setAhmiaMeta] = useState(null);
  const [paste, setPaste] = useState(null);
  const [telegramChannel, setTelegramChannel] = useState(null);
  const [telegramInput, setTelegramInput] = useState("");

  const isPremium = !!user;
  const showGlobalSearch = !["breach", "enrich", "telegram"].includes(activeTab);

  const doFetch = async (tab, query, page = 1) => {
    setLoading(true); setError(null);
    try {
      if (tab === "intelx") {
        setIntelxStatus(null);
        const r = await fetch("/api/darkweb/search", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ term: query }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        setIntelxStatus(d.configRequired ? d.message : null);
        setIntelxResults(d.results || []);
      } else if (tab === "otx") {
        const q = query ? `?query=${encodeURIComponent(query)}` : "";
        const r = await fetch(`/api/darkweb/otx${q}`, { credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        setOtxStatus(d.authRequired ? d.message : null);
        setOtx(d.pulses || []);
      } else if (tab === "ahmia") {
        if (!query) return;
        const r = await fetch(`/api/darkweb/ahmia?query=${encodeURIComponent(query)}&page=${page}`, { credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        setAhmia(d.results || []);
        setAhmiaMeta({ source: d.source, total: d.total, lastPage: d.lastPage, page });
      } else if (tab === "paste") {
        if (!query) return;
        const r = await fetch(`/api/darkweb/paste?query=${encodeURIComponent(query)}`, { credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error);
        setPaste(d.pastes || []);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const fetchTelegram = async (channel) => {
    if (!channel?.trim()) return;
    setLoading(true); setError(null); setTelegramChannel(null);
    try {
      const r = await fetch(`/api/darkweb/telegram?channel=${encodeURIComponent(channel.trim())}`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTelegramChannel(d);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };



  const doEnrich = async () => {
    if (!enrichInput.trim()) return;
    setEnrichLoading(true); setError(null); setEnrichResult(null);
    try {
      const r = await fetch("/api/darkweb/enrich", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ioc: enrichInput.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setEnrichResult(d);
    } catch (e) { setError(e.message); }
    setEnrichLoading(false);
  };

  useEffect(() => {
    const manualTabs = ["intelx", "breach", "enrich", "ahmia", "paste", "telegram"];
    if (!manualTabs.includes(activeTab)) doFetch(activeTab, "");
  }, [activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    setSearchTerm(searchInput.trim());
    if (activeTab === "intelx") doFetch("intelx", searchInput.trim());
    else doFetch(activeTab, searchInput.trim());
  };

  const handleBreachCheck = async (e) => {
    e.preventDefault();
    if (!breachDomain.trim()) return;
    setLoading(true); setError(null); setBreachResults(null); setBreachStatus(null);
    try {
      const r = await fetch(`/api/darkweb/breach?domain=${encodeURIComponent(breachDomain.trim())}`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setBreachStatus(d.configRequired ? d.message : null);
      setBreachResults(d.configRequired ? null : (d.breaches || []));
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const newUUID = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

  const stixNow = () => new Date().toISOString();
  const toStixTime = (value) => {
    if (!value) return stixNow();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? stixNow() : d.toISOString();
  };
  const escapeStix = (value) => String(value || "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  const uniq = (items) => [...new Set((items || []).map(v => String(v || "").trim()).filter(Boolean))];
  const makeRel = (relationshipType, sourceRef, targetRef, description, created) => ({
    type: "relationship",
    id: `relationship--${newUUID()}`,
    spec_version: "2.1",
    relationship_type: relationshipType,
    source_ref: sourceRef,
    target_ref: targetRef,
    ...(description ? { description } : {}),
    created: created || stixNow(),
    modified: stixNow(),
  });
  const genericFamilyTags = new Set(["apk", "dll", "doc", "docx", "downloader", "elf", "exe", "file", "hta", "jar", "js", "lnk", "macro", "msi", "pdf", "pe", "ps1", "rat", "script", "trojan", "unknown", "vba", "xls", "xlsx", "zip"]);
  const meaningfulValue = (value) => {
    const s = String(value || "").trim();
    return s && !["n/a", "na", "none", "null", "unknown"].includes(s.toLowerCase()) ? s : "";
  };
  const familyFromTags = (tags = []) => (tags || []).find(t => {
    const norm = String(t || "").toLowerCase().trim();
    return norm.length > 2 && !genericFamilyTags.has(norm) && !/^\d+$/.test(norm);
  });
  const malwareFamilyName = (sample = {}) => (
    meaningfulValue(sample.signature) ||
    meaningfulValue(sample.malware_family) ||
    meaningfulValue(sample.family) ||
    meaningfulValue(sample.malware) ||
    familyFromTags(sample.tags) ||
    "Unknown Malware"
  );


  const buildStixFromMalwareSample = (sample, familyIdOverride = null, includeFamilyObject = true) => {
    const sha256 = sample.sha256_hash || sample.sha256 || sample.hash || "";
    const family = malwareFamilyName(sample);
    const created = toStixTime(sample.first_seen || sample.date_added);
    const sampleName = `${family} - ${sha256 || sample.file_name || "sample"}`;
    const labels = uniq(["malware-bazaar", family, ...(sample.tags || [])]);
    const malwareId = familyIdOverride || `malware--${newUUID()}`;
    const indicatorId = `indicator--${newUUID()}`;
    const fileId = `file--${newUUID()}`;
    const externalReferences = sha256
      ? [{ source_name: "MalwareBazaar", url: `https://bazaar.abuse.ch/sample/${sha256}/` }]
      : [{ source_name: "MalwareBazaar" }];
    const hashes = {
      ...(sha256 ? { "SHA-256": sha256 } : {}),
      ...(sample.sha1_hash ? { "SHA-1": sample.sha1_hash } : {}),
      ...(sample.md5_hash ? { MD5: sample.md5_hash } : {}),
    };

    const malwareObj = {
      type: "malware",
      id: malwareId,
      spec_version: "2.1",
      name: family,
      description: `Malware family associated with MalwareBazaar sample ${sha256 || sample.file_name || "unknown sample"}.`,
      malware_types: ["unknown"],
      is_family: true,
      labels: uniq(["malware-bazaar", family]),
      external_references: externalReferences,
      created,
      modified: stixNow(),
    };

    const indicator = {
      type: "indicator",
      id: indicatorId,
      spec_version: "2.1",
      name: sampleName,
      description: [
        `MalwareBazaar file hash indicator for ${family}.`,
        sample.file_type ? `File type: ${sample.file_type}.` : null,
        sample.file_name ? `Filename: ${sample.file_name}.` : null,
        sample.reporter ? `Reporter: ${sample.reporter}.` : null,
      ].filter(Boolean).join(" "),
      pattern: `[file:hashes.'SHA-256' = '${escapeStix(sha256)}']`,
      pattern_type: "stix",
      indicator_types: ["malicious-activity"],
      valid_from: created,
      labels: uniq(["malicious-activity", ...labels]),
      external_references: externalReferences,
      created,
      modified: stixNow(),
    };

    const fileObj = {
      type: "file",
      id: fileId,
      spec_version: "2.1",
      name: sampleName,
      ...(Object.keys(hashes).length ? { hashes } : {}),
      ...(sample.file_name ? { x_file_name: sample.file_name } : {}),
      ...(sample.mime_type ? { mime_type: sample.mime_type } : {}),
      labels,
      external_references: externalReferences,
      created,
      modified: stixNow(),
    };

    const sampleObjects = [
      indicator,
      fileObj,
      makeRel("indicates", indicatorId, malwareId, `${sampleName} indicates the ${family} malware family.`, created),
      makeRel("has-sample", malwareId, fileId, `${family} has MalwareBazaar sample ${sha256 || sample.file_name || "unknown sample"}.`, created),
      makeRel("related-to", indicatorId, fileId, "Hash indicator describes this MalwareBazaar file object.", created),
    ];

    return includeFamilyObject ? [malwareObj, ...sampleObjects] : sampleObjects;
  };



  const buildStixFromEnrichment = (result) => {
    const { ioc, type, sources } = result;
    const objs = [];
    const gn = sources.greynoise || {};
    const sho = sources.shodan || {};
    const tf = Array.isArray(sources.threatfox) ? sources.threatfox : [];
    const uh = sources.urlhaus || {};
    const mb = Array.isArray(sources.malwarebazaar) ? sources.malwarebazaar : [];

    if (type === "hash" && mb.length > 0) {
      return buildStixFromMalwareSample({ ...mb[0], sha256_hash: mb[0].sha256_hash || ioc });
    }

    let pattern = "";
    let description = "";

    if (type === "ip") {
      pattern = `[ipv4-addr:value = '${ioc}']`;
      description = [
        gn.classification ? `GreyNoise: ${gn.classification}` : null,
        gn.noise ? `Scanning internet: yes` : null,
        gn.name && gn.name !== "unknown" ? `Name: ${gn.name}` : null,
        sho.ports?.length ? `Open ports: ${sho.ports.join(", ")}` : null,
        sho.vulns?.length ? `CVEs: ${sho.vulns.join(", ")}` : null,
        sho.hostnames?.length ? `Hostnames: ${sho.hostnames.join(", ")}` : null,
        tf.length ? `ThreatFox: ${tf.length} match(es)` : null,
        uh.urls?.length ? `URLhaus: ${uh.urls.length} malicious URL(s)` : null,
      ].filter(Boolean).join(". ");
    } else if (type === "domain") {
      pattern = `[domain-name:value = '${ioc}']`;
      description = [
        tf.length ? `ThreatFox: ${tf.length} match(es)` : null,
        uh.urls?.length ? `URLhaus: ${uh.urls.length} malicious URL(s)` : null,
      ].filter(Boolean).join(". ") || "Domain indicator.";
    } else if (type === "url") {
      pattern = `[url:value = '${ioc.replace(/'/g, "\\'")}']`;
      description = [
        tf.length ? `ThreatFox: ${tf.length} match(es)` : null,
        uh.query_status ? `URLhaus status: ${uh.query_status}` : null,
      ].filter(Boolean).join(". ") || "URL indicator.";
    } else if (type === "hash") {
      pattern = `[file:hashes.'SHA-256' = '${ioc}']`;
      const sample = mb[0] || {};
      description = [
        sample.signature ? `Malware: ${sample.signature}` : null,
        sample.file_type ? `Type: ${sample.file_type}` : null,
        sample.first_seen ? `First seen: ${sample.first_seen}` : null,
        tf.length ? `ThreatFox: ${tf.length} match(es)` : null,
      ].filter(Boolean).join(". ") || "File hash indicator.";
    }

    const isEvil = gn.classification === "malicious" || tf.length > 0 || uh.urls?.length > 0 || mb.length > 0 || sho.vulns?.length > 0;

    objs.push({
      type: "indicator",
      id: `indicator--${newUUID()}`,
      spec_version: "2.1",
      name: `${type.toUpperCase()}: ${ioc.length > 60 ? ioc.slice(0, 60) + "…" : ioc}`,
      description: description || "IOC enriched via Idle Hour.",
      pattern,
      pattern_type: "stix",
      valid_from: new Date().toISOString(),
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      labels: isEvil ? ["malicious-activity"] : ["benign"],
    });
    return objs;
  };

  const buildStixFromOtxPulse = (pulse) => {
    const objs = [];
    const indicatorObjs = [];

    (pulse.indicators || []).slice(0, 10).forEach(ind => {
      let pattern = null;
      if (ind.type === "IPv4") pattern = `[ipv4-addr:value = '${ind.indicator}']`;
      else if (ind.type === "domain") pattern = `[domain-name:value = '${ind.indicator}']`;
      else if (ind.type === "URL") pattern = `[url:value = '${ind.indicator.replace(/'/g, "\\'")}']`;
      else if (ind.type === "FileHash-SHA256") pattern = `[file:hashes.'SHA256' = '${ind.indicator}']`;
      else if (ind.type === "FileHash-MD5") pattern = `[file:hashes.'MD5' = '${ind.indicator}']`;
      if (!pattern) return;
      const obj = {
        type: "indicator",
        id: `indicator--${newUUID()}`,
        spec_version: "2.1",
        name: `${ind.type}: ${ind.indicator}`,
        description: ind.description || `OTX indicator from pulse: ${pulse.name}`,
        pattern,
        pattern_type: "stix",
        valid_from: ind.created || new Date().toISOString(),
        created: ind.created || new Date().toISOString(),
        modified: new Date().toISOString(),
        labels: ["malicious-activity"],
      };
      indicatorObjs.push(obj);
      objs.push(obj);
    });

    const report = {
      type: "report",
      id: `report--${newUUID()}`,
      spec_version: "2.1",
      name: pulse.name,
      description: pulse.description || `OTX Pulse. Tags: ${(pulse.tags || []).join(", ")}.`,
      published: pulse.created || new Date().toISOString(),
      created: pulse.created || new Date().toISOString(),
      modified: pulse.modified || new Date().toISOString(),
      report_types: ["threat-report"],
      object_refs: indicatorObjs.length ? indicatorObjs.map(o => o.id) : [`identity--${newUUID()}`],
    };
    objs.unshift(report);
    return objs;
  };





  // Lookups that query outside services on demand. The standing feeds (ThreatFox,
  // URLhaus, MalwareBazaar, PhishStats, CISA KEV, ransomware leak sites) live in the
  // Knowledge Base sections instead: Indicators, Arsenal and Threats.
  const tabs = [
    { k: "enrich",     l: "IOC Lookup",        icon: "⬡" },
    { k: "otx",        l: "OTX Pulse Search",  icon: "◎" },
    { k: "ahmia",      l: "Dark Web Search",   icon: "⊘" },
    { k: "paste",      l: "Pastebin OSINT",    icon: "⊡" },
    { k: "telegram",   l: "Telegram Monitor",  icon: "✈" },
    { k: "intelx",     l: "IntelX Search",     icon: "⌕" },
    { k: "breach",     l: "Breach Check",      icon: "⬡" },
  ];


  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="darkweb" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column" }}>
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>
      {/* Header */}
      <div style={{ padding: "14px 28px", borderBottom: `1px solid ${B.border}`, background: B.bgPanel, display: "flex", alignItems: "center", gap: 14 }}>
        <div>
          <div style={{ fontSize: fs(16), fontWeight: 900, color: B.white }}>OSINT Tools</div>
          <div style={{ fontSize: fs(10), color: B.textDim, letterSpacing: 2, textTransform: "uppercase" }}>Lookups · Dark web · Breach data</div>
        </div>
        <div style={{ marginLeft: "auto" }} />
      </div>

      {/* Global search bar */}
      {showGlobalSearch && <div style={{ padding: "12px 28px", borderBottom: `1px solid ${B.border}`, background: B.bgCard }}>
        <form onSubmit={handleSearch} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 8, padding: "0 14px" }}>
            <span style={{ color: B.textDim, fontSize: fs(14) }}>⌕</span>
            <input
              type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder={activeTab === "intelx" ? "Search IntelX (email, domain, IP, hash…)" : activeTab === "ahmia" ? "Search dark web (keyword, onion domain, actor name…)" : activeTab === "paste" ? "Search pastes (malware name, domain, IP, hash…)" : "Filter current feed…"}
              style={{ flex: 1, background: "transparent", border: "none", color: B.text, fontSize: fs(13), padding: "10px 0", outline: "none", fontFamily: "'Satoshi', sans-serif" }}
            />
          </div>
          <button type="submit" style={HP2.accentBtn} disabled={loading}>{loading ? "Loading…" : "Search"}</button>
          {searchTerm && <button type="button" onClick={() => { setSearchInput(""); setSearchTerm(""); doFetch(activeTab, ""); }} style={HP2.navBtn}>Clear</button>}
        </form>
      </div>}

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar tabs */}
        <div style={{ width: 192, background: B.bgPanel, borderRight: `1px solid ${B.border}`, padding: "12px 0", flexShrink: 0, overflowY: "auto" }}>
          {tabs.map(t => (
            <button key={t.k} onClick={() => { setActiveTab(t.k); setSearchInput(""); setSearchTerm(""); setError(null); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "9px 16px", background: activeTab === t.k ? B.primary + "18" : "transparent", border: "none", borderLeft: `3px solid ${activeTab === t.k ? B.primary : "transparent"}`, color: activeTab === t.k ? B.primary : B.textMuted, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: activeTab === t.k ? 700 : 500, cursor: "pointer" }}>
              <span style={{ fontSize: fs(11), opacity: 0.7 }}>{t.icon}</span>
              {t.l}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {error && <div style={{ background: "#ef444422", border: "1px solid #ef444444", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: fs(12), marginBottom: 16 }}>{error}</div>}

          {/* ─── IOC Lookup / Enrichment ─── */}
          {activeTab === "enrich" && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: fs(13), fontWeight: 700, color: B.white, marginBottom: 4 }}>IOC Enrichment Lookup</div>
                <div style={{ fontSize: fs(11), color: B.textDim }}>Paste any IP address, domain, URL, or file hash — queries GreyNoise, Shodan InternetDB, ThreatFox, URLhaus, and MalwareBazaar simultaneously.</div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
                <input
                  value={enrichInput} onChange={e => setEnrichInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && doEnrich()}
                  placeholder="1.2.3.4 · evil.com · https://... · abc123hash..."
                  style={{ flex: 1, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, color: B.text, padding: "10px 14px", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", outline: "none" }}
                />
                <button onClick={doEnrich} disabled={enrichLoading} style={HP2.accentBtn}>{enrichLoading ? "Enriching…" : "Enrich"}</button>
                {enrichResult && <button onClick={() => { setEnrichResult(null); setEnrichInput(""); }} style={HP2.navBtn}>Clear</button>}
              </div>

              {enrichLoading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Querying threat intel sources…</div>}

              {enrichResult && (() => {
                const { ioc, type, sources } = enrichResult;
                const gn = sources.greynoise || {};
                const sho = sources.shodan || {};
                const tf = Array.isArray(sources.threatfox) ? sources.threatfox : [];
                const uh = sources.urlhaus || {};
                const mb = Array.isArray(sources.malwarebazaar) ? sources.malwarebazaar : [];

                const isEvil = gn.classification === "malicious" || tf.length > 0 || uh.urls?.length > 0 || mb.length > 0;
                const verdictColor = isEvil ? "#ef4444" : gn.classification === "benign" ? "#22c55e" : "#f59e0b";
                const verdict = isEvil ? "MALICIOUS" : gn.classification === "benign" ? "BENIGN" : "UNKNOWN";

                return (
                  <div>
                    {/* Verdict banner */}
                    <div style={{ background: verdictColor + "15", border: `1px solid ${verdictColor}44`, borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: verdictColor, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: fs(13), fontFamily: "monospace", color: B.white, wordBreak: "break-all" }}>{ioc}</div>
                        <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 2 }}>Type: {type.toUpperCase()}</div>
                      </div>
                      <span style={{ fontSize: fs(13), fontWeight: 900, color: verdictColor, letterSpacing: 1 }}>{verdict}</span>
                      {isPremium
                        ? <button onClick={() => onAddToBundle(buildStixFromEnrichment(enrichResult))} style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "5px 12px" }}>✦ STIX</button>
                        : <PremiumGate><button disabled style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "5px 12px", opacity: 0.5, cursor: "not-allowed" }}>✦ STIX</button></PremiumGate>}
                    </div>

                    {/* Source cards grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>

                      {/* GreyNoise */}
                      {sources.greynoise && (
                        <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                          <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>GreyNoise</div>
                          {gn.error ? <div style={{ color: "#ef4444", fontSize: fs(11) }}>{gn.error}</div> : (
                            <>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                                {gn.classification && <span style={{ fontSize: fs(10), padding: "2px 8px", borderRadius: 10, background: (gn.classification === "malicious" ? "#ef4444" : gn.classification === "benign" ? "#22c55e" : "#f59e0b") + "22", color: gn.classification === "malicious" ? "#ef4444" : gn.classification === "benign" ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>{gn.classification}</span>}
                                {gn.noise && <span style={{ fontSize: fs(10), padding: "2px 8px", borderRadius: 10, background: "#f59e0b22", color: "#f59e0b" }}>scanner</span>}
                                {gn.riot && <span style={{ fontSize: fs(10), padding: "2px 8px", borderRadius: 10, background: "#6366f122", color: "#6366f1" }}>RIOT</span>}
                              </div>
                              {gn.name && gn.name !== "unknown" && <div style={{ fontSize: fs(11), color: B.textMuted }}>{gn.name}</div>}
                              {gn.last_seen && <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 4 }}>Last seen: {gn.last_seen}</div>}
                              {gn.message === "This IP is commonly found in our RIOT dataset" && <div style={{ fontSize: fs(10), color: "#22c55e", marginTop: 4 }}>Known good infrastructure</div>}
                            </>
                          )}
                        </div>
                      )}

                      {/* Shodan InternetDB */}
                      {sources.shodan && (
                        <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                          <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>Shodan InternetDB</div>
                          {sho.error ? <div style={{ color: "#ef4444", fontSize: fs(11) }}>{sho.error}</div> : sho.detail ? <div style={{ fontSize: fs(11), color: B.textMuted }}>{sho.detail}</div> : (
                            <>
                              {sho.ports?.length > 0 && <div style={{ marginBottom: 6 }}>
                                <div style={{ fontSize: fs(9), color: B.textDim, marginBottom: 4 }}>OPEN PORTS</div>
                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{sho.ports.slice(0, 12).map(p => <span key={p} style={{ fontSize: fs(9), background: "#6366f122", color: "#6366f1", borderRadius: 3, padding: "1px 5px" }}>{p}</span>)}</div>
                              </div>}
                              {sho.vulns?.length > 0 && <div style={{ marginBottom: 6 }}>
                                <div style={{ fontSize: fs(9), color: "#ef4444", marginBottom: 4 }}>CVEs</div>
                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{sho.vulns.slice(0, 6).map(v => <span key={v} style={{ fontSize: fs(9), background: "#ef444422", color: "#ef4444", borderRadius: 3, padding: "1px 5px" }}>{v}</span>)}</div>
                              </div>}
                              {sho.hostnames?.length > 0 && <div style={{ fontSize: fs(10), color: B.textMuted }}>{sho.hostnames.slice(0, 3).join(", ")}</div>}
                              {sho.tags?.length > 0 && <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>{sho.tags.map(t => <span key={t} style={{ fontSize: fs(9), background: B.accent + "22", color: B.accent, borderRadius: 3, padding: "1px 5px" }}>{t}</span>)}</div>}
                            </>
                          )}
                        </div>
                      )}

                      {/* ThreatFox */}
                      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                        <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>ThreatFox</div>
                        {tf.error ? <div style={{ color: "#ef4444", fontSize: fs(11) }}>{tf.error}</div> : tf.length === 0 ? <div style={{ fontSize: fs(11), color: "#22c55e" }}>No matches found</div> : (
                          <div style={{ display: "grid", gap: 6 }}>
                            {tf.slice(0, 4).map((t, i) => (
                              <div key={i} style={{ borderBottom: `1px solid ${B.border}33`, paddingBottom: 6 }}>
                                <div style={{ fontSize: fs(10), color: "#f59e0b", fontWeight: 700 }}>{t.malware || t.malware_printable || "Unknown"}</div>
                                <div style={{ fontSize: fs(10), color: B.textDim }}>{t.ioc_type} · confidence {t.confidence_level}%</div>
                                {t.tags?.length > 0 && <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 3 }}>{t.tags.map(tg => <span key={tg} style={{ fontSize: fs(8), background: B.accent + "22", color: B.accent, borderRadius: 3, padding: "1px 4px" }}>{tg}</span>)}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* URLhaus */}
                      {sources.urlhaus && (
                        <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                          <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>URLhaus</div>
                          {uh.error ? <div style={{ color: "#ef4444", fontSize: fs(11) }}>{uh.error}</div> : (
                            <>
                              <div style={{ fontSize: fs(11), color: uh.urls?.length > 0 ? "#ef4444" : "#22c55e", fontWeight: 700, marginBottom: 6 }}>
                                {uh.urls?.length > 0 ? `${uh.urls.length} malicious URL(s)` : "No matches"}
                              </div>
                              {(uh.urls || []).slice(0, 3).map((u, i) => (
                                <div key={i} style={{ fontSize: fs(9), color: B.textDim, fontFamily: "monospace", marginBottom: 3, wordBreak: "break-all" }}>{u.url}</div>
                              ))}
                              {uh.note && <div style={{ fontSize: fs(9), color: B.textDim, marginTop: 8, lineHeight: 1.4 }}>{uh.note}</div>}
                            </>
                          )}
                        </div>
                      )}

                      {/* MalwareBazaar */}
                      {sources.malwarebazaar && (
                        <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                          <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>MalwareBazaar</div>
                          {mb.length === 0 ? <div style={{ fontSize: fs(11), color: "#22c55e" }}>Hash not found</div> : (
                            mb.slice(0, 2).map((s, i) => (
                              <div key={i} style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: fs(11), color: "#f59e0b", fontWeight: 700 }}>{s.signature || "Unknown"}</div>
                                <div style={{ fontSize: fs(10), color: B.textMuted }}>{s.file_type} · {s.file_size ? `${(s.file_size / 1024).toFixed(1)} KB` : ""}</div>
                                <div style={{ fontSize: fs(10), color: B.textDim }}>First seen: {s.first_seen}</div>
                                {s.tags?.length > 0 && <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 4 }}>{s.tags.map(t => <span key={t} style={{ fontSize: fs(8), background: B.accent + "22", color: B.accent, borderRadius: 3, padding: "1px 4px" }}>{t}</span>)}</div>}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {!enrichResult && !enrichLoading && !error && (
                <div style={{ textAlign: "center", padding: 60, color: B.textDim }}>
                  <div style={{ fontSize: fs(40), marginBottom: 14, opacity: 0.4 }}>⬡</div>
                  <div style={{ fontSize: fs(13) }}>Enter any IOC above to enrich it</div>
                  <div style={{ fontSize: fs(11), marginTop: 8, color: B.textDim }}>IP addresses get GreyNoise + Shodan results · Hashes get MalwareBazaar · All types checked against ThreatFox + URLhaus</div>
                </div>
              )}
            </div>
          )}

          {/* ─── OTX Pulses ─── */}
          {activeTab === "otx" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div>
                  <span style={{ fontSize: fs(13), fontWeight: 700, color: B.white }}>AlienVault OTX Pulses</span>
                  <span style={{ display: "block", fontSize: fs(10), color: B.textDim, marginTop: 1 }}>Community threat intelligence · Requires an OTX API key</span>
                </div>
              </div>
              {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading OTX pulses…</div>}
              {(otxStatus || (error && error.includes("OTX_API_KEY"))) && (
                <div style={{ background: "#f59e0b18", border: "1px solid #f59e0b44", borderRadius: 8, padding: "14px 18px", color: "#f59e0b", fontSize: fs(12) }}>
                  {otxStatus || "OTX_API_KEY not set. Get a free key at otx.alienvault.com and add it to your server .env.local file."}
                </div>
              )}
              {!loading && otx && (
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))" }}>
                  {otx.map((pulse, i) => (
                    <div key={pulse.id || i} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 16 }}>
                      <div style={{ fontSize: fs(13), fontWeight: 700, color: B.white, marginBottom: 6, lineHeight: 1.3 }}>{pulse.name}</div>
                      {pulse.description && <div style={{ fontSize: fs(11), color: B.textMuted, marginBottom: 8, lineHeight: 1.5 }}>{pulse.description.slice(0, 140)}{pulse.description.length > 140 ? "…" : ""}</div>}
                      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                        <div style={{ fontSize: fs(10), color: B.textDim }}><span style={{ color: B.text, fontWeight: 700 }}>{pulse.indicator_count || (pulse.indicators || []).length}</span> indicators</div>
                        {pulse.author_name && <div style={{ fontSize: fs(10), color: B.textDim }}>by <span style={{ color: B.textMuted }}>{pulse.author_name}</span></div>}
                        {pulse.created && <div style={{ fontSize: fs(10), color: B.textDim }}>{pulse.created.slice(0, 10)}</div>}
                      </div>
                      {pulse.tags?.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                          {pulse.tags.slice(0, 6).map(t => <span key={t} style={{ fontSize: fs(9), background: B.accent + "22", color: B.accent, borderRadius: 3, padding: "1px 5px" }}>{t}</span>)}
                        </div>
                      )}
                      {pulse.malware_families?.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                          {pulse.malware_families.slice(0, 4).map(m => <span key={m.id} style={{ fontSize: fs(9), background: "#ef444422", color: "#ef4444", borderRadius: 3, padding: "1px 5px" }}>{m.display_name}</span>)}
                        </div>
                      )}
                      {isPremium
                        ? <button onClick={() => onAddToBundle(buildStixFromOtxPulse(pulse))} style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "5px 12px" }}>✦ Add Pulse to Bundle</button>
                        : <PremiumGate><button disabled style={{ ...HP2.premiumBtn, fontSize: fs(10), padding: "5px 12px", opacity: 0.5, cursor: "not-allowed" }}>✦ Add Pulse to Bundle</button></PremiumGate>}
                    </div>
                  ))}
                  {otx.length === 0 && <div style={{ color: B.textDim, fontSize: fs(12) }}>No pulses found.</div>}
                </div>
              )}
            </div>
          )}

          {/* IntelX Search */}
          {activeTab === "intelx" && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: fs(13), fontWeight: 700, color: B.white, marginBottom: 4 }}>IntelX Dark Web Search</div>
                <div style={{ fontSize: fs(11), color: B.textDim }}>Search paste sites, dark web leaks, and data breaches. Enter an email, domain, IP, or hash above and press Search.</div>
              </div>
              {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Searching IntelX…</div>}
              {!loading && intelxStatus && (
                <div style={{ background: "#f59e0b18", border: "1px solid #f59e0b44", borderRadius: 8, padding: "14px 18px", color: "#f59e0b", fontSize: fs(12), marginBottom: 16 }}>
                  {intelxStatus}
                </div>
              )}
              {!loading && intelxResults && !intelxStatus && (
                <div>
                  <div style={{ fontSize: fs(11), color: B.textMuted, marginBottom: 12 }}>{intelxResults.length} results for "{searchTerm}"</div>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
                    {intelxResults.map((r, i) => (
                      <div key={i} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 14 }}>
                        <div style={{ fontSize: fs(12), fontWeight: 700, color: B.white, marginBottom: 4, wordBreak: "break-all" }}>{r.name || r.systemid || `Result ${i + 1}`}</div>
                        <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 6 }}>Type: {r.media || r.type || "unknown"} · Bucket: {r.bucket || "—"}</div>
                        {r.date && <div style={{ fontSize: fs(10), color: B.textDim }}>Date: {r.date}</div>}
                        {r.tags && r.tags.length > 0 && <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>{r.tags.map((t, j) => <span key={j} style={{ fontSize: fs(9), background: B.accent + "22", color: B.accent, borderRadius: 3, padding: "1px 5px" }}>{t}</span>)}</div>}
                      </div>
                    ))}
                    {intelxResults.length === 0 && <div style={{ color: B.textDim, fontSize: fs(12) }}>No results. Try a different search term.</div>}
                  </div>
                </div>
              )}
              {!loading && !intelxResults && !intelxStatus && !error && (
                <div style={{ textAlign: "center", padding: 60, color: B.textDim }}>
                  <div style={{ fontSize: fs(32), marginBottom: 12 }}>🔍</div>
                  <div style={{ fontSize: fs(13) }}>Enter a search term above to search IntelX</div>
                  <div style={{ fontSize: fs(11), marginTop: 6 }}>Requires INTELX_API_KEY in server .env.local</div>
                </div>
              )}
            </div>
          )}

          {/* Breach Check */}
          {activeTab === "breach" && (
            <div>
              <div style={{ fontSize: fs(13), fontWeight: 700, color: B.white, marginBottom: 4 }}>HIBP Domain Breach Check</div>
              <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 20 }}>Check if a domain's email addresses appear in known data breaches. Powered by Have I Been Pwned.</div>
              <form onSubmit={handleBreachCheck} style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <input
                  type="text" value={breachDomain} onChange={e => setBreachDomain(e.target.value)}
                  placeholder="example.com"
                  style={{ flex: 1, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "9px 14px", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", outline: "none" }}
                />
                <button type="submit" style={HP2.accentBtn} disabled={loading}>{loading ? "Checking…" : "Check Breaches"}</button>
              </form>
              {!loading && breachStatus && (
                <div style={{ background: "#f59e0b18", border: "1px solid #f59e0b44", borderRadius: 8, padding: "14px 18px", color: "#f59e0b", fontSize: fs(12), marginBottom: 16 }}>
                  {breachStatus}
                </div>
              )}
              {breachResults && (
                <div>
                  {breachResults.length === 0 ? (
                    <div style={{ color: "#22c55e", fontSize: fs(13), fontWeight: 600 }}>✓ No breaches found for {breachDomain}</div>
                  ) : (
                    <div>
                      <div style={{ color: "#ef4444", fontSize: fs(13), fontWeight: 700, marginBottom: 12 }}>⚠ {Object.keys(breachResults).length} email(s) found in breaches</div>
                      <div style={{ display: "grid", gap: 8 }}>
                        {Object.entries(breachResults).slice(0, 30).map(([email, breaches], i) => (
                          <div key={i} style={{ background: B.bgCard, border: `1px solid #ef444433`, borderRadius: 6, padding: "10px 14px" }}>
                            <div style={{ fontSize: fs(12), fontFamily: "monospace", color: B.text, marginBottom: 6 }}>{email}</div>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {(Array.isArray(breaches) ? breaches : [breaches]).map((b, j) => (
                                <span key={j} style={{ fontSize: fs(10), background: "#ef444422", color: "#ef4444", borderRadius: 4, padding: "2px 8px" }}>{b}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!loading && !breachResults && !breachStatus && !error && (
                <div style={{ textAlign: "center", padding: 60, color: B.textDim }}>
                  <div style={{ fontSize: fs(32), marginBottom: 12 }}>🛡</div>
                  <div style={{ fontSize: fs(13) }}>Enter a domain to check for breached email addresses</div>
                  <div style={{ fontSize: fs(11), marginTop: 6 }}>Requires HIBP_API_KEY in server .env.local</div>
                </div>
              )}
            </div>
          )}

          {/* ── Ahmia Dark Web Search ── */}
          {activeTab === "ahmia" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: fs(13), fontWeight: 700, color: B.white }}>Dark Web Search</span>
                <span style={{ fontSize: fs(10), color: B.textDim }}>indexes .onion hidden services</span>
                {ahmiaMeta && <span style={{ fontSize: fs(9), padding: "1px 6px", borderRadius: 3, background: B.bgCard, color: B.textDim, border: `1px solid ${B.border}` }}>via {ahmiaMeta.source === "darksearch" ? "DarkSearch.io" : "Ahmia"}</span>}
                {ahmiaMeta?.total && <span style={{ fontSize: fs(10), color: B.textDim, marginLeft: "auto" }}>{ahmiaMeta.total.toLocaleString()} total results</span>}
              </div>
              {!ahmia && !loading && !error && (
                <div style={{ textAlign: "center", padding: 60, color: B.textDim }}>
                  <div style={{ fontSize: fs(28), marginBottom: 12, opacity: 0.4 }}>⊘</div>
                  <div style={{ fontSize: fs(13) }}>Search Ahmia to find dark web content</div>
                  <div style={{ fontSize: fs(11), marginTop: 6 }}>Try: ransomware, credentials, exploit, malware C2, threat actor name</div>
                </div>
              )}
              {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Searching dark web index…</div>}
              {ahmia && (
                <div style={{ display: "grid", gap: 10 }}>
                  {ahmia.length === 0 && <div style={{ color: B.textDim, fontSize: fs(12), padding: "20px 0" }}>No results found. Try broader terms — threat actor names, malware families, or specific CVEs tend to return results.</div>}
                  {ahmia.map((r, i) => (
                    <div key={i} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: fs(13), fontWeight: 700, color: B.white, marginBottom: 4 }}>{r.title || "Untitled"}</div>
                          <div style={{ fontSize: fs(10), fontFamily: "monospace", color: B.accent, wordBreak: "break-all", marginBottom: r.description ? 6 : 0 }}>{r.url}</div>
                          {r.description && <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.5 }}>{r.description}</div>}
                        </div>
                        {isPremium && r.onion && (
                          <button onClick={() => onAddToBundle([{
                            type: "domain-name", id: `domain-name--${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
                            spec_version: "2.1", value: r.onion,
                            labels: ["dark-web", "onion"],
                            x_description: r.description || r.title,
                            created: new Date().toISOString(), modified: new Date().toISOString(),
                          }])} style={{ ...HP2.premiumBtn, fontSize: fs(9), padding: "3px 8px", flexShrink: 0 }}>✦ STIX</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {ahmiaMeta?.lastPage > 1 && (
                    <div style={{ display: "flex", gap: 8, justifyContent: "center", paddingTop: 8 }}>
                      <button disabled={ahmiaMeta.page <= 1} onClick={() => doFetch("ahmia", searchTerm, ahmiaMeta.page - 1)} style={{ ...HP2.navBtn, opacity: ahmiaMeta.page <= 1 ? 0.4 : 1 }}>← Prev</button>
                      <span style={{ fontSize: fs(11), color: B.textDim, alignSelf: "center" }}>Page {ahmiaMeta.page} of {ahmiaMeta.lastPage}</span>
                      <button disabled={ahmiaMeta.page >= ahmiaMeta.lastPage} onClick={() => doFetch("ahmia", searchTerm, ahmiaMeta.page + 1)} style={{ ...HP2.navBtn, opacity: ahmiaMeta.page >= ahmiaMeta.lastPage ? 0.4 : 1 }}>Next →</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Pastebin OSINT ── */}
          {activeTab === "paste" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: fs(13), fontWeight: 700, color: B.white }}>Pastebin OSINT</span>
                <span style={{ fontSize: fs(10), color: B.textDim }}>via psbdmp.ws · IOC extraction included</span>
              </div>
              {!paste && !loading && !error && (
                <div style={{ textAlign: "center", padding: 60, color: B.textDim }}>
                  <div style={{ fontSize: fs(28), marginBottom: 12, opacity: 0.4 }}>⊡</div>
                  <div style={{ fontSize: fs(13) }}>Search public paste dumps for IOCs</div>
                  <div style={{ fontSize: fs(11), marginTop: 6 }}>Try: C2 domain, malware hash, threat actor, campaign name, leaked credentials</div>
                </div>
              )}
              {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Searching paste archives…</div>}
              {paste && (
                <div style={{ display: "grid", gap: 10 }}>
                  {paste.length === 0 && <div style={{ color: B.textDim, fontSize: fs(12) }}>No pastes found.</div>}
                  {paste.map((p, i) => {
                    const hasIocs = p.totalIocs > 0;
                    const iocParts = [
                      p.iocs?.urls?.length ? `${p.iocs.urls.length} URL` : null,
                      p.iocs?.domains?.length ? `${p.iocs.domains.length} domain` : null,
                      p.iocs?.ipv4?.length ? `${p.iocs.ipv4.length} IP` : null,
                      p.iocs?.hashes?.length ? `${p.iocs.hashes.length} hash` : null,
                    ].filter(Boolean);
                    const allIocs = [
                      ...(p.iocs?.urls || []), ...(p.iocs?.domains || []),
                      ...(p.iocs?.ipv4 || []), ...(p.iocs?.hashes || []),
                    ];
                    return (
                      <div key={i} style={{ background: B.bgCard, border: `1px solid ${hasIocs ? "#f59e0b44" : B.border}`, borderRadius: 8, padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                              <span style={{ fontSize: fs(12), fontWeight: 700, color: B.white }}>{p.title}</span>
                              {hasIocs && <span style={{ fontSize: fs(9), background: "#f59e0b22", color: "#f59e0b", borderRadius: 3, padding: "1px 5px", fontWeight: 700 }}>{p.totalIocs} IOC{p.totalIocs !== 1 ? "s" : ""}</span>}
                            </div>
                            {p.time && <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 6 }}>{new Date(p.time * 1000).toLocaleDateString()}</div>}
                            <div style={{ fontSize: fs(10), fontFamily: "monospace", color: B.textMuted, background: B.bg, borderRadius: 4, padding: "6px 8px", maxHeight: 80, overflow: "hidden", lineHeight: 1.5 }}>{p.preview}</div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                            <a href={p.url} target="_blank" rel="noreferrer" style={{ ...HP2.navBtn, fontSize: fs(9), padding: "3px 8px", textDecoration: "none", textAlign: "center" }}>View</a>
                            {isPremium && hasIocs && (
                              <button onClick={() => {
                                const now = new Date().toISOString();
                                const uid = () => crypto.randomUUID?.() || Math.random().toString(36).slice(2);
                                onAddToBundle(allIocs.slice(0, 20).map(v => {
                                  const isHash = /^[a-f0-9]{32,64}$/i.test(v);
                                  const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(v);
                                  const isUrl = /^https?:\/\//.test(v);
                                  const type = isHash ? "file" : isIp ? "ipv4-addr" : isUrl ? "url" : "domain-name";
                                  const pattern = isHash ? `[file:hashes.'SHA-256' = '${v}']` : isIp ? `[ipv4-addr:value = '${v}']` : isUrl ? `[url:value = '${v}']` : `[domain-name:value = '${v}']`;
                                  return { type: "indicator", id: `indicator--${uid()}`, spec_version: "2.1", name: `Paste IOC: ${v.slice(0, 60)}`, pattern, pattern_type: "stix", indicator_types: ["malicious-activity"], valid_from: now, labels: ["pastebin", "osint"], created: now, modified: now };
                                }));
                              }} style={{ ...HP2.premiumBtn, fontSize: fs(9), padding: "3px 8px" }}>✦ STIX</button>
                            )}
                          </div>
                        </div>
                        {hasIocs && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {iocParts.map(s => <span key={s} style={{ fontSize: fs(9), background: "#f59e0b22", color: "#f59e0b", borderRadius: 3, padding: "1px 6px" }}>{s}</span>)}
                            {allIocs.slice(0, 4).map((v, j) => <span key={j} style={{ fontSize: fs(9), fontFamily: "monospace", background: B.bg, color: B.textDim, borderRadius: 3, padding: "1px 6px" }}>{v.slice(0, 40)}</span>)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Telegram Channel Monitor ── */}
          {activeTab === "telegram" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: fs(13), fontWeight: 700, color: B.white }}>Telegram Channel Monitor</span>
                <span style={{ fontSize: fs(10), color: B.textDim }}>reads public channels · extracts IOCs</span>
              </div>
              <div style={{ background: "#f59e0b0a", border: "1px solid #f59e0b33", borderRadius: 8, padding: "10px 14px", fontSize: fs(11), color: "#f59e0b", marginBottom: 16, lineHeight: 1.5 }}>
                Only public Telegram channels are accessible. No account or API key needed.
                Try: <span style={{ fontFamily: "monospace" }}>vxunderground</span>, <span style={{ fontFamily: "monospace" }}>TheDFIRReport</span>, <span style={{ fontFamily: "monospace" }}>malware_traffic</span>
              </div>
              <form onSubmit={e => { e.preventDefault(); fetchTelegram(telegramInput); }} style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "0 14px", flex: 1 }}>
                  <span style={{ color: B.textDim, fontSize: fs(13) }}>@</span>
                  <input value={telegramInput} onChange={e => setTelegramInput(e.target.value)} placeholder="channel_username" style={{ flex: 1, background: "transparent", border: "none", color: B.text, fontSize: fs(13), padding: "10px 0", outline: "none", fontFamily: "'Satoshi', sans-serif" }} />
                </div>
                <button type="submit" disabled={!telegramInput.trim() || loading} style={{ ...HP2.accentBtn, opacity: !telegramInput.trim() || loading ? 0.6 : 1 }}>{loading ? "Loading…" : "Fetch"}</button>
              </form>
              {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Fetching channel…</div>}
              {telegramChannel && (
                <div>
                  <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ fontSize: fs(15), fontWeight: 800, color: B.white, marginBottom: 4 }}>@{telegramChannel.channel} · {telegramChannel.title}</div>
                    {telegramChannel.description && <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.5 }}>{telegramChannel.description}</div>}
                    <div style={{ marginTop: 8, fontSize: fs(11), color: B.textMuted }}>{telegramChannel.messages.length} recent messages · {telegramChannel.messages.filter(m => m.totalIocs > 0).length} with IOCs</div>
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {telegramChannel.messages.map((m, i) => {
                      const hasIocs = m.totalIocs > 0;
                      const allIocs = [...(m.iocs?.urls || []), ...(m.iocs?.domains || []), ...(m.iocs?.ipv4 || []), ...(m.iocs?.hashes || [])];
                      return (
                        <div key={i} style={{ background: B.bgCard, border: `1px solid ${hasIocs ? B.accent + "44" : B.border}`, borderRadius: 8, padding: "12px 14px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {m.date && <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 6 }}>{new Date(m.date).toLocaleString()}{m.views ? ` · ${m.views} views` : ""}</div>}
                              <div style={{ fontSize: fs(12), color: B.text, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.text}</div>
                              {hasIocs && (
                                <div style={{ marginTop: 8, display: "flex", gap: 5, flexWrap: "wrap" }}>
                                  {allIocs.slice(0, 6).map((v, j) => <span key={j} style={{ fontSize: fs(9), fontFamily: "monospace", background: B.accent + "18", color: B.accent, borderRadius: 3, padding: "1px 6px" }}>{v.slice(0, 50)}</span>)}
                                  {allIocs.length > 6 && <span style={{ fontSize: fs(9), color: B.textDim }}>+{allIocs.length - 6} more</span>}
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                              <a href={m.url} target="_blank" rel="noreferrer" style={{ ...HP2.navBtn, fontSize: fs(9), padding: "3px 8px", textDecoration: "none", textAlign: "center" }}>↗</a>
                              {isPremium && hasIocs && (
                                <button onClick={() => {
                                  const now = new Date().toISOString();
                                  const uid = () => crypto.randomUUID?.() || Math.random().toString(36).slice(2);
                                  onAddToBundle(allIocs.slice(0, 20).map(v => {
                                    const isHash = /^[a-f0-9]{32,64}$/i.test(v);
                                    const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(v);
                                    const isUrl = /^https?:\/\//.test(v);
                                    const pattern = isHash ? `[file:hashes.'SHA-256' = '${v}']` : isIp ? `[ipv4-addr:value = '${v}']` : isUrl ? `[url:value = '${v}']` : `[domain-name:value = '${v}']`;
                                    return { type: "indicator", id: `indicator--${uid()}`, spec_version: "2.1", name: `Telegram IOC: ${v.slice(0, 60)}`, pattern, pattern_type: "stix", indicator_types: ["malicious-activity"], valid_from: now, labels: ["telegram", "osint"], created: now, modified: now };
                                  }));
                                }} style={{ ...HP2.premiumBtn, fontSize: fs(9), padding: "3px 8px" }}>✦ STIX</button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      </div>
    </div>
  );
}

// ─── Object Notes Panel ───
function ObjectNotesPanel({ objectId, user }) {
  const [myNotes, setMyNotes] = useState([]);
  const [communityNotes, setCommunityNotes] = useState([]);
  const [input, setInput] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await fetch(`/api/notes/${encodeURIComponent(objectId)}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setMyNotes(data.myNotes || []);
      setCommunityNotes(data.communityNotes || []);
    } catch (e) {}
  }, [objectId]);

  useEffect(() => {
    if (!user || !expanded) return;
    fetchNotes();
  }, [objectId, expanded, user, fetchNotes]);

  const addNote = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/notes/${encodeURIComponent(objectId)}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input.trim(), isPublic }),
      });
      if (res.ok) { setInput(""); await fetchNotes(); }
    } catch (e) {}
    setLoading(false);
  };

  const togglePublic = async (note) => {
    try {
      await fetch(`/api/note/${note.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic: !note.is_public }),
      });
      await fetchNotes();
    } catch (e) {}
  };

  const deleteNote = async (noteId) => {
    try {
      await fetch(`/api/note/${noteId}`, { method: "DELETE", credentials: "include" });
      await fetchNotes();
    } catch (e) {}
  };

  const total = myNotes.length + communityNotes.length;

  return (
    <div style={{ marginTop: 20, borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
      <button onClick={() => setExpanded(e => !e)} style={{ display: "flex", alignItems: "center", width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, gap: 6 }}>
        <span style={{ fontSize: fs(9), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, fontFamily: "'Satoshi', sans-serif", flex: 1, textAlign: "left" }}>
          Community Notes {total > 0 && <span style={{ color: B.accent }}>({total})</span>}
        </span>
        <span style={{ fontSize: fs(10), color: B.textDim }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {!user ? (
            <div style={{ fontSize: fs(11), color: B.textMuted, padding: "6px 0" }}>Login to add notes.</div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), addNote())}
                placeholder="Add a note… (Enter to submit)"
                rows={2}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 7, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(12), resize: "none", outline: "none", boxSizing: "border-box" }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: fs(11), color: isPublic ? B.accent : B.textDim, userSelect: "none" }}>
                  <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} style={{ accentColor: B.accent }} />
                  Share with community
                </label>
                <button onClick={addNote} disabled={loading || !input.trim()} style={{ marginLeft: "auto", padding: "4px 14px", borderRadius: 6, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, cursor: "pointer", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", fontWeight: 700, opacity: !input.trim() ? 0.5 : 1 }}>Add</button>
              </div>
            </div>
          )}

          {myNotes.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: fs(9), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 7, fontFamily: "'Satoshi', sans-serif" }}>My Notes</div>
              {myNotes.map(n => (
                <div key={n.id} style={{ padding: "9px 11px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, marginBottom: 7 }}>
                  <div style={{ fontSize: fs(12), color: B.text, lineHeight: 1.55, marginBottom: 6 }}>{n.text}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: fs(9), color: B.textDim }}>{new Date(n.created_at).toLocaleDateString()}</span>
                    <button onClick={() => togglePublic(n)} style={{ fontSize: fs(9), padding: "1px 8px", borderRadius: 8, border: `1px solid ${n.is_public ? B.accent : B.border}`, background: n.is_public ? B.accent + "18" : "transparent", color: n.is_public ? B.accent : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 600 }}>
                      {n.is_public ? "Public" : "Private"}
                    </button>
                    <button onClick={() => deleteNote(n.id)} style={{ marginLeft: "auto", fontSize: fs(9), color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {communityNotes.length > 0 && (
            <div>
              <div style={{ fontSize: fs(9), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 7, fontFamily: "'Satoshi', sans-serif" }}>Community</div>
              {communityNotes.map((n, i) => (
                <div key={i} style={{ padding: "9px 11px", borderRadius: 8, background: B.bgPanel, border: `1px solid ${B.border}`, marginBottom: 7 }}>
                  <div style={{ fontSize: fs(12), color: B.text, lineHeight: 1.55, marginBottom: 4 }}>{n.text}</div>
                  <div style={{ fontSize: fs(9), color: B.textDim }}>Community Analyst · {new Date(n.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}

          {myNotes.length === 0 && communityNotes.length === 0 && (
            <div style={{ fontSize: fs(11), color: B.textDim, textAlign: "center", padding: "8px 0" }}>No notes yet — be the first.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Label Editor ───
function LabelEditor({ labels = [], onUpdate }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v || labels.includes(v)) return;
    onUpdate([...labels, v]);
    setInput("");
  };
  const remove = (l) => onUpdate(labels.filter(x => x !== l));
  return (
    <div style={{ margin: "8px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: labels.length ? 6 : 0 }}>
        {labels.map(l => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: fs(10), padding: "2px 6px 2px 8px", borderRadius: 10, background: B.accent + "18", color: B.accent, border: `1px solid ${B.accent}30` }}>
            {l}
            <button onClick={() => remove(l)} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", padding: 0, fontSize: fs(13), lineHeight: 1 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 5 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="Add label…"
          style={{ flex: 1, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "4px 8px", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", outline: "none" }} />
        <button onClick={add} disabled={!input.trim()}
          style={{ padding: "4px 10px", borderRadius: 6, background: input.trim() ? `linear-gradient(135deg, ${B.primary}, ${B.accent})` : B.bgCard, border: "none", color: B.white, cursor: input.trim() ? "pointer" : "default", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", fontWeight: 600 }}>+</button>
      </div>
    </div>
  );
}

// ─── Detail Panel (quick view) ───
function DetailPanel({ obj, objects, relationships, markingDefs = {}, onSelect, onClose, onViewFull, onPin, isPinned, onUpdateLabels, user, isGhostOrigin, onRevertGhost }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyRaw = () => {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  if (!obj) return null;
  const c = getConf(obj.type);
  const rels = relationships.filter(r => r.source_ref === obj.id || r.target_ref === obj.id);
  const skip = new Set(["type","id","name","description","created","modified","object_refs","content","labels","external_references"]);
  const extra = Object.entries(obj).filter(([k]) => !skip.has(k));
  const refs = (obj.object_refs || []).map(id => objects.find(o => o.id === id)).filter(Boolean);
  const markings = getMarkings(obj, markingDefs);
  return (
    <div style={ST.detailPanel}>
      <button onClick={onClose} style={ST.closeBtn} aria-label="Close" title="Close">✕</button>
      {/* Room on the right for the close button, so it never covers Pin to Investigation. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingRight: 30 }}>
        <TypeIcon type={obj.type} color={c.color} size={18} />
        <span style={{ fontSize: fs(12), padding: "3px 12px", borderRadius: 12, background: c.color + "18", color: c.color, fontWeight: 700 }}>{c.label}</span>
        {onPin && <button onClick={() => onPin(obj.id)} style={{ marginLeft: "auto", fontSize: fs(12), padding: "4px 12px", borderRadius: 10, border: `1px solid ${isPinned ? B.accent : B.border}`, background: isPinned ? B.accent + "18" : "transparent", color: isPinned ? B.accent : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 600 }}>{isPinned ? "Pinned ✓" : "Pin to Investigation"}</button>}
      </div>
      {isGhostOrigin && onRevertGhost && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)" }}>
          <span style={{ fontSize: fs(10), color: "rgba(255,255,255,0.45)", flex: 1, lineHeight: 1.4 }}>◌ Imported from ghost — click to revert and remove from bundle</span>
          <button onClick={() => onRevertGhost(obj.id)} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.7)", fontSize: fs(10), fontWeight: 700, fontFamily: "'Satoshi', sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}>Revert to Ghost</button>
        </div>
      )}
      <h2 style={ST.detailName}>{getDisplayName(obj)}{obj.name && <TranslateButton text={obj.name} />}</h2>
      {onUpdateLabels
        ? <LabelEditor labels={obj.labels || []} onUpdate={(newLabels) => onUpdateLabels(obj.id, newLabels)} />
        : obj.labels?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "8px 0" }}>
            {obj.labels.map(l => <span key={l} style={{ fontSize: fs(12), padding: "3px 10px", borderRadius: 10, background: B.accent + "18", color: B.accent, border: `1px solid ${B.accent}30` }}>{l}</span>)}
          </div>
        )
      }
      {markings.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "8px 0" }}>
          {markings.map(m => <span key={m.id || m.name} style={{ fontSize: fs(12), padding: "3px 10px", borderRadius: 10, background: m.color + "18", color: m.color, border: `1px solid ${m.color}30` }}>{m.name}</span>)}
        </div>
      )}
      {obj.description && <p style={ST.detailDesc}>{obj.description}<TranslateButton text={obj.description} /></p>}
      {obj.content && <div style={{ ...ST.codeBox, marginTop: 12 }}><div style={ST.miniLabel}>Content</div><div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>{obj.content}</div></div>}
      {obj.pattern && <div style={ST.codeBox}><div style={ST.miniLabel}>Pattern</div><code style={{ fontSize: fs(12), color: B.accent, wordBreak: "break-all" }}>{obj.pattern}</code></div>}
      {extra.length > 0 && <div style={{ marginTop: 16 }}><div style={ST.miniLabel}>Properties</div>{extra.map(([k, v]) => <div key={k} style={ST.propRow}><span style={ST.propKey}>{k.replace(/_/g, " ")}</span><span style={ST.propVal}>{Array.isArray(v) ? v.map(i => typeof i === "object" ? JSON.stringify(i) : i).join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>)}</div>}
      {refs.length > 0 && <div style={{ marginTop: 16 }}><div style={ST.miniLabel}>Referenced Objects ({refs.length})</div>{refs.map(ro => { const rc = getConf(ro.type); return <div key={ro.id} style={ST.relRow} onClick={() => onSelect(ro)}><TypeIcon type={ro.type} color={rc.color} size={12} /><span style={{ color: rc.color, fontSize: fs(12) }}>{ro.name}</span></div>; })}</div>}
      {rels.length > 0 && <div style={{ marginTop: 16 }}><div style={ST.miniLabel}>Relationships ({rels.length})</div>{rels.map(r => { const isSrc = r.source_ref === obj.id, oId = isSrc ? r.target_ref : r.source_ref, o = objects.find(x => x.id === oId), oc = o ? getConf(o.type) : getConf("?"); const rm = getMarkings(r, markingDefs); return <div key={r.id} style={ST.relRow} onClick={() => o && onSelect(o)}><span style={{ color: B.textDim, fontSize: fs(12), minWidth: 90, fontWeight: 600 }}>{isSrc ? r.relationship_type : `← ${r.relationship_type}`}</span><TypeIcon type={o?.type||"?"} color={oc.color} size={13} /><span style={{ color: oc.color, fontSize: fs(13), fontWeight: 600 }}>{o?.name || oId}</span>{rm[0] && <span style={{ marginLeft: "auto", fontSize: fs(10), padding: "2px 7px", borderRadius: 4, background: rm[0].color + "22", color: rm[0].color, fontWeight: 700 }}>{rm[0].name}</span>}</div>; })}</div>}
      <div style={{ marginTop: 20, fontSize: fs(12), color: B.textDim }}>{obj.created && <div>Created {new Date(obj.created).toLocaleDateString()}</div>}{obj.modified && <div>Modified {new Date(obj.modified).toLocaleDateString()}</div>}<div style={{ marginTop: 6, wordBreak: "break-all", fontFamily: "monospace", fontSize: fs(10) }}>{obj.id}</div></div>
      <ObjectNotesPanel objectId={obj.id} user={user} />

      {/* Raw STIX */}
      <div style={{ marginTop: 18, borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setRawOpen(o => !o)} style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", fontSize: fs(11), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, fontFamily: "'Satoshi', sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
            Raw STIX JSON <span>{rawOpen ? "▲" : "▼"}</span>
          </button>
          <button onClick={copyRaw} style={{ flexShrink: 0, background: copied ? "#10b98118" : "transparent", border: `1px solid ${copied ? "#10b981" : B.border}`, borderRadius: 5, color: copied ? "#10b981" : B.textDim, padding: "4px 12px", cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, transition: "all 0.2s" }}>
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </div>
        {rawOpen && (
          <pre style={{ marginTop: 10, padding: "12px 14px", borderRadius: 7, background: B.bg, border: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim, overflowX: "hidden", overflowY: "auto", fontFamily: "monospace", lineHeight: 1.6, maxHeight: 300, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {JSON.stringify(obj, null, 2)}
          </pre>
        )}
      </div>

      <button onClick={() => onViewFull(obj)} style={{ marginTop: 14, width: "100%", background: `linear-gradient(135deg, ${B.primary}22, ${B.accent}22)`, border: `1px solid ${B.border}`, borderRadius: 6, color: B.text, padding: "10px", cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 600 }}>View Full Details →</button>
    </div>
  );
}

// ─── Object Detail Page ───
function ObjectDetailPage({ obj, objects, relationships, markingDefs, onBack, onNavigate }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyRaw = () => {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const c = getConf(obj.type);
  const allRels = relationships.filter(r => r.source_ref === obj.id || r.target_ref === obj.id);
  const refs = (obj.object_refs || []).map(id => objects.find(o => o.id === id)).filter(Boolean);
  const referencedByReports = objects.filter(o => o.type === "report" && (o.object_refs || []).includes(obj.id));
  const extRefs = obj.external_references || [];
  const markings = getMarkings(obj, markingDefs);
  const skip = new Set(["type","id","name","description","created","modified","object_refs","content","labels","external_references","pattern","pattern_type"]);
  const extra = Object.entries(obj).filter(([k]) => !skip.has(k));

  const relsByType = {};
  for (const r of allRels) {
    const isSrc = r.source_ref === obj.id;
    const label = isSrc ? r.relationship_type : `← ${r.relationship_type}`;
    if (!relsByType[label]) relsByType[label] = [];
    const otherId = isSrc ? r.target_ref : r.source_ref;
    const other = objects.find(x => x.id === otherId);
    relsByType[label].push({ rel: r, other, otherId });
  }

  return (
    <div style={{ background: B.bg, minHeight: "100vh", fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column", overflowY: "auto" }}>
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>

      {/* Header */}
      <div style={{ padding: "14px 32px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 14, background: B.bgPanel, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: "none", border: `1px solid ${B.border}`, borderRadius: 6, color: B.textMuted, padding: "5px 12px", cursor: "pointer", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif" }}>← Back</button>
        <TypeIcon type={obj.type} color={c.color} size={18} />
        <span style={{ fontSize: fs(11), padding: "3px 10px", borderRadius: 10, background: c.color + "18", color: c.color, fontWeight: 600 }}>{c.label}</span>
        <h1 style={{ fontSize: fs(20), fontWeight: 900, margin: 0, color: B.text }}>{obj.name || obj.id}</h1>
      </div>

      <div style={{ padding: "32px 40px", width: "100%", boxSizing: "border-box" }}>
        {/* Labels */}
        {obj.labels?.length > 0 && (
          <div style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {obj.labels.map(l => <span key={l} style={{ fontSize: fs(11), padding: "3px 12px", borderRadius: 12, background: B.accent + "18", color: B.accent, border: `1px solid ${B.accent}30`, fontWeight: 500 }}>{l}</span>)}
          </div>
        )}
        {markings.length > 0 && (
          <div style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {markings.map(m => <span key={m.id || m.name} style={{ fontSize: fs(11), padding: "3px 12px", borderRadius: 12, background: m.color + "18", color: m.color, border: `1px solid ${m.color}30`, fontWeight: 500 }}>{m.name}</span>)}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {obj.description && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Description</div>
                <p style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.7, margin: 0 }}>{obj.description}</p>
              </div>
            )}
            {obj.content && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Content</div>
                <p style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.7, margin: 0 }}>{obj.content}</p>
              </div>
            )}
            {obj.pattern && (
              <div style={{ background: B.bg, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Pattern</div>
                <code style={{ fontSize: fs(12), color: B.accent, wordBreak: "break-all", lineHeight: 1.6 }}>{obj.pattern}</code>
              </div>
            )}
            {extra.length > 0 && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Properties</div>
                {extra.map(([k, v]) => (
                  <div key={k} style={{ padding: "6px 0", borderBottom: `1px solid ${B.border}22` }}>
                    <div style={{ fontSize: fs(9), color: B.textDim, textTransform: "capitalize", letterSpacing: 0.8, fontWeight: 600, marginBottom: 3 }}>{k.replace(/_/g, " ")}</div>
                    <div style={{ fontSize: fs(12), color: B.textMuted, wordBreak: "break-word" }}>{Array.isArray(v) ? v.map(i => typeof i === "object" ? JSON.stringify(i) : i).join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                  </div>
                ))}
              </div>
            )}
            {extRefs.length > 0 && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>External References</div>
                {extRefs.map((ref, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${B.border}22`, fontSize: fs(12) }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {ref.source_name === "mitre-attack" && <span style={{ fontSize: fs(10), padding: "2px 7px", borderRadius: 6, background: "#ef444420", color: "#ef4444", fontWeight: 600 }}>MITRE ATT&CK</span>}
                      {ref.source_name === "cve" && <span style={{ fontSize: fs(10), padding: "2px 7px", borderRadius: 6, background: "#f9731620", color: "#f97316", fontWeight: 600 }}>CVE</span>}
                      {ref.external_id && <code style={{ fontSize: fs(12), color: B.primary, fontWeight: 700 }}>{ref.external_id}</code>}
                      {!ref.external_id && <span style={{ color: B.textMuted }}>{ref.source_name}</span>}
                    </div>
                    {ref.description && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4 }}>{ref.description}</div>}
                    {ref.url && <a href={ref.url} target="_blank" rel="noreferrer" style={{ fontSize: fs(10), color: B.accent, textDecoration: "none", marginTop: 2, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ref.url}</a>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {Object.keys(relsByType).length > 0 && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Relationships ({allRels.length})</div>
                {Object.entries(relsByType).map(([label, items]) => (
                  <div key={label} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                    {items.map(({ other, otherId }) => {
                      const oc = other ? getConf(other.type) : { color: B.textDim, label: "Unknown" };
                      return (
                        <div key={otherId} onClick={() => other && onNavigate(other)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: other ? "pointer" : "default", background: B.bg, marginBottom: 4 }}>
                          <TypeIcon type={other?.type || "?"} color={oc.color} size={13} />
                          <span style={{ fontSize: fs(12), color: other ? oc.color : B.textDim, fontWeight: 500 }}>{other?.name || otherId}</span>
                          <span style={{ marginLeft: "auto", fontSize: fs(10), color: B.textDim }}>{oc.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
            {refs.length > 0 && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Referenced Objects ({refs.length})</div>
                {refs.map(ro => { const rc = getConf(ro.type); return (
                  <div key={ro.id} onClick={() => onNavigate(ro)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: "pointer", background: B.bg, marginBottom: 4 }}>
                    <TypeIcon type={ro.type} color={rc.color} size={13} /><span style={{ fontSize: fs(12), color: rc.color }}>{ro.name}</span>
                  </div>
                ); })}
              </div>
            )}
            {referencedByReports.length > 0 && (
              <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
                <div style={DP.sectionLabel}>Referenced by Reports ({referencedByReports.length})</div>
                {referencedByReports.map(rpt => (
                  <div key={rpt.id} onClick={() => onNavigate(rpt)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: "pointer", background: B.bg, marginBottom: 4 }}>
                    <TypeIcon type="report" color="#7C3AED" size={13} /><span style={{ fontSize: fs(12), color: "#7C3AED" }}>{rpt.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 20 }}>
              <div style={DP.sectionLabel}>Metadata</div>
              {obj.created && <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 4 }}>Created: <span style={{ color: B.text }}>{new Date(obj.created).toLocaleString()}</span></div>}
              {obj.modified && <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 8 }}>Modified: <span style={{ color: B.text }}>{new Date(obj.modified).toLocaleString()}</span></div>}
              <div style={{ fontSize: fs(10), color: B.textDim, fontFamily: "monospace", wordBreak: "break-all" }}>{obj.id}</div>
            </div>
            <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", padding: "12px 20px", borderBottom: rawOpen ? `1px solid ${B.border}` : "none" }}>
                <button onClick={() => setRawOpen(o => !o)} style={{ flex: 1, background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: fs(11), color: B.textMuted, fontFamily: "'Satoshi', sans-serif", fontWeight: 600, padding: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <span>Raw STIX JSON</span>
                  <span style={{ fontSize: fs(10), color: B.textDim }}>{rawOpen ? "▲" : "▼"}</span>
                </button>
                <button onClick={copyRaw} style={{ flexShrink: 0, background: copied ? "#10b98120" : B.bg, border: `1px solid ${copied ? "#10b981" : B.border}`, borderRadius: 6, color: copied ? "#10b981" : B.textMuted, padding: "4px 12px", cursor: "pointer", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, transition: "all 0.2s" }}>
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              {rawOpen && <pre style={{ margin: 0, padding: "16px 20px", fontSize: fs(11), color: B.textDim, overflowX: "hidden", overflowY: "auto", fontFamily: "monospace", lineHeight: 1.6, background: B.bg, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{JSON.stringify(obj, null, 2)}</pre>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const DP = {
  sectionLabel: { fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 14 },
};

// ─── Sidebar ───
function AppShell({
  page, onNavigate, user, onLogin, onLogout, pcapSession,
  collapsed, onToggleCollapse,
  categories, activeTypes, onToggleType, objectCounts, view, onSetView, kbSection,
}) {
  const [exp, setExp] = useState({});
  const toggle = (n) => setExp(p => ({ ...p, [n]: p[n] === false }));
  const [searching, setSearching] = useState(false);
  const openSearch = useCallback(() => setSearching(true), []);
  useSearchHotkey(openSearch);
  const searchUi = { B, fs, TypeIcon, getConf };
  const sections = visibleSections(user);
  // Knowledge Base sections navigate by URL hash; the rest switch pages.
  const goSection = (s) => (s.hash ? (window.location.hash = s.hash) : onNavigate(s.landing || s.p));
  const isActive = (s) => (s.kb ? page === "knowledge" && kbSection === s.kb : s.group ? GROUP_OF[page] === s.group : page === s.p);
  // Headings fold their group away; remembered in this browser. The page you're on stays in view.
  const [shutGroups, setShutGroups] = useState(() => { try { return JSON.parse(window.localStorage.getItem("idlehour.sidebar.shut") || "[]"); } catch { return []; } });
  const toggleGroup = (g) => setShutGroups(prev => {
    const next = prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g];
    try { window.localStorage.setItem("idlehour.sidebar.shut", JSON.stringify(next)); } catch {}
    return next;
  });

  const isViewer = page === "viewer" && !!categories;

  const knownTypes = isViewer ? new Set(categories.flatMap(c => c.types)) : new Set();
  const customTypes = isViewer ? Object.keys(objectCounts).filter(t => !knownTypes.has(t) && t !== "relationship" && objectCounts[t] > 0) : [];
  const allCats = isViewer ? (customTypes.length > 0 ? [...categories, { name: "Custom", types: customTypes }] : categories) : [];

  const navTabs = [
    { k: "graph", l: "Graph" }, { k: "list", l: "List" }, { k: "timeline", l: "Timeline" },
    { k: "dashboard", l: "Dashboard" }, { k: "mitre", l: "Attack Patterns" }, { k: "geo", l: "Map" },
    { k: "cases", l: "Cases" }, { k: "investigation", l: "Investigation" },
  ];

  const renderType = (t) => {
    const tc = getConf(t); const count = objectCounts[t] || 0; if (!count) return null;
    const on = activeTypes.has(t);
    return (
      <div key={t} onClick={() => onToggleType(t)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 16px 6px 34px", cursor: "pointer", fontSize: fs(13), color: on ? tc.color : B.textMuted, background: on ? tc.color + "08" : "transparent", userSelect: "none" }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: on ? tc.color : "transparent", border: `1.5px solid ${on ? tc.color : B.borderLight}`, flexShrink: 0 }} />
        <TypeIcon type={t} color={on ? tc.color : B.textDim} size={14} />
        <span style={{ flex: 1 }}>{tc.label}</span>
        <span style={{ fontSize: fs(11), color: B.textDim }}>{count}</span>
      </div>
    );
  };

  return (
    <div style={{ ...ST.sidebar, width: collapsed ? 40 : 280 }}>
      {/* Logo + collapse toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", padding: collapsed ? "10px 0 6px" : "10px 8px 6px 14px", borderBottom: `1px solid ${B.border}`, flexShrink: 0 }}>
        {!collapsed && (
          <button onClick={() => onNavigate("home")} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <Logo size={34} />
            <Wordmark fs={fs} color={B.text} size={15} />
          </button>
        )}
        <button
          onClick={onToggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(16), lineHeight: 1, padding: "2px 4px", borderRadius: 4 }}
        >
          {collapsed ? "›" : "‹"}
        </button>
      </div>
      {!collapsed && <SearchLauncher collapsed={false} onOpen={openSearch} ui={searchUi} />}
      {searching && <SearchPalette onClose={() => setSearching(false)} ui={searchUi} />}

      {!collapsed && (
        <>
          {/* App sections */}
          <div style={{ borderBottom: `1px solid ${B.border}`, paddingBottom: 6, flexShrink: 0 }}>
            {sections.map((sec, i) => {
              const on = isActive(sec);
              const shut = !!sec.under && shutGroups.includes(sec.under);
              const head = sec.under && sec.under !== sections[i - 1]?.under;
              const inGroup = head ? sections.filter(s => s.under === sec.under).length : 0;
              return (
              <Fragment key={sec.p}>
              {head && (
                <button onClick={() => toggleGroup(sec.under)} aria-expanded={!shut} title={shut ? `Show ${sec.under}` : `Hide ${sec.under}`} style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.8, padding: "12px 16px 4px", fontWeight: 700 }}>
                  <span style={{ flex: 1 }}>{sec.under}</span>
                  {shut && <span style={{ letterSpacing: 0, fontWeight: 600 }}>{inGroup}</span>}
                  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ transform: shut ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }}><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              )}
              {(!shut || on) && <button onClick={() => goSection(sec)} aria-current={on ? "page" : undefined} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "7px 16px",
                border: "none", borderLeft: `3px solid ${on ? B.primary : "transparent"}`,
                background: on ? B.primary + "12" : "transparent",
                color: on ? B.text : B.textDim,
                cursor: "pointer", fontSize: fs(13),
                fontWeight: on ? 700 : 500,
                fontFamily: "'Satoshi', sans-serif",
                textAlign: "left",
              }}>
                <SectionIcon page={sec.p} color={on ? B.primary : B.textDim} size={15} />
                {sec.l}
                {isNewSection(sec) && <span style={{ fontSize: fs(9), fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: B.primary, border: `1px solid ${B.primary}66`, background: B.primary + "14", borderRadius: 999, padding: "1px 6px", lineHeight: 1.4 }}>New</span>}
              </button>}
              </Fragment>
            ); })}
            {pcapSession?.stats && page !== "pcap" && (
              <button onClick={() => onNavigate("pcap")} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "7px 16px", marginTop: 2,
                border: "none", background: "rgba(34,197,94,0.08)",
                color: "#22c55e", cursor: "pointer", fontSize: fs(12),
                fontWeight: 700, fontFamily: "'Satoshi', sans-serif", textAlign: "left",
              }}>
                ● Resume PCAP
              </button>
            )}
          </div>

          {isViewer && (
            <>
              {/* Workspace nav */}
              <div style={{ borderBottom: `1px solid ${B.border}`, paddingBottom: 6, flexShrink: 0 }}>
                <div style={{ fontSize: fs(11), color: B.textDim, textTransform: "uppercase", letterSpacing: 2, padding: "8px 16px 6px", fontWeight: 700 }}>Views</div>
                {navTabs.map(t => (
                  <button key={t.k} onClick={() => onSetView(t.k)} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 16px",
                    border: "none", borderLeft: `3px solid ${view === t.k ? B.primary : "transparent"}`,
                    background: view === t.k ? B.primary + "12" : "transparent",
                    color: view === t.k ? B.text : B.textDim,
                    cursor: "pointer", fontSize: fs(13),
                    fontWeight: view === t.k ? 700 : 500,
                    fontFamily: "'Satoshi', sans-serif",
                    textAlign: "left",
                  }}>
                    {t.l}
                  </button>
                ))}
              </div>

              {/* Type filters */}
              <div style={{ overflowY: "auto", flex: 1, paddingTop: 4 }}>
                <div style={{ fontSize: fs(11), color: B.textDim, textTransform: "uppercase", letterSpacing: 2, padding: "10px 16px 4px", fontWeight: 700 }}>Filter by type</div>
                {allCats.map(cat => {
                  const isOpen = exp[cat.name] !== false;
                  const cnt = cat.types.reduce((s, t) => s + (objectCounts[t] || 0), 0);
                  if (!cnt) return null;
                  return (
                    <div key={cat.name}>
                      <div onClick={() => toggle(cat.name)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", cursor: "pointer", fontSize: fs(14), color: B.textMuted, userSelect: "none", fontWeight: 600 }}>
                        <span style={{ fontSize: fs(8), color: B.textDim, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 0.15s" }}>▶</span>
                        <span style={{ flex: 1 }}>{cat.name}</span>
                        {cat.name === "Custom" && <span style={{ fontSize: fs(10), color: B.accent, marginRight: 4, fontWeight: 600 }}>NEW</span>}
                        <span style={{ fontSize: fs(12), color: B.textDim, background: B.bgCard, padding: "1px 7px", borderRadius: 8 }}>{cnt}</span>
                      </div>
                      {isOpen && cat.types.map(renderType)}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!isViewer && <div style={{ flex: 1 }} />}

          {/* Account footer */}
          <div style={{ borderTop: `1px solid ${B.border}`, padding: "10px 14px", flexShrink: 0 }}>
            {user ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={() => onNavigate("settings")} style={{ background: "none", border: "none", color: page === "settings" ? B.text : B.textDim, cursor: "pointer", fontSize: fs(11), padding: 0, textAlign: "left", fontFamily: "'Satoshi', sans-serif", fontWeight: page === "settings" ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</button>
                <button onClick={onLogout} style={{ fontSize: fs(11), padding: "5px 0", border: "none", background: "none", color: B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", textAlign: "left" }}>Sign Out</button>
              </div>
            ) : LOCKDOWN ? (
              <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.5 }}>Public preview: read-only.</div>
            ) : (
              <button onClick={onLogin} style={{ width: "100%", fontSize: fs(11), padding: "6px 12px", borderRadius: 6, border: `1px solid ${B.border}`, background: "none", color: B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>Sign In</button>
            )}
          </div>
        </>
      )}

      {collapsed && (
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingTop: 6 }}>
            <SearchLauncher collapsed onOpen={openSearch} ui={searchUi} />
            {sections.map((sec, i) => (
              <Fragment key={sec.p}>
              {sec.under && sec.under !== sections[i - 1]?.under && <div aria-hidden="true" title={sec.under} style={{ width: 18, borderTop: `1px solid ${B.border}`, margin: "5px 0" }} />}
              <button onClick={() => goSection(sec)} title={isNewSection(sec) ? `${sec.l} (new)` : sec.l} aria-label={isNewSection(sec) ? `${sec.l} (new)` : sec.l} style={{
                width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                border: "none", borderRadius: 6, cursor: "pointer", position: "relative",
                background: isActive(sec) ? B.primary + "1f" : "transparent",
              }}>
                <SectionIcon page={sec.p} color={isActive(sec) ? B.primary : B.textDim} />
                {isNewSection(sec) && <span aria-hidden="true" style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: B.primary }} />}
              </button>
              </Fragment>
            ))}
            {pcapSession?.stats && page !== "pcap" && (
              <button onClick={() => onNavigate("pcap")} title="Resume PCAP" aria-label="Resume PCAP" style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, cursor: "pointer", background: "rgba(34,197,94,0.12)" }}>
                <SectionIcon page="pcap" color="#22c55e" />
              </button>
            )}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", justifyContent: "center", borderTop: `1px solid ${B.border}`, padding: "8px 0" }}>
            {user ? (
              <button onClick={() => onNavigate("settings")} title={`Account (${user.email})`} aria-label="Account" style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, cursor: "pointer", background: page === "settings" ? B.primary + "1f" : "transparent" }}>
                <SectionIcon page="settings" color={page === "settings" ? B.primary : B.textDim} />
              </button>
            ) : LOCKDOWN ? null : (
              <button onClick={onLogin} title="Sign In" aria-label="Sign In" style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, cursor: "pointer", background: "transparent" }}>
                <SectionIcon page="login" color={B.textDim} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Bundle Preview Page ───
function BundlePreviewPage({ bundle: rawBundle, onBack, backLabel, onNavigate, user, onLogin, onLogout, pcapSession, collapsed, onToggleCollapse, onLoad }) {
  const { bundle, idDupes, nameDupes } = useMemo(() => deduplicateBundle(rawBundle), [rawBundle]);
  const allObjects = bundle.objects || [];
  const nonRels = allObjects.filter(o => o.type !== "relationship");
  const rels = allObjects.filter(o => o.type === "relationship");
  const [selected, setSelected] = useState(new Set(allObjects.map(o => o.id)));
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);

  const types = [...new Set(nonRels.map(o => o.type))].sort();

  const toggleObj = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleType = (type) => {
    const ids = nonRels.filter(o => o.type === type).map(o => o.id);
    const allOn = ids.every(id => selected.has(id));
    setSelected(prev => { const n = new Set(prev); ids.forEach(id => allOn ? n.delete(id) : n.add(id)); return n; });
  };
  const selectAll = () => setSelected(new Set(allObjects.map(o => o.id)));
  const deselectAll = () => setSelected(new Set());

  const visible = nonRels.filter(o => {
    if (typeFilter && o.type !== typeFilter) return false;
    if (search && !(o.name || o.id).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectedNonRels = nonRels.filter(o => selected.has(o.id)).length;

  const load = () => {
    const kept = new Set(nonRels.filter(o => selected.has(o.id)).map(o => o.id));
    const finalObjects = [
      ...nonRels.filter(o => kept.has(o.id)),
      ...rels.filter(r => kept.has(r.source_ref) && kept.has(r.target_ref)),
    ];
    onLoad({ ...bundle, objects: finalObjects });
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="preview" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, overflowY: "auto", background: B.bg, fontFamily: "'Satoshi', sans-serif", color: B.text, display: "flex", flexDirection: "column" }}>
      <GroupTabs page="preview" onNavigate={onNavigate} />
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>

      {/* Summary bar */}
      <div style={{ padding: "16px 32px", borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
        <button onClick={onBack} style={HP.navBtn}>← {backLabel || "Back to Import"}</button>
        <div>
          <span style={{ fontSize: fs(18), fontWeight: 900 }}>Review Bundle</span>
          <span style={{ fontSize: fs(11), color: B.textDim, marginLeft: 12 }}>Select which objects to ingest</span>
          {(idDupes > 0 || nameDupes > 0) && (
            <div style={{ marginTop: 4, fontSize: fs(11), color: "#f59e0b" }}>
              ✓ Auto-deduped: {idDupes > 0 ? `${idDupes} duplicate ID${idDupes > 1 ? "s" : ""}` : ""}{idDupes > 0 && nameDupes > 0 ? ", " : ""}{nameDupes > 0 ? `${nameDupes} duplicate name${nameDupes > 1 ? "s" : ""} (case/separator-insensitive)` : ""} removed
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 16, marginLeft: "auto", flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Objects", value: nonRels.length, color: B.primary },
            { label: "Types", value: types.length, color: B.accent },
            { label: "Relationships", value: rels.length, color: B.textMuted },
            { label: "Selected", value: selectedNonRels, color: "#10b981" },
          ].map(s => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: fs(20), fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: fs(9), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
          <button onClick={load} disabled={selectedNonRels === 0} style={{ ...HP.primaryBtn, opacity: selectedNonRels > 0 ? 1 : 0.4, marginLeft: 8 }}>
            Load {selectedNonRels} Objects →
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left: type panel */}
        <div style={{ width: 220, background: B.bgPanel, borderRight: `1px solid ${B.border}`, overflow: "auto", flexShrink: 0, padding: "12px 0" }}>
          <div style={{ padding: "0 12px 10px", display: "flex", gap: 6 }}>
            <button onClick={selectAll} style={{ flex: 1, fontSize: fs(10), padding: "4px", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 4, color: B.textMuted, cursor: "pointer" }}>All</button>
            <button onClick={deselectAll} style={{ flex: 1, fontSize: fs(10), padding: "4px", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 4, color: B.textMuted, cursor: "pointer" }}>None</button>
          </div>
          <div onClick={() => setTypeFilter(null)} style={{ padding: "6px 16px", cursor: "pointer", fontSize: fs(11), fontWeight: 600, color: !typeFilter ? B.accent : B.textMuted, background: !typeFilter ? B.accent + "10" : "transparent" }}>
            All Types <span style={{ float: "right", fontSize: fs(10), color: B.textDim }}>{nonRels.length}</span>
          </div>
          {types.map(t => {
            const c = getConf(t);
            const count = nonRels.filter(o => o.type === t).length;
            const checkedCount = nonRels.filter(o => o.type === t && selected.has(o.id)).length;
            const allOn = checkedCount === count;
            const someOn = checkedCount > 0 && !allOn;
            return (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 16px", cursor: "pointer", background: typeFilter === t ? c.color + "10" : "transparent" }}>
                <span onClick={() => toggleType(t)} style={{ width: 13, height: 13, borderRadius: 3, border: `1.5px solid ${allOn ? c.color : someOn ? c.color : B.borderLight}`, background: allOn ? c.color : someOn ? c.color + "44" : "transparent", flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {allOn && <span style={{ color: "#fff", fontSize: fs(9), lineHeight: 1 }}>✓</span>}
                  {someOn && <span style={{ color: c.color, fontSize: fs(9), lineHeight: 1 }}>–</span>}
                </span>
                <span onClick={() => setTypeFilter(typeFilter === t ? null : t)} style={{ flex: 1, fontSize: fs(11), color: typeFilter === t ? c.color : B.textMuted, display: "flex", alignItems: "center", gap: 5 }}>
                  <TypeIcon type={t} color={c.color} size={12} />{c.label}
                  {c.custom && <span style={{ fontSize: fs(8), color: c.color, border: `1px solid ${c.color}44`, borderRadius: 4, padding: "0 4px" }}>custom</span>}
                </span>
                <span style={{ fontSize: fs(10), color: B.textDim }}>{checkedCount}/{count}</span>
              </div>
            );
          })}
        </div>

        {/* Right: object list */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${B.border}`, display: "flex", gap: 10, alignItems: "center" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search objects…" style={{ flex: 1, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, padding: "7px 12px", color: B.text, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" }} />
            <span style={{ fontSize: fs(11), color: B.textDim }}>{visible.length} shown</span>
          </div>
          <div style={{ overflow: "auto", flex: 1, padding: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(12) }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${B.border}` }}>
                  {["", "Type", "Name", "UUID", "Description"].map(h => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(o => {
                  const c = getConf(o.type);
                  const on = selected.has(o.id);
                  return (
                    <tr key={o.id} onClick={() => toggleObj(o.id)} style={{ borderBottom: `1px solid ${B.border}22`, cursor: "pointer", background: on ? "transparent" : B.bgCard + "44", opacity: on ? 1 : 0.45 }}>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${on ? c.color : B.borderLight}`, background: on ? c.color : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {on && <span style={{ color: "#fff", fontSize: fs(9) }}>✓</span>}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ fontSize: fs(10), padding: "2px 8px", borderRadius: 8, background: c.color + "15", color: c.color, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                          <TypeIcon type={o.type} color={c.color} size={10} />{c.label}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px", fontWeight: 600, color: B.text }}>{o.name || "—"}</td>
                      <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: fs(10), color: B.textDim, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.id}</td>
                      <td style={{ padding: "8px 10px", color: B.textMuted, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.description || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visible.length === 0 && <div style={{ textAlign: "center", padding: 40, color: B.textDim }}>No objects match</div>}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── Export Utilities ───
function exportCSV(objects) {
  const nonRels = objects.filter(o => o.type !== "relationship");
  const headers = ["id","type","name","description","created","modified","labels","confidence"];
  const rows = nonRels.map(o => headers.map(h => {
    const v = o[h]; if (!v) return "";
    if (Array.isArray(v)) return v.join("; ");
    return String(v).replace(/"/g, '""').replace(/\n/g, " ");
  }));
  const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: "idle-hour-export.csv" });
  a.click(); URL.revokeObjectURL(a.href);
}
// The workspace as a valid STIX 2.1 bundle (src/lib/stix21.js fixes format, never content).
function exportBundle(bundle) {
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([JSON.stringify(finishBundle(bundle), null, 2)], { type: "application/stix+json;version=2.1" })), download: "idle-hour-bundle.stix.json" });
  a.click(); URL.revokeObjectURL(a.href);
}

// ─── Export Menu ───
function ExportMenu({ objects, bundle }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "5px 14px", borderRadius: 6, cursor: "pointer", fontSize: fs(11), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }}>Export ▾</button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "110%", background: B.bgPanel, border: `1px solid ${B.border}`, borderRadius: 8, zIndex: 50, minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
          {[["Export STIX 2.1 bundle", () => exportBundle(bundle)], ["Export CSV", () => exportCSV(objects)]].map(([label, fn]) => (
            <button key={label} onClick={() => { fn(); setOpen(false); }} style={{ display: "block", width: "100%", background: "none", border: "none", padding: "10px 16px", textAlign: "left", color: B.textMuted, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", cursor: "pointer" }}>{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Chart Components ───
function DonutChart({ segments, size = 130, label }) {
  const total = segments.reduce((s, g) => s + g.value, 0);
  if (!total) return <div style={{ width: size, height: size, borderRadius: "50%", background: B.bgCard, border: `1px solid ${B.border}` }} />;
  let angle = -Math.PI / 2;
  const r = size * 0.38, ir = size * 0.24, cx = size / 2, cy = size / 2;
  const paths = segments.filter(s => s.value > 0).map(seg => {
    const a = (seg.value / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
    angle += a;
    const x2 = cx + r * Math.cos(angle), y2 = cy + r * Math.sin(angle);
    const ix1 = cx + ir * Math.cos(angle), iy1 = cy + ir * Math.sin(angle);
    const ix2 = cx + ir * Math.cos(angle - a), iy2 = cy + ir * Math.sin(angle - a);
    const la = a > Math.PI ? 1 : 0;
    return <path key={seg.label} d={`M${x1} ${y1} A${r} ${r} 0 ${la} 1 ${x2} ${y2} L${ix1} ${iy1} A${ir} ${ir} 0 ${la} 0 ${ix2} ${iy2}Z`} fill={seg.color} opacity={0.85} />;
  });
  return (
    <svg width={size} height={size}>
      {paths}
      {label && <text x={cx} y={cy + 4} textAnchor="middle" fill={B.text} fontSize={size * 0.12} fontWeight="bold" fontFamily="Satoshi, sans-serif">{label}</text>}
    </svg>
  );
}

function HBarChart({ data, maxRows = 10 }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {data.slice(0, maxRows).map(d => (
        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 100, fontSize: fs(10), color: B.textMuted, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{d.label}</div>
          <div style={{ flex: 1, background: B.bg, borderRadius: 3, height: 18, overflow: "hidden" }}>
            <div style={{ width: `${(d.value / max) * 100}%`, height: "100%", background: d.color || B.primary, borderRadius: 3, minWidth: 2, transition: "width 0.3s" }} />
          </div>
          <div style={{ width: 28, fontSize: fs(10), color: B.textDim, textAlign: "right" }}>{d.value}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Widget Menu (3-dot edit/delete) ───
function WidgetMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(p => !p)} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(16), padding: "0 4px", lineHeight: 1, borderRadius: 4 }}>⋮</button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 200, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, overflow: "hidden", minWidth: 110, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
          {[["Edit", B.text, onEdit], ["Delete", "#ef4444", onDelete]].map(([l, color, fn]) => (
            <div key={l} onClick={() => { fn(); setOpen(false); }} style={{ padding: "9px 14px", fontSize: fs(12), cursor: "pointer", color, background: "transparent" }}
              onMouseEnter={e => e.currentTarget.style.background = B.bgPanel}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Widget Builder Modal ───
function WidgetBuilder({ onSave, onClose, initial, allLabels, allTypes }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [objectType, setObjectType] = useState(initial?.objectType || "");
  const [filterText, setFilterText] = useState(initial?.filterText || "");
  const [filterLabel, setFilterLabel] = useState(initial?.filterLabel || "");
  const [display, setDisplay] = useState(initial?.display || "list");

  const canSave = title.trim().length > 0;
  const inputSt = { width: "100%", padding: "8px 12px", borderRadius: 8, background: B.bg, border: `1px solid ${B.border}`, color: B.text, fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 14, padding: 28, width: 440, boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: fs(15), fontWeight: 800, color: B.text, marginBottom: 22 }}>{initial ? "Edit Panel" : "New Panel"}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Panel Title</div>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Russian Threat Actors" style={inputSt} autoFocus />
          </div>

          <div>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Object Type</div>
            <select value={objectType} onChange={e => setObjectType(e.target.value)} style={{ ...inputSt, appearance: "none" }}>
              <option value="">All Types</option>
              {allTypes.map(t => <option key={t} value={t}>{getConf(t).label}</option>)}
            </select>
          </div>

          <div>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Keyword Filter</div>
            <input value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="e.g. russia, ransomware, APT29…" style={inputSt} />
            <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 5 }}>Searches name, description, aliases, country, and all fields</div>
          </div>

          {allLabels.length > 0 && (
            <div>
              <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Filter by Label</div>
              <select value={filterLabel} onChange={e => setFilterLabel(e.target.value)} style={{ ...inputSt, appearance: "none" }}>
                <option value="">Any Label</option>
                {allLabels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}

          <div>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>Display As</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[["card", "Count Card"], ["list", "Object List"], ["bar", "Bar Chart"]].map(([k, l]) => (
                <button key={k} onClick={() => setDisplay(k)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1px solid ${display === k ? B.primary : B.border}`, background: display === k ? B.primary + "18" : "transparent", color: display === k ? B.primary : B.textDim, fontSize: fs(12), cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: display === k ? 700 : 400, transition: "all 0.15s" }}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 22px", borderRadius: 8, background: "transparent", border: `1px solid ${B.border}`, color: B.textDim, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>Cancel</button>
          <button onClick={() => canSave && onSave({ title: title.trim(), objectType, filterText: filterText.trim(), filterLabel, display })} disabled={!canSave}
            style={{ padding: "9px 22px", borderRadius: 8, background: canSave ? `linear-gradient(135deg, ${B.primary}, ${B.accent})` : B.bgPanel, border: "none", color: canSave ? B.white : B.textDim, cursor: canSave ? "pointer" : "not-allowed", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 }}>
            {initial ? "Save Changes" : "Add Panel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard View ───
function DashboardView({ objects, relationships, markingDefs, onSelect, allObjects = objects }) {
  const nonRels = objects.filter(o => o.type !== "relationship");
  const healthObjects = allObjects.filter(o => o.type !== "relationship");

  const [widgets, setWidgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ih_dashboard_widgets") || "[]"); } catch { return []; }
  });
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);

  const saveWidgets = (next) => { setWidgets(next); localStorage.setItem("ih_dashboard_widgets", JSON.stringify(next)); };
  const addWidget = (cfg) => { saveWidgets([...widgets, { id: String(Date.now()), ...cfg }]); setBuilderOpen(false); };
  const updateWidget = (id, cfg) => { saveWidgets(widgets.map(w => w.id === id ? { ...w, ...cfg } : w)); setEditingWidget(null); };
  const deleteWidget = (id) => saveWidgets(widgets.filter(w => w.id !== id));

  const filterWidgetObjects = (widget) => {
    let res = nonRels;
    if (widget.objectType) res = res.filter(o => o.type === widget.objectType);
    if (widget.filterText) { const q = widget.filterText.toLowerCase(); res = res.filter(o => JSON.stringify(o).toLowerCase().includes(q)); }
    if (widget.filterLabel) res = res.filter(o => (o.labels || []).includes(widget.filterLabel));
    return res;
  };

  const typeCounts = {};
  nonRels.forEach(o => { typeCounts[o.type] = (typeCounts[o.type] || 0) + 1; });
  const typeData = Object.entries(typeCounts).map(([t, v]) => ({ label: getConf(t).label, value: v, color: getConf(t).color })).sort((a, b) => b.value - a.value);

  const relCounts = {};
  relationships.forEach(r => { relCounts[r.source_ref] = (relCounts[r.source_ref] || 0) + 1; relCounts[r.target_ref] = (relCounts[r.target_ref] || 0) + 1; });
  const topNodes = Object.entries(relCounts).map(([id, cnt]) => { const o = nonRels.find(x => x.id === id); return { label: o?.name || id, value: cnt, color: o ? getConf(o.type).color : B.textDim, obj: o }; }).sort((a, b) => b.value - a.value).slice(0, 8);

  const markingCounts = {};
  const markingColors = {};
  nonRels.forEach(o => {
    const markings = getMarkings(o, markingDefs);
    const label = markings.length ? markings[0].name : "No Marking";
    markingCounts[label] = (markingCounts[label] || 0) + 1;
    if (markings.length && !markingColors[label]) markingColors[label] = markings[0].color;
  });

  const confBuckets = { "High (≥70)": 0, "Med (40-69)": 0, "Low (<40)": 0, "N/A": 0 };
  nonRels.forEach(o => { if (o.confidence == null) confBuckets["N/A"]++; else if (o.confidence >= 70) confBuckets["High (≥70)"]++; else if (o.confidence >= 40) confBuckets["Med (40-69)"]++; else confBuckets["Low (<40)"]++; });
  const confSegs = [{ label: "High", value: confBuckets["High (≥70)"], color: "#16a34a" }, { label: "Med", value: confBuckets["Med (40-69)"], color: "#f59e0b" }, { label: "Low", value: confBuckets["Low (<40)"], color: "#ef4444" }, { label: "N/A", value: confBuckets["N/A"], color: B.textDim }];

  const labelCounts = {};
  nonRels.forEach(o => (o.labels || []).forEach(l => { labelCounts[l] = (labelCounts[l] || 0) + 1; }));

  const stats = [
    { label: "Objects", value: nonRels.length, color: B.primary },
    { label: "Relationships", value: relationships.length, color: B.accent },
    { label: "Types", value: Object.keys(typeCounts).length, color: "#06b6d4" },
    { label: "Threat Actors", value: typeCounts["threat-actor"] || 0, color: "#E4571E" },
    { label: "Malware", value: typeCounts["malware"] || 0, color: "#A855F7" },
    { label: "Indicators", value: (typeCounts["indicator"] || 0) + (typeCounts["ipv4-addr"] || 0) + (typeCounts["domain-name"] || 0), color: "#f59e0b" },
    { label: "Attack Patterns", value: typeCounts["attack-pattern"] || 0, color: "#ef4444" },
    { label: "Campaigns", value: typeCounts["campaign"] || 0, color: "#10b981" },
  ];

  const tlpOrder = ["TLP:WHITE", "TLP:GREEN", "TLP:AMBER", "TLP:RED"];
  const tlpColorMap = { "TLP:WHITE": "#9ca3af", "TLP:CLEAR": "#9ca3af", "TLP:GREEN": "#16a34a", "TLP:AMBER": "#f59e0b", "TLP:RED": "#ef4444" };
  const tlpCounts = {};
  nonRels.forEach(o => { const tlp = getTLP(o, markingDefs); const k = tlp ? tlp.name : "None"; tlpCounts[k] = (tlpCounts[k] || 0) + 1; });
  const tlpSegs = [...tlpOrder.map(k => ({ label: k, value: tlpCounts[k] || 0, color: tlpColorMap[k] })), { label: "None", value: tlpCounts["None"] || 0, color: B.textDim }];

  const intelHealth = useMemo(() => {
    const objectById = new Map(healthObjects.map(o => [o.id, o]));
    const connectedIds = new Set();
    const danglingRefs = [];

    relationships.forEach(r => {
      const source = objectById.get(r.source_ref);
      const target = objectById.get(r.target_ref);
      if (source && target) {
        connectedIds.add(source.id);
        connectedIds.add(target.id);
      } else {
        danglingRefs.push({ id: r.id, obj: source || target || null, missing: source ? r.target_ref : r.source_ref });
      }
    });

    healthObjects.forEach(o => {
      (o.object_refs || []).forEach(ref => {
        if (objectById.has(ref)) {
          connectedIds.add(o.id);
          connectedIds.add(ref);
        } else {
          danglingRefs.push({ id: `${o.id}:${ref}`, obj: o, missing: ref });
        }
      });
    });

    const orphanObjects = healthObjects.filter(o => o.type !== "marking-definition" && !connectedIds.has(o.id));
    const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const duplicateBuckets = new Map();
    const addDuplicateKey = (key, obj) => {
      if (!key) return;
      if (!duplicateBuckets.has(key)) duplicateBuckets.set(key, []);
      duplicateBuckets.get(key).push(obj);
    };
    healthObjects.forEach(o => {
      addDuplicateKey(`${o.type}:name:${normalize(getDisplayName(o))}`, o);
      if (o.type === "indicator" && o.pattern) addDuplicateKey(`indicator:pattern:${normalize(o.pattern)}`, o);
    });
    const duplicateGroups = [...duplicateBuckets.entries()]
      .map(([key, group]) => ({ key, objects: group }))
      .filter(g => g.objects.length > 1);
    const duplicateObjects = [...new Map(duplicateGroups.flatMap(g => g.objects).map(o => [o.id, o])).values()];

    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const staleIndicators = healthObjects.filter(o => {
      if (o.type !== "indicator") return false;
      const validUntil = Date.parse(o.valid_until || "");
      if (Number.isFinite(validUntil) && validUntil < now) return true;
      const modified = Date.parse(o.modified || o.created || "");
      return Number.isFinite(modified) && now - modified > 365 * dayMs;
    });
    const confidenceTypes = new Set(["indicator", "malware", "threat-actor", "campaign", "attack-pattern", "tool", "vulnerability"]);
    const missingConfidence = healthObjects.filter(o => confidenceTypes.has(o.type) && o.confidence == null);

    const hasNeighborType = (obj, targetTypes) => relationships.some(r => {
      if (r.source_ref !== obj.id && r.target_ref !== obj.id) return false;
      const otherId = r.source_ref === obj.id ? r.target_ref : r.source_ref;
      const other = objectById.get(otherId);
      return other && targetTypes.includes(other.type);
    });
    const gapDefs = [
      { type: "malware", label: "Malware without indicators", targets: ["indicator", "domain-name", "ipv4-addr", "url", "file"] },
      { type: "campaign", label: "Campaigns without attribution", targets: ["threat-actor", "intrusion-set"] },
      { type: "threat-actor", label: "Threat actors without capabilities", targets: ["malware", "tool", "campaign", "attack-pattern"] },
      { type: "vulnerability", label: "Vulnerabilities without exploitation context", targets: ["malware", "attack-pattern", "indicator"] },
    ];
    const relationshipGaps = gapDefs.flatMap(def =>
      healthObjects.filter(o => o.type === def.type && !hasNeighborType(o, def.targets)).map(obj => ({ obj, label: def.label }))
    );

    const issues = [
      danglingRefs.length > 0 && { key: "dangling", severity: "Critical", color: "#ef4444", title: "Dangling references", detail: `${danglingRefs.length} relationship or object reference points to a missing STIX ID.`, count: danglingRefs.length, objects: danglingRefs.map(d => d.obj).filter(Boolean) },
      duplicateGroups.length > 0 && { key: "duplicates", severity: "High", color: "#f97316", title: "Duplicate-ish objects", detail: `${duplicateGroups.length} repeated name or indicator-pattern group${duplicateGroups.length === 1 ? "" : "s"} detected.`, count: duplicateGroups.length, objects: duplicateObjects },
      orphanObjects.length > 0 && { key: "orphans", severity: "Medium", color: "#f59e0b", title: "Orphan objects", detail: `${orphanObjects.length} object${orphanObjects.length === 1 ? "" : "s"} have no relationship or report reference.`, count: orphanObjects.length, objects: orphanObjects },
      staleIndicators.length > 0 && { key: "stale", severity: "Medium", color: "#f59e0b", title: "Stale indicators", detail: `${staleIndicators.length} indicator${staleIndicators.length === 1 ? "" : "s"} are expired or have not been modified in over a year.`, count: staleIndicators.length, objects: staleIndicators },
      missingConfidence.length > 0 && { key: "confidence", severity: "Low", color: "#06b6d4", title: "Missing confidence", detail: `${missingConfidence.length} analytic object${missingConfidence.length === 1 ? "" : "s"} have no confidence score.`, count: missingConfidence.length, objects: missingConfidence },
      relationshipGaps.length > 0 && { key: "gaps", severity: "Low", color: B.accent, title: "Relationship gaps", detail: `${relationshipGaps.length} object${relationshipGaps.length === 1 ? "" : "s"} are missing expected analytic context.`, count: relationshipGaps.length, objects: relationshipGaps.map(g => g.obj) },
    ].filter(Boolean);

    const penalty =
      Math.min(28, danglingRefs.length * 8) +
      Math.min(18, duplicateGroups.length * 6) +
      Math.min(18, orphanObjects.length * 2) +
      Math.min(14, staleIndicators.length * 4) +
      Math.min(10, missingConfidence.length) +
      Math.min(14, relationshipGaps.length * 3);
    const score = Math.max(0, Math.min(100, 100 - penalty));
    const status = score >= 85 ? "Strong" : score >= 70 ? "Watch" : score >= 50 ? "Needs Review" : "Critical";
    const statusColor = score >= 85 ? "#16a34a" : score >= 70 ? "#f59e0b" : score >= 50 ? "#f97316" : "#ef4444";

    return { score, status, statusColor, issues, orphanObjects, danglingRefs, duplicateGroups, staleIndicators, missingConfidence, relationshipGaps };
  }, [healthObjects, relationships]);

  const W = { background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 18 };
  const WL = { fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 14 };

  const allLabels = Object.keys(labelCounts);
  const allTypes = Object.keys(typeCounts);

  const renderCustomWidget = (widget) => {
    const matched = filterWidgetObjects(widget);
    const tc = getConf(widget.objectType || "");

    if (widget.display === "card") {
      return (
        <div key={widget.id} style={{ ...W, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 110 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{widget.title}</div>
            <WidgetMenu onEdit={() => setEditingWidget(widget)} onDelete={() => deleteWidget(widget.id)} />
          </div>
          <div style={{ fontSize: fs(44), fontWeight: 900, color: widget.objectType ? tc.color : B.primary, lineHeight: 1, margin: "10px 0 6px" }}>{matched.length}</div>
          <div style={{ fontSize: fs(10), color: B.textDim }}>
            {[widget.objectType && tc.label, widget.filterText && `"${widget.filterText}"`, widget.filterLabel && `label: ${widget.filterLabel}`].filter(Boolean).join(" · ") || "All objects"}
          </div>
        </div>
      );
    }

    if (widget.display === "bar") {
      const grouped = {};
      matched.forEach(o => {
        const key = widget.objectType ? (o.labels?.[0] || "No Label") : getConf(o.type).label;
        const color = widget.objectType ? B.accent : getConf(o.type).color;
        if (!grouped[key]) grouped[key] = { value: 0, color };
        grouped[key].value++;
      });
      const barData = Object.entries(grouped).map(([label, { value, color }]) => ({ label, value, color })).sort((a, b) => b.value - a.value);
      return (
        <div key={widget.id} style={W}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={WL}>{widget.title}</div>
            <WidgetMenu onEdit={() => setEditingWidget(widget)} onDelete={() => deleteWidget(widget.id)} />
          </div>
          {barData.length > 0 ? <HBarChart data={barData} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>No matches</div>}
        </div>
      );
    }

    return (
      <div key={widget.id} style={W}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
            {widget.title} <span style={{ fontWeight: 400, opacity: 0.7 }}>({matched.length})</span>
          </div>
          <WidgetMenu onEdit={() => setEditingWidget(widget)} onDelete={() => deleteWidget(widget.id)} />
        </div>
        {matched.length === 0 ? (
          <div style={{ fontSize: fs(12), color: B.textDim, padding: "12px 0" }}>No objects match this filter</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, maxHeight: 260, overflowY: "auto" }}>
            {matched.slice(0, 20).map(o => {
              const c = getConf(o.type);
              return (
                <div key={o.id} onClick={() => onSelect(o)} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", background: B.bg, borderRadius: 6, cursor: "pointer", border: `1px solid ${B.border}` }}>
                  <TypeIcon type={o.type} color={c.color} size={13} />
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ fontSize: fs(11), fontWeight: 600, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name || o.id}</div>
                    <div style={{ fontSize: fs(9), color: c.color }}>{c.label}</div>
                    {o.description && <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.description}</div>}
                  </div>
                </div>
              );
            })}
            {matched.length > 20 && <div style={{ fontSize: fs(10), color: B.textDim, padding: "8px 0", gridColumn: "1/-1" }}>+{matched.length - 20} more</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: 20, overflow: "auto", height: "100%" }}>
      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10, marginBottom: 18 }}>
        {stats.map(s => (
          <div key={s.label} style={{ ...W, padding: "14px 16px" }}>
            <div style={{ fontSize: fs(26), fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 5, textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Intel health */}
      <div style={{ ...W, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, alignItems: "start" }}>
          <div>
            <div style={WL}>Intel Health</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
              <div style={{ fontSize: fs(58), fontWeight: 900, color: intelHealth.statusColor, lineHeight: 0.9 }}>{intelHealth.score}</div>
              <div style={{ paddingBottom: 5 }}>
                <div style={{ fontSize: fs(15), fontWeight: 800, color: B.text }}>{intelHealth.status}</div>
                <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 3 }}>Full bundle</div>
              </div>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: B.bg, overflow: "hidden", border: `1px solid ${B.border}` }}>
              <div style={{ height: "100%", width: `${intelHealth.score}%`, background: intelHealth.statusColor, borderRadius: 999 }} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
              {[
                ["Dangling", intelHealth.danglingRefs.length, "#ef4444"],
                ["Orphans", intelHealth.orphanObjects.length, "#f59e0b"],
                ["Dupes", intelHealth.duplicateGroups.length, "#f97316"],
                ["Stale", intelHealth.staleIndicators.length, "#f59e0b"],
                ["Gaps", intelHealth.relationshipGaps.length, B.accent],
              ].map(([label, value, color]) => (
                <span key={label} style={{ fontSize: fs(10), color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 8, padding: "3px 8px", fontWeight: 700 }}>
                  {label} {value}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...WL, marginBottom: 4 }}>Health Findings</div>
            {intelHealth.issues.length === 0 ? (
              <div style={{ padding: "22px 0", color: B.textMuted, fontSize: fs(12), borderTop: `1px solid ${B.border}` }}>No major hygiene issues found.</div>
            ) : (
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                {intelHealth.issues.map(issue => {
                  const samples = [...new Map(issue.objects.map(o => [o.id, o])).values()].slice(0, 5);
                  const canOpen = samples.length > 0;
                  return (
                    <div key={issue.key} onClick={canOpen ? () => onSelect(samples[0]) : undefined} style={{ padding: "11px 0", borderTop: `1px solid ${B.border}`, cursor: canOpen ? "pointer" : "default" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                        <span style={{ fontSize: fs(9), color: issue.color, background: `${issue.color}18`, border: `1px solid ${issue.color}35`, borderRadius: 6, padding: "2px 6px", fontWeight: 800, textTransform: "uppercase" }}>{issue.severity}</span>
                        <span style={{ fontSize: fs(10), color: B.textDim, marginLeft: "auto" }}>{issue.count}</span>
                      </div>
                      <div style={{ fontSize: fs(13), color: B.text, fontWeight: 800, marginBottom: 3 }}>{issue.title}</div>
                      <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.4 }}>{issue.detail}</div>
                      {samples.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                          {samples.map(sample => {
                            const c = getConf(sample.type);
                            return (
                              <span key={sample.id} onClick={e => { e.stopPropagation(); onSelect(sample); }} style={{ display: "flex", alignItems: "center", gap: 4, maxWidth: 180, fontSize: fs(10), color: c.color, background: `${c.color}12`, border: `1px solid ${c.color}26`, borderRadius: 7, padding: "3px 7px" }}>
                                <TypeIcon type={sample.type} color={c.color} size={10} />
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getDisplayName(sample)}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div style={W}>
          <div style={WL}>Object Type Distribution</div>
          <HBarChart data={typeData} />
        </div>
        <div style={W}>
          <div style={WL}>Most Connected Nodes</div>
          <HBarChart data={topNodes.map(n => ({ ...n }))} />
        </div>
        <div style={W}>
          <div style={WL}>TLP Distribution</div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <DonutChart segments={tlpSegs} size={110} label={nonRels.length} />
            {tlpSegs.filter(s => s.value > 0).map(s => (
              <div key={s.label} style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: fs(10), color: B.textMuted }}>
                <span style={{ color: s.color }}>{s.label}</span><span>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={W}>
          <div style={WL}>Confidence</div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <DonutChart segments={confSegs} size={110} />
            {confSegs.filter(s => s.value > 0).map(s => (
              <div key={s.label} style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: fs(10), color: B.textMuted }}>
                <span style={{ color: s.color }}>{s.label}</span><span>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {Object.keys(labelCounts).length > 0 && (
        <div style={{ ...W, marginBottom: 14 }}>
          <div style={WL}>Labels</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(labelCounts).sort((a, b) => b[1] - a[1]).map(([l, cnt]) => (
              <span key={l} style={{ fontSize: fs(11), padding: "3px 10px", borderRadius: 10, background: B.accent + "18", color: B.accent, border: `1px solid ${B.accent}30` }}>{l} <span style={{ opacity: 0.6 }}>{cnt}</span></span>
            ))}
          </div>
        </div>
      )}

      <div style={{ ...W, marginBottom: 20 }}>
        <div style={WL}>Top Connected Objects</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
          {topNodes.map(n => {
            if (!n.obj) return null;
            const c = getConf(n.obj.type);
            return (
              <div key={n.obj.id} onClick={() => onSelect(n.obj)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: B.bg, borderRadius: 6, cursor: "pointer", border: `1px solid ${B.border}` }}>
                <TypeIcon type={n.obj.type} color={c.color} size={13} />
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: fs(11), fontWeight: 600, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.obj.name}</div>
                  <div style={{ fontSize: fs(9), color: c.color }}>{c.label}</div>
                </div>
                <span style={{ fontSize: fs(10), color: B.primary, fontWeight: 700 }}>{n.value}</span>
              </div>
            );
          }).filter(Boolean)}
        </div>
      </div>

      {/* Custom Panels */}
      <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5 }}>Custom Panels</div>
          <button onClick={() => setBuilderOpen(true)} style={{ padding: "7px 18px", borderRadius: 8, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", color: B.white, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 }}>+ Add Panel</button>
        </div>
        {widgets.length === 0 ? (
          <div style={{ padding: 36, textAlign: "center", background: B.bgCard, borderRadius: 10, border: `1px dashed ${B.border}`, color: B.textDim, fontSize: fs(12), lineHeight: 1.6 }}>
            No custom panels yet.<br />
            <span style={{ color: B.text, fontWeight: 600 }}>+ Add Panel</span> to create filtered views like "Russian Threat Actors" or "Active Ransomware."
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {widgets.map(renderCustomWidget)}
          </div>
        )}
      </div>

      {builderOpen && <WidgetBuilder onSave={addWidget} onClose={() => setBuilderOpen(false)} allLabels={allLabels} allTypes={allTypes} />}
      {editingWidget && <WidgetBuilder initial={editingWidget} onSave={cfg => updateWidget(editingWidget.id, cfg)} onClose={() => setEditingWidget(null)} allLabels={allLabels} allTypes={allTypes} />}
    </div>
  );
}

// ─── MITRE Heatmap ───
const MITRE_TACTICS = ["Reconnaissance","Resource Development","Initial Access","Execution","Persistence","Privilege Escalation","Defense Evasion","Credential Access","Discovery","Lateral Movement","Collection","C2","Exfiltration","Impact"];
const TACTIC_MAP = {"T1595":"Reconnaissance","T1592":"Reconnaissance","T1589":"Reconnaissance","T1590":"Reconnaissance","T1591":"Reconnaissance","T1598":"Reconnaissance","T1597":"Reconnaissance","T1596":"Reconnaissance","T1593":"Reconnaissance","T1594":"Reconnaissance","T1583":"Resource Development","T1584":"Resource Development","T1587":"Resource Development","T1588":"Resource Development","T1585":"Resource Development","T1586":"Resource Development","T1608":"Resource Development","T1189":"Initial Access","T1190":"Initial Access","T1133":"Initial Access","T1200":"Initial Access","T1566":"Initial Access","T1091":"Initial Access","T1195":"Initial Access","T1199":"Initial Access","T1078":"Initial Access","T1059":"Execution","T1609":"Execution","T1610":"Execution","T1203":"Execution","T1559":"Execution","T1106":"Execution","T1053":"Execution","T1129":"Execution","T1072":"Execution","T1569":"Execution","T1204":"Execution","T1047":"Execution","T1098":"Persistence","T1197":"Persistence","T1547":"Persistence","T1037":"Persistence","T1176":"Persistence","T1554":"Persistence","T1136":"Persistence","T1543":"Persistence","T1546":"Persistence","T1574":"Persistence","T1525":"Persistence","T1137":"Persistence","T1542":"Persistence","T1505":"Persistence","T1548":"Defense Evasion","T1134":"Defense Evasion","T1622":"Defense Evasion","T1140":"Defense Evasion","T1006":"Defense Evasion","T1480":"Defense Evasion","T1211":"Defense Evasion","T1222":"Defense Evasion","T1564":"Defense Evasion","T1562":"Defense Evasion","T1070":"Defense Evasion","T1036":"Defense Evasion","T1556":"Defense Evasion","T1112":"Defense Evasion","T1027":"Defense Evasion","T1055":"Defense Evasion","T1110":"Credential Access","T1555":"Credential Access","T1212":"Credential Access","T1187":"Credential Access","T1606":"Credential Access","T1056":"Credential Access","T1557":"Credential Access","T1111":"Credential Access","T1621":"Credential Access","T1040":"Credential Access","T1003":"Credential Access","T1528":"Credential Access","T1558":"Credential Access","T1539":"Credential Access","T1552":"Credential Access","T1087":"Discovery","T1010":"Discovery","T1217":"Discovery","T1580":"Discovery","T1538":"Discovery","T1526":"Discovery","T1619":"Discovery","T1613":"Discovery","T1046":"Discovery","T1135":"Discovery","T1201":"Discovery","T1120":"Discovery","T1069":"Discovery","T1057":"Discovery","T1012":"Discovery","T1018":"Discovery","T1518":"Discovery","T1082":"Discovery","T1016":"Discovery","T1049":"Discovery","T1033":"Discovery","T1007":"Discovery","T1124":"Discovery","T1497":"Discovery","T1210":"Lateral Movement","T1534":"Lateral Movement","T1570":"Lateral Movement","T1563":"Lateral Movement","T1021":"Lateral Movement","T1080":"Lateral Movement","T1550":"Lateral Movement","T1560":"Collection","T1123":"Collection","T1119":"Collection","T1115":"Collection","T1530":"Collection","T1602":"Collection","T1213":"Collection","T1005":"Collection","T1039":"Collection","T1025":"Collection","T1074":"Collection","T1114":"Collection","T1185":"Collection","T1113":"Collection","T1125":"Collection","T1071":"C2","T1092":"C2","T1132":"C2","T1001":"C2","T1568":"C2","T1573":"C2","T1008":"C2","T1105":"C2","T1104":"C2","T1095":"C2","T1571":"C2","T1572":"C2","T1090":"C2","T1219":"C2","T1205":"C2","T1102":"C2","T1020":"Exfiltration","T1030":"Exfiltration","T1048":"Exfiltration","T1041":"Exfiltration","T1011":"Exfiltration","T1052":"Exfiltration","T1567":"Exfiltration","T1029":"Exfiltration","T1537":"Exfiltration","T1531":"Impact","T1485":"Impact","T1486":"Impact","T1565":"Impact","T1491":"Impact","T1561":"Impact","T1499":"Impact","T1495":"Impact","T1490":"Impact","T1498":"Impact","T1496":"Impact","T1489":"Impact","T1529":"Impact"};

function MITREView({ objects, onSelect }) {
  const attackPatterns = objects.filter(o => o.type === "attack-pattern");
  const techniques = attackPatterns.map(ap => {
    const mitreRef = (ap.external_references || []).find(r => r.source_name === "mitre-attack");
    const tcode = mitreRef?.external_id || null;
    const base = tcode ? tcode.split(".")[0] : null;
    const tactic = base ? TACTIC_MAP[base] || "Other" : "Other";
    return { obj: ap, tcode, tactic };
  });

  const byTactic = {};
  techniques.forEach(t => { if (!byTactic[t.tactic]) byTactic[t.tactic] = []; byTactic[t.tactic].push(t); });
  const orderedTactics = [...MITRE_TACTICS.filter(t => byTactic[t]), ...(byTactic["Other"] ? ["Other"] : [])];

  if (!techniques.length) return (
    <div style={{ padding: 60, textAlign: "center", color: B.textDim }}>
      <div style={{ fontSize: fs(15), fontWeight: 600, marginBottom: 8 }}>No MITRE ATT&CK techniques found</div>
      <div style={{ fontSize: fs(12) }}>Load a bundle with attack-pattern objects that include mitre-attack external references</div>
    </div>
  );

  return (
    <div style={{ padding: 28, overflow: "auto", height: "100%" }}>
      <div style={{ marginBottom: 24, display: "flex", alignItems: "baseline", gap: 14 }}>
        <div style={{ fontSize: fs(20), fontWeight: 800, color: B.text }}>MITRE ATT&CK Coverage</div>
        <div style={{ fontSize: fs(13), color: B.textMuted }}>{techniques.length} technique{techniques.length !== 1 ? "s" : ""} · {orderedTactics.length} tactic{orderedTactics.length !== 1 ? "s" : ""}</div>
      </div>

      <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 24 }}>
        {orderedTactics.map(tacticName => (
          <div key={tacticName} style={{ minWidth: 160, flexShrink: 0 }}>
            <div style={{ fontSize: fs(11), color: B.white, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5, textAlign: "center", padding: "7px 6px", background: tacticName === "Other" ? B.textDim : B.primaryDark, borderRadius: 5 }}>{tacticName}</div>
            {byTactic[tacticName].map(({ obj, tcode }) => (
              <div key={obj.id} onClick={() => onSelect(obj)} title={obj.name} style={{ background: B.primary, border: `1px solid ${B.primaryDark}`, borderRadius: 4, padding: "6px 8px", marginBottom: 3, cursor: "pointer" }}>
                <div style={{ color: B.white, fontWeight: 800, fontSize: fs(11) }}>{tcode || "–"}</div>
                <div style={{ color: B.white, opacity: 0.9, fontSize: fs(11), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{obj.name}</div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: fs(12), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 16 }}>All Techniques</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {techniques.map(({ obj, tcode, tactic }) => (
            <div key={obj.id} onClick={() => onSelect(obj)} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: "14px 16px", cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                {tcode && <code style={{ fontSize: fs(12), color: B.primary, fontWeight: 800 }}>{tcode}</code>}
                <span style={{ fontSize: fs(11), color: B.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{tactic}</span>
              </div>
              <div style={{ fontSize: fs(14), fontWeight: 700, color: B.text, marginBottom: 4 }}>{obj.name}</div>
              {obj.description && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5 }}>{obj.description.slice(0, 110)}…</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Cases View ───
function CasesView({ objects, relationships, markingDefs, onSelect }) {
  const cases = objects.filter(o => o.type === "case-incident" || o.type === "x-case" || o.type === "grouping");
  const reports = objects.filter(o => o.type === "report");
  const allCases = [...cases, ...reports];
  if (!allCases.length) return (
    <div style={{ padding: 60, textAlign: "center", color: B.textDim }}>
      <div style={{ fontSize: fs(15), fontWeight: 600, marginBottom: 8 }}>No cases or reports</div>
      <div style={{ fontSize: fs(12) }}>Cases and reports from your STIX bundle will appear here</div>
    </div>
  );
  return (
    <div style={{ padding: 20, overflow: "auto", height: "100%" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {allCases.map(c => {
          const cfg = getConf(c.type);
          const refs = (c.object_refs || []).map(id => objects.find(o => o.id === id)).filter(Boolean);
          const markings = getMarkings(c, markingDefs);
          const tlp = markings[0];
          const conf = getConfidenceInfo(c.confidence);
          return (
            <div key={c.id} onClick={() => onSelect(c)} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 10, padding: 16, cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <TypeIcon type={c.type} color={cfg.color} size={14} />
                <span style={{ fontSize: fs(10), color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
                {tlp && <span style={{ marginLeft: "auto", fontSize: fs(9), padding: "1px 6px", borderRadius: 4, background: tlp.color + "22", color: tlp.color, fontWeight: 700 }}>{tlp.name}</span>}
                {conf && <span style={{ fontSize: fs(9), padding: "1px 6px", borderRadius: 4, background: conf.color + "22", color: conf.color, fontWeight: 700 }}>{conf.label}</span>}
              </div>
              <div style={{ fontSize: fs(14), fontWeight: 700, color: B.text, marginBottom: 6 }}>{c.name}</div>
              {c.published && <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 6 }}>Published {new Date(c.published).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>}
              {c.description && <p style={{ fontSize: fs(11), color: B.textMuted, lineHeight: 1.5, margin: "0 0 10px 0" }}>{c.description.slice(0, 120)}…</p>}
              {c.labels?.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>{c.labels.map(l => <span key={l} style={{ fontSize: fs(9), padding: "2px 7px", borderRadius: 8, background: B.accent + "18", color: B.accent }}>{l}</span>)}</div>}
              {refs.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {refs.slice(0, 6).map(obj => { const rc = getConf(obj.type); return <span key={obj.id} onClick={e => { e.stopPropagation(); onSelect(obj); }} style={{ fontSize: fs(9), padding: "2px 7px", borderRadius: 8, background: rc.color + "12", color: rc.color, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}><TypeIcon type={obj.type} color={rc.color} size={9} />{obj.name}</span>; })}
                  {refs.length > 6 && <span style={{ fontSize: fs(9), color: B.textDim }}>+{refs.length - 6} more</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Autocomplete Input ───
function AutocompleteInput({ value, onChange, onSelect, suggestions, placeholder, style, inputStyle, prefix }) {
  const [focused, setFocused] = useState(false);
  const q = value.trim().toLowerCase();
  const matches = q.length === 0 ? [] : suggestions
    .filter(s => s.toLowerCase().includes(q) && s.toLowerCase() !== q)
    .slice(0, 8);
  const show = focused && matches.length > 0;
  return (
    <div style={{ position: "relative", ...style }}>
      {prefix}
      <input value={value} onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        placeholder={placeholder} style={inputStyle} />
      {show && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 300,
          minWidth: "100%", background: B.bgCard, border: `1px solid ${B.border}`,
          borderRadius: 8, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
          {matches.map((s, i) => (
            <div key={s}
              onMouseDown={e => { e.preventDefault(); onSelect(s); setFocused(false); }}
              style={{ padding: "8px 12px", fontSize: fs(12), cursor: "pointer", color: B.text,
                borderBottom: i < matches.length - 1 ? `1px solid ${B.border}` : "none",
                background: "transparent", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = B.bgPanel}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Investigation View ───
function InvestigationView({ objects, markingDefs, invName, setInvName, invStatus, setInvStatus, invNotes, setInvNotes, pinnedIds, evidenceStatuses = {}, onSetEvidenceStatus, onUnpin, analystLog, setAnalystLog, customClassification, setCustomClassification, onSave, onShare, onSelect, user }) {
  const [logInput, setLogInput] = useState("");
  const [dragEvidenceId, setDragEvidenceId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const pinnedObjs = [...pinnedIds].map(id => objects.find(o => o.id === id)).filter(Boolean);
  const statusColors = { active: "#16a34a", complete: B.accent, archived: B.textDim };
  const evidenceColumns = [
    { key: "lead", label: "Lead", color: B.primary },
    { key: "confirmed", label: "Confirmed", color: "#16a34a" },
    { key: "rejected", label: "Rejected", color: "#ef4444" },
    { key: "review", label: "Needs Review", color: "#f59e0b" },
  ];
  const evidenceByColumn = evidenceColumns.reduce((acc, col) => ({ ...acc, [col.key]: [] }), {});
  pinnedObjs.forEach(obj => {
    const status = evidenceStatuses[obj.id] || "lead";
    (evidenceByColumn[status] || evidenceByColumn.lead).push(obj);
  });

  const addLogEntry = () => {
    if (!logInput.trim()) return;
    setAnalystLog(prev => [{ id: String(Date.now() + Math.random()), text: logInput.trim(), ts: new Date().toISOString() }, ...prev]);
    setLogInput("");
  };

  return (
    <div style={{ padding: 28, overflow: "auto", height: "100%", display: "flex", flexDirection: "column", gap: 24, boxSizing: "border-box" }}>
      {/* Header row */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={invName}
          onChange={e => setInvName(e.target.value)}
          placeholder="Untitled Investigation…"
          style={{ flex: 1, minWidth: 200, fontSize: fs(22), fontWeight: 800, background: "transparent", border: "none", borderBottom: `2px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", outline: "none", paddingBottom: 4 }}
        />
        <div style={{ display: "flex", gap: 4 }}>
          {["active", "complete", "archived"].map(s => (
            <button key={s} onClick={() => setInvStatus(s)} style={{ fontSize: fs(10), padding: "4px 12px", borderRadius: 20, border: `1px solid ${invStatus === s ? statusColors[s] : B.border}`, background: invStatus === s ? statusColors[s] + "22" : "transparent", color: invStatus === s ? statusColors[s] : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 600, textTransform: "capitalize" }}>{s}</button>
          ))}
        </div>
        <AutocompleteInput
          value={customClassification}
          onChange={e => setCustomClassification(e.target.value)}
          onSelect={s => setCustomClassification(s)}
          suggestions={["TLP:CLEAR", "TLP:GREEN", "TLP:AMBER", "TLP:AMBER+STRICT", "TLP:RED"]}
          placeholder="TLP / Classification…"
          style={{ width: 170 }}
          inputStyle={{ fontSize: fs(11), padding: "5px 10px", borderRadius: 6, background: B.bgCard, border: `1px solid ${B.border}`, color: B.primary, fontFamily: "'Satoshi', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" }}
        />
        <button onClick={onSave} disabled={!user} style={{ padding: "7px 20px", borderRadius: 8, background: user ? `linear-gradient(135deg, ${B.primary}, ${B.accent})` : B.bgCard, border: "none", color: B.white, cursor: user ? "pointer" : "not-allowed", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: fs(12), opacity: user ? 1 : 0.5 }}>
          {user ? "Save" : "Login to save"}
        </button>
        {user && <button onClick={onShare} style={{ padding: "7px 20px", borderRadius: 8, background: "transparent", border: `1px solid ${B.accent}`, color: B.accent, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: fs(12) }}>Share</button>}
      </div>

      {/* Two-column body */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, flex: 1, minHeight: 0 }}>
        {/* Notes */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5 }}>Notes</div>
          <textarea
            value={invNotes}
            onChange={e => setInvNotes(e.target.value)}
            placeholder="Write your investigation notes here — findings, hypotheses, context…"
            style={{ flex: 1, minHeight: 320, padding: 16, borderRadius: 10, background: B.bgCard, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(13), resize: "none", outline: "none", lineHeight: 1.7, boxSizing: "border-box" }}
          />
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20, overflow: "auto" }}>
          {/* Evidence Board */}
          <div>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>
              Evidence Board <span style={{ fontWeight: 400 }}>({pinnedObjs.length})</span>
            </div>
            {pinnedObjs.length === 0 ? (
              <div style={{ padding: 20, borderRadius: 10, background: B.bgCard, border: `1px dashed ${B.border}`, color: B.textDim, fontSize: fs(12), textAlign: "center", lineHeight: 1.6 }}>
                Open any object in the detail panel<br />and click <strong style={{ color: B.accent }}>Pin to Investigation</strong>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(150px, 1fr))", gap: 10, overflowX: "auto", paddingBottom: 2 }}>
                {evidenceColumns.map(col => (
                  <div
                    key={col.key}
                    onDragOver={e => { e.preventDefault(); setDragOverColumn(col.key); }}
                    onDragLeave={() => setDragOverColumn(null)}
                    onDrop={e => {
                      e.preventDefault();
                      const id = e.dataTransfer.getData("text/plain") || dragEvidenceId;
                      if (id) onSetEvidenceStatus?.(id, col.key);
                      setDragEvidenceId(null);
                      setDragOverColumn(null);
                    }}
                    style={{ minHeight: 180, background: dragOverColumn === col.key ? col.color + "12" : B.bgCard, border: `1px solid ${dragOverColumn === col.key ? col.color : B.border}`, borderRadius: 10, padding: 10, transition: "background 0.12s, border 0.12s" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0 }} />
                      <span style={{ fontSize: fs(11), color: B.text, fontWeight: 800 }}>{col.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: fs(10), color: B.textDim }}>{evidenceByColumn[col.key].length}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {evidenceByColumn[col.key].map(obj => {
                        const c = getConf(obj.type);
                        const mark = getMarkings(obj, markingDefs)[0];
                        return (
                          <div
                            key={obj.id}
                            draggable
                            onDragStart={e => { setDragEvidenceId(obj.id); e.dataTransfer.setData("text/plain", obj.id); e.dataTransfer.effectAllowed = "move"; }}
                            onDragEnd={() => { setDragEvidenceId(null); setDragOverColumn(null); }}
                            onClick={() => onSelect(obj)}
                            style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 10px", borderRadius: 8, background: B.bg, border: `1px solid ${c.color}44`, cursor: "grab", opacity: dragEvidenceId === obj.id ? 0.55 : 1 }}
                          >
                            <TypeIcon type={obj.type} color={c.color} size={12} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: fs(11), color: B.text, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{getDisplayName(obj)}</div>
                              <div style={{ fontSize: fs(9), color: c.color }}>{c.label}</div>
                              {mark && <span style={{ display: "inline-block", marginTop: 4, fontSize: fs(8), padding: "1px 5px", borderRadius: 4, background: mark.color + "22", color: mark.color, fontWeight: 700 }}>{mark.name}</span>}
                            </div>
                            <button onClick={e => { e.stopPropagation(); onUnpin(obj.id); }} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(15), lineHeight: 1, flexShrink: 0 }}>×</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Analyst Log */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            <div style={{ fontSize: fs(10), color: B.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5 }}>Analyst Log</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                value={logInput}
                onChange={e => setLogInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addLogEntry()}
                placeholder="Add a log entry and press Enter…"
                style={{ flex: 1, padding: "7px 12px", borderRadius: 8, background: B.bgCard, border: `1px solid ${B.border}`, color: B.text, fontFamily: "'Satoshi', sans-serif", fontSize: fs(12), outline: "none" }}
              />
              <button onClick={addLogEntry} style={{ padding: "7px 14px", borderRadius: 8, background: B.accent, border: "none", color: B.white, cursor: "pointer", fontSize: fs(16), fontFamily: "'Satoshi', sans-serif", fontWeight: 700 }}>+</button>
            </div>
            {analystLog.length === 0 ? (
              <div style={{ fontSize: fs(11), color: B.textDim, textAlign: "center", padding: 16 }}>No log entries yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {analystLog.map(entry => (
                  <div key={entry.id} style={{ display: "flex", gap: 8, padding: "9px 12px", borderRadius: 8, background: B.bgCard, border: `1px solid ${B.border}`, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: fs(9), color: B.textDim, marginBottom: 3 }}>{new Date(entry.ts).toLocaleString()}</div>
                      <div style={{ fontSize: fs(12), color: B.text, lineHeight: 1.5 }}>{entry.text}</div>
                    </div>
                    <button onClick={() => setAnalystLog(prev => prev.filter(e => e.id !== entry.id))} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(16), lineHeight: 1, flexShrink: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Relationship Detection ───
function extractNameTokens(obj) {
  const tokens = new Set();
  const add = (str) => { if (!str) return; str.toLowerCase().split(/[\]\s,;:()[/\\]+/).filter(t => t.length > 3).forEach(t => tokens.add(t)); };
  add(obj.name);
  add(obj.display_name);
  if (obj.aliases) obj.aliases.forEach(a => add(a));
  if (obj.pattern) { const m = obj.pattern.match(/'([^']+)'/g); if (m) m.forEach(v => tokens.add(v.slice(1, -1).toLowerCase())); }
  return tokens;
}

function findBundleConnections(newObjs, existingObjs) {
  const SKIP_TYPES = new Set(["relationship", "bundle", "marking-definition", "identity"]);
  const connections = [];
  const existing = existingObjs.filter(o => !SKIP_TYPES.has(o.type));
  const incoming = newObjs.filter(o => !SKIP_TYPES.has(o.type));
  const existingRelPairs = new Set(existingObjs.filter(o => o.type === "relationship").map(r => `${r.source_ref}|${r.target_ref}`));

  for (const nObj of incoming) {
    const nTokens = extractNameTokens(nObj);
    if (nTokens.size === 0) continue;
    for (const eObj of existing) {
      if (eObj.id === nObj.id) continue;
      const eTokens = extractNameTokens(eObj);
      const shared = [...nTokens].filter(t => eTokens.has(t));
      if (shared.length === 0) continue;
      const pairFwd = `${nObj.id}|${eObj.id}`;
      const pairBwd = `${eObj.id}|${nObj.id}`;
      if (existingRelPairs.has(pairFwd) || existingRelPairs.has(pairBwd)) continue;
      const alreadyLogged = connections.some(c => (c.sourceId === nObj.id && c.targetId === eObj.id) || (c.sourceId === eObj.id && c.targetId === nObj.id));
      if (alreadyLogged) continue;
      connections.push({ sourceId: nObj.id, sourceName: nObj.name || nObj.id, sourceType: nObj.type, targetId: eObj.id, targetName: eObj.name || eObj.id, targetType: eObj.type, reason: shared.slice(0, 2).join(", ") });
    }
    for (let i = 0; i < incoming.length; i++) {
      const other = incoming[i];
      if (other.id === nObj.id) continue;
      const oTokens = extractNameTokens(other);
      const shared = [...nTokens].filter(t => oTokens.has(t));
      if (shared.length === 0) continue;
      const alreadyLogged = connections.some(c => (c.sourceId === nObj.id && c.targetId === other.id) || (c.sourceId === other.id && c.targetId === nObj.id));
      if (alreadyLogged) continue;
      connections.push({ sourceId: nObj.id, sourceName: nObj.name || nObj.id, sourceType: nObj.type, targetId: other.id, targetName: other.name || other.id, targetType: other.type, reason: shared.slice(0, 2).join(", "), isNew: true });
    }
  }
  return connections;
}

// ─── Main ───
export default function App() {
  const [page, setPage] = useState(() => (parseKbHash() ? "knowledge" : parseDashHash() ? "dashboards" : parseGraphHash() ? "graph" : parseCreateHash() ? "create" : parseCommunityHash() ? "community" : "home"));

  // Knowledge Base profiles live at #/kb/... — follow back/forward and pasted links,
  // and drop the hash when leaving the section so a refresh lands where the user is.
  useEffect(() => {
    const onHash = () => { if (parseKbHash()) setPage("knowledge"); else if (parseDashHash()) setPage("dashboards"); else if (parseGraphHash()) setPage("graph"); else if (parseCreateHash()) setPage("create"); else if (parseCommunityHash()) setPage("community"); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if ((page !== "knowledge" && parseKbHash()) || (page !== "dashboards" && parseDashHash()) || (page !== "graph" && parseGraphHash()) || (page !== "create" && parseCreateHash()) || (page !== "community" && parseCommunityHash())) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [page]);
  const [theme, setTheme] = useState(() => localStorage.getItem("ih-theme") || "dark");
  const [fontScale, setFontScale] = useState(() => parseFloat(localStorage.getItem("ih-font-scale") || "1.1"));
  const activeB = theme === "light" ? LIGHT_B : DARK_B;
  B = activeB; // reassign module-level B so all inline components pick up the current theme
  const activeFs = (size) => Math.round(size * fontScale);
  const handleThemeChange = (t) => { setTheme(t); localStorage.setItem("ih-theme", t); };
  const handleFontSizeChange = (scale) => { setFontScale(scale); localStorage.setItem("ih-font-scale", String(scale)); };
  const [pendingBundle, setPendingBundle] = useState(null);
  const [bundle, setBundle] = useState(SAMPLE_BUNDLE);
  const [detailObj, setDetailObj] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTypes, setActiveTypes] = useState(new Set());
  const [search, setSearch] = useState("");
  const [view, setView] = useState("graph");
  const [user, setUser] = useState(null);
  const [lockdown, setLockdown] = useState(false);
  LOCKDOWN = lockdown;
  const [analyzeInfo, setAnalyzeInfo] = useState(null);
  ANALYZE_LOCKED = lockdown && !analyzeInfo?.open;
  ANALYZE_MAX_MB = analyzeInfo?.maxMB || 10;
  ANALYZE_PER_HOUR = analyzeInfo?.perHour || 20;
  const [authReady, setAuthReady] = useState(false);
  const [savedBundles, setSavedBundles] = useState([]);
  const [savedInvestigations, setSavedInvestigations] = useState([]);
  const [receivedInvestigations, setReceivedInvestigations] = useState([]);
  const [sharingInvestigation, setSharingInvestigation] = useState(null);
  const [customClassification, setCustomClassification] = useState("");
  const [invName, setInvName] = useState("");
  const [invStatus, setInvStatus] = useState("active");
  const [invNotes, setInvNotes] = useState("");
  const [pinnedIds, setPinnedIds] = useState(new Set());
  const [evidenceStatuses, setEvidenceStatuses] = useState({});
  const [analystLog, setAnalystLog] = useState([]);
  const [currentInvestigationId, setCurrentInvestigationId] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [pcapSession, setPcapSession] = useState({
    packets: [],
    stats: null,
    total: 0,
    analysis: null,
    suggestions: null,
    selectedSuggestionIds: [],
    page: 0,
  });
  const updatePcapSession = useCallback((updates) => setPcapSession(prev => ({ ...prev, ...updates })), []);
  const resetPcapSession = useCallback(() => setPcapSession({ packets: [], stats: null, total: 0, analysis: null, suggestions: null, selectedSuggestionIds: [], page: 0 }), []);
  const [bundleConnections, setBundleConnections] = useState(null);
  const [ghostOpen, setGhostOpen] = useState(false);
  const [ghostOpacity, setGhostOpacity] = useState(0.35);
  const [ghostMode, setGhostMode] = useState("both");
  const [ghostPreviewed, setGhostPreviewed] = useState(null);
  const [addedGhostIds, setAddedGhostIds] = useState(new Set());
  const [addedGhostMeta, setAddedGhostMeta] = useState(new Map());
  const [communityGhosts, setCommunityGhosts] = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const fileRef = useRef(null);
  const bundleHistory = useRef([]);

  const FAKE_GHOSTS = [
    { id: "threat-actor--fg1", type: "threat-actor",   name: "APT28 (Fancy Bear)",       fromBundle: "Operation Aurora", description: "Russian GRU-linked actor, active since 2004. Known for credential phishing, spearphishing, and destructive wiper deployments against NATO targets." },
    { id: "malware--fg2",      type: "malware",         name: "Cobalt Strike Beacon",     fromBundle: "RedTeam IOCs Q4",  description: "Commercial adversary simulation tool widely abused. Beacon payload communicates over HTTP/S with malleable C2 profiles." },
    { id: "indicator--fg3",    type: "indicator",       name: "185.220.101.45 C2",        fromBundle: "ThreatFox Nov-24", description: "IPv4 address observed as Cobalt Strike C2 in multiple campaigns. Last seen 2024-11-14." },
    { id: "campaign--fg4",     type: "campaign",        name: "Operation Ghost Cloak",    fromBundle: "Financial Intel",  description: "Multi-stage campaign targeting SWIFT infrastructure. First observed Q3 2024." },
    { id: "vulnerability--fg5",type: "vulnerability",   name: "CVE-2024-3400",            fromBundle: "KEV Tracker",      description: "Critical PAN-OS command injection zero-day, CVSS 10.0. Exploited by UTA0218 before patch." },
    { id: "domain-name--fg6",  type: "domain-name",     name: "update-cdn-secure.net",    fromBundle: "RedTeam IOCs Q4",  description: "Malware staging domain registered 2024-10-02. Resolves to Hetzner VPS infrastructure." },
    { id: "intrusion-set--fg7",type: "intrusion-set",   name: "Sandworm Team",            fromBundle: "Operation Aurora", description: "Russian GRU Unit 74455. Attributed to NotPetya, Industroyer, and Prestige wiper campaigns." },
    { id: "attack-pattern--fg8",type:"attack-pattern",  name: "T1566 — Phishing",         fromBundle: "MITRE Mappings",   description: "Initial access via spearphishing. Subtype T1566.001 uses malicious attachments; T1566.002 uses links." },
    { id: "tool--fg9",         type: "tool",            name: "Mimikatz",                 fromBundle: "Credential Tools", description: "Open-source post-exploitation tool for extracting Windows credentials from LSASS memory." },
    { id: "identity--fg10",    type: "identity",        name: "NATO Headquarters",        fromBundle: "Targets 2024",     description: "Target identity observed in multiple APT28 spearphishing campaigns, Brussels, Belgium." },
  ];
  const ghostRelType = (ghostType, bundleTypes) => {
    const bt = new Set(bundleTypes);
    if (ghostType === "threat-actor")  return bt.has("malware") || bt.has("tool") ? "uses" : bt.has("campaign") ? "attributed-to" : "related-to";
    if (ghostType === "malware")       return bt.has("indicator") ? "indicates" : "related-to";
    if (ghostType === "indicator")     return "indicates";
    if (ghostType === "campaign")      return bt.has("threat-actor") || bt.has("intrusion-set") ? "attributed-to" : "related-to";
    if (ghostType === "vulnerability") return bt.has("attack-pattern") ? "targets" : "related-to";
    if (ghostType === "attack-pattern")return bt.has("malware") ? "uses" : "related-to";
    if (ghostType === "tool")          return bt.has("threat-actor") || bt.has("intrusion-set") ? "uses" : "related-to";
    return "related-to";
  };

  const addGhostToBundle = (ghost) => {
    const bundleTypes = nonRels.map(o => o.type);
    const relType = ghostRelType(ghost.type, bundleTypes);
    const prefTypes = { "threat-actor": ["malware","campaign","tool","attack-pattern"], "malware": ["threat-actor","intrusion-set","indicator"], "indicator": ["malware","threat-actor","attack-pattern"], "vulnerability": ["attack-pattern","malware"], "campaign": ["threat-actor","intrusion-set"], "tool": ["threat-actor","intrusion-set"], "attack-pattern": ["malware","tool","campaign"] };
    const pref = prefTypes[ghost.type] || [];
    const target = nonRels.find(o => pref.includes(o.type)) || nonRels[0];
    const now = new Date().toISOString();
    const { fromBundle, ...stixObj } = ghost;
    const relId = target ? `relationship--fg-${Date.now()}` : null;
    const newObjects = [{ ...stixObj, spec_version: "2.1", created: now, modified: now }];
    if (relId) newObjects.push({ type: "relationship", id: relId, spec_version: "2.1", created: now, modified: now, relationship_type: relType, source_ref: ghost.id, target_ref: target.id, description: `Auto-linked ghost — imported from "${fromBundle}"` });
    handleAddToBundle(newObjects);
    setAddedGhostIds(prev => new Set([...prev, ghost.id]));
    setAddedGhostMeta(prev => new Map([...prev, [ghost.id, { relId }]]));
    setGhostPreviewed(null);
  };

  const handleRevertGhost = useCallback((ghostId) => {
    const meta = addedGhostMeta.get(ghostId);
    const removeIds = new Set([ghostId, meta?.relId].filter(Boolean));
    setBundle(prev => ({ ...prev, objects: (prev.objects || []).filter(o => !removeIds.has(o.id)) }));
    setAddedGhostIds(prev => { const n = new Set(prev); n.delete(ghostId); return n; });
    setAddedGhostMeta(prev => { const n = new Map(prev); n.delete(ghostId); return n; });
    setSelected(s => (s && s.id === ghostId ? null : s));
  }, [addedGhostMeta]);

  const objects = bundle.objects || [];
  const rels = objects.filter(o => o.type === "relationship");
  const nonRels = objects.filter(o => o.type !== "relationship");
  const visibleGhosts = [...FAKE_GHOSTS, ...communityGhosts].filter(g => !addedGhostIds.has(g.id) && !nonRels.some(o => o.id === g.id));
  const counts = useMemo(() => { const c = {}; nonRels.forEach(o => { c[o.type] = (c[o.type] || 0) + 1; }); return c; }, [nonRels]);
  const toggleType = useCallback((t) => setActiveTypes(p => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; }), []);
  const [activeLabels, setActiveLabels] = useState(new Set());
  const [dateWindow, setDateWindow] = useState(null);
  const allLabels = useMemo(() => { const s = new Set(); nonRels.forEach(o => (o.labels || []).forEach(l => s.add(l))); return [...s].sort(); }, [nonRels]);
  const toggleLabel = useCallback((l) => setActiveLabels(p => { const n = new Set(p); n.has(l) ? n.delete(l) : n.add(l); return n; }), []);
  const baseFiltered = useMemo(() => nonRels.filter(o => {
    if (activeTypes.size > 0 && !activeTypes.has(o.type)) return false;
    if (activeLabels.size > 0 && !(o.labels || []).some(l => activeLabels.has(l))) return false;
    if (search && !(o.name || "").toLowerCase().includes(search.toLowerCase()) && !(o.description || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [nonRels, activeTypes, activeLabels, search]);
  const filtered = useMemo(() => {
    if (!dateWindow) return baseFiltered;
    const start = new Date(dateWindow.start).getTime();
    const end = new Date(dateWindow.end).getTime();
    return baseFiltered.filter(o => {
      const d = getObjectDate(o);
      if (!d) return false;
      const t = d.getTime();
      return t >= start && t <= end;
    });
  }, [baseFiltered, dateWindow]);
  const graphObjects = useMemo(() => [...filtered.filter(o => o.type !== "relationship"), ...rels], [filtered, rels]);
  const loadFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.type === "bundle" && d.objects) {
          setBundle(d); setSelected(null); setActiveTypes(new Set()); setActiveLabels(new Set());
          setDateWindow(null); setSearch(""); setPinnedIds(new Set()); setEvidenceStatuses({});
          setAddedGhostIds(new Set()); setAddedGhostMeta(new Map());
          setCurrentInvestigationId(null); setInvName(""); setInvStatus("active"); setInvNotes(""); setAnalystLog([]);
          setSaveStatus("");
        } else alert("Not a valid STIX bundle");
      } catch { alert("Invalid JSON"); }
    };
    r.readAsText(f);
  };
  const sel = useCallback((o) => setSelected(o), []);
  const pinObj = useCallback((id) => {
    setPinnedIds(prev => new Set([...prev, id]));
    setEvidenceStatuses(prev => prev[id] ? prev : { ...prev, [id]: "lead" });
  }, []);
  const unpinObj = useCallback((id) => {
    setPinnedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    setEvidenceStatuses(prev => { const n = { ...prev }; delete n[id]; return n; });
  }, []);
  const setEvidenceStatus = useCallback((id, status) => {
    setEvidenceStatuses(prev => ({ ...prev, [id]: status }));
  }, []);

  const updateObjectLabels = useCallback((id, labels) => {
    setBundle(b => {
      bundleHistory.current = [...bundleHistory.current.slice(-9), b];
      return { ...b, objects: (b.objects || []).map(o => o.id === id ? { ...o, labels } : o) };
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        if (bundleHistory.current.length === 0) return;
        const prev = bundleHistory.current[bundleHistory.current.length - 1];
        bundleHistory.current = bundleHistory.current.slice(0, -1);
        setBundle(prev);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const markingDefs = useMemo(() => {
    const defs = {};
    (bundle.objects || []).forEach((o) => {
      if (o.type === "marking-definition") defs[o.id] = o;
    });
    return defs;
  }, [bundle]);

  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);

  useEffect(() => {
    (async () => {
      // Handle email verification redirect from server
      const params = new URLSearchParams(window.location.search);
      if (params.has("verified")) {
        const ok = params.get("verified") === "1";
        const msg = params.get("msg");
        window.history.replaceState({}, "", window.location.pathname);
        setPage("login");
        // Small delay so LoginPage is mounted before we set its state via a ref trick
        setTimeout(() => {
          window.__verifyResult = ok
            ? { info: "Email verified! You can now sign in." }
            : { error: msg || "Verification failed." };
          window.dispatchEvent(new Event("verifyresult"));
        }, 50);
      }

      try {
        const res = await fetch("/api/me", { credentials: "include" });
        const data = await res.json();
        if (data.lockdown) setLockdown(true);
        if (data.analyze) setAnalyzeInfo(data.analyze);
        if (data.user) {
          setUser(data.user);
          await fetchBundles();
          await fetchInvestigations();
          await fetchReceivedInvestigations();
        }
      } catch (e) {
        console.error("Auth fetch failed", e);
      } finally {
        setAuthReady(true);
      }
    })();
  }, []);

  const fetchBundles = async () => {
    try {
      const res = await fetch("/api/bundles", { credentials: "include" });
      if (!res.ok) throw new Error("Could not load bundles.");
      const data = await res.json();
      setSavedBundles(data.bundles || []);
    } catch (e) {
      console.error(e);
      setSavedBundles([]);
    }
  };

  const fetchInvestigations = async () => {
    try {
      const res = await fetch("/api/investigations", { credentials: "include" });
      if (!res.ok) throw new Error("Could not load investigations.");
      const data = await res.json();
      setSavedInvestigations(data.investigations || []);
    } catch (e) {
      console.error(e);
      setSavedInvestigations([]);
    }
  };

  const fetchReceivedInvestigations = async () => {
    try {
      const res = await fetch("/api/investigations/received", { credentials: "include" });
      if (!res.ok) throw new Error("Could not load received investigations.");
      const data = await res.json();
      setReceivedInvestigations(data.received || []);
    } catch (e) {
      console.error(e);
      setReceivedInvestigations([]);
    }
  };

  const handleAuth = async ({ email, password, signup, honeypot, elapsed }) => {
    const url = signup ? "/api/signup" : "/api/login";
    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, honeypot, elapsed }),
      });
    } catch {
      throw new Error("Cannot reach the backend server. Make sure it's running on port 4000 (node server.js).");
    }
    const data = await res.json().catch(() => {
      throw new Error("Cannot reach the backend server. Make sure it's running on port 4000 (node server.js).");
    });
    if (!res.ok) {
      throw new Error(data.error || "Auth failed.");
    }
    if (data.user) {
      setUser(data.user);
      setPage("home");
      await fetchBundles();
      await fetchInvestigations();
      await fetchReceivedInvestigations();
    }
    return data;
  };

  const handleResendVerification = async (email) => {
    const res = await fetch("/api/resend-verification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || "Unable to resend verification email.");
    }
    return data;
  };

  const handleLogout = async () => {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "include" });
    } catch (e) {
      console.error(e);
    }
    setUser(null);
    setSavedBundles([]);
    setSavedInvestigations([]);
    setReceivedInvestigations([]);
    setPage("home");
  };

  const saveBundle = async () => {
    if (!user) {
      setSaveStatus("Login to save bundles.");
      return;
    }
    try {
      const name = prompt("Save bundle as:", bundle.id || "My STIX bundle");
      if (!name) return;
      const eventName = prompt("Event name (optional):", "") || "";
      const classification = prompt("Classification / marking (optional):", customClassification || "") || "";
      if (classification) setCustomClassification(classification);
      const tagsInput = prompt("Tags (comma separated, optional):", "") || "";
      const tags = tagsInput.split(",").map((tag) => tag.trim()).filter(Boolean);
      const res = await fetch("/api/bundles", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, bundle, eventName, tags, classification }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed.");
      setSaveStatus("Bundle saved.");
      await fetchBundles();
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const saveInvestigation = async () => {
    if (!user) {
      setSaveStatus("Login to save investigations.");
      return;
    }
    if (!bundle || !bundle.objects) {
      setSaveStatus("Load a bundle first to save your investigation.");
      return;
    }
    try {
      const name = invName.trim() || prompt("Investigation name:", `Investigation ${new Date().toLocaleDateString()}`);
      if (!name) return;
      if (!invName.trim()) setInvName(name);
      const state = {
        bundle, view, search,
        activeTypes: [...activeTypes],
        activeLabels: [...activeLabels],
        dateWindow,
        selectedId: selected?.id || null,
        classification: customClassification,
        invName: name,
        invStatus,
        invNotes,
        pinnedIds: [...pinnedIds],
        evidenceStatuses,
        analystLog,
      };
      const res = await fetch("/api/investigations", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, notes: invNotes, classification: customClassification, state }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Investigation save failed.");
      setCurrentInvestigationId(data.investigation.id);
      setSaveStatus("Investigation saved.");
      await fetchInvestigations();
      return data.investigation.id;
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const shareCurrentInvestigation = async () => {
    let id = currentInvestigationId;
    if (!id) {
      id = await saveInvestigation();
      if (!id) return;
    }
    const name = invName.trim() || "Untitled Investigation";
    setSharingInvestigation({ id, name });
  };

  const loadSavedBundle = async (bundleId) => {
    try {
      const res = await fetch(`/api/bundles/${bundleId}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load bundle.");
      setBundle(data.bundle.bundle);
      setSelected(null); setActiveTypes(new Set()); setActiveLabels(new Set());
      setDateWindow(null); setSearch(""); setCustomClassification(data.bundle.classification || "");
      setPinnedIds(new Set()); setEvidenceStatuses({});
      setAddedGhostIds(new Set()); setAddedGhostMeta(new Map());
      setCurrentInvestigationId(null); setInvName(""); setInvStatus("active"); setInvNotes(""); setAnalystLog([]);
      setSaveStatus(""); setPage("viewer");
    } catch (e) {
      alert(e.message);
    }
  };

  const loadSavedInvestigation = async (investigationId) => {
    try {
      const res = await fetch(`/api/investigations/${investigationId}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load investigation.");
      const state = data.investigation.state || {};
      setBundle(state.bundle || bundle);
      setView(state.view || "investigation");
      setSearch(state.search || "");
      setActiveTypes(new Set(state.activeTypes || []));
      setActiveLabels(new Set(state.activeLabels || []));
      setDateWindow(state.dateWindow || null);
      setCustomClassification(data.investigation.classification || state.classification || "");
      setInvName(state.invName || data.investigation.name || "");
      setInvStatus(state.invStatus || "active");
      setInvNotes(state.invNotes || data.investigation.notes || "");
      setPinnedIds(new Set(state.pinnedIds || []));
      setEvidenceStatuses(state.evidenceStatuses || {});
      setAnalystLog(state.analystLog || []);
      const selectedObj = (state.selectedId && state.bundle?.objects) ? state.bundle.objects.find((o) => o.id === state.selectedId) : null;
      setSelected(selectedObj || null);
      setPage("viewer");
    } catch (e) {
      alert(e.message);
    }
  };

  const renameBundle = async (bundleId) => {
    const name = prompt("Rename bundle to:");
    if (!name) return;
    try {
      const res = await fetch(`/api/bundles/${bundleId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Rename failed.");
      setSaveStatus("Bundle renamed.");
      await fetchBundles();
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm("Delete this bundle? This cannot be undone.")) return;
    try {
      const res = await fetch(`/api/bundles/${bundleId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed.");
      setSaveStatus("Bundle deleted.");
      await fetchBundles();
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const renameInvestigation = async (investigationId) => {
    const name = prompt("Rename investigation to:");
    if (!name) return;
    try {
      const res = await fetch(`/api/investigations/${investigationId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Rename failed.");
      setSaveStatus("Investigation renamed.");
      await fetchInvestigations();
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const loadReceivedInvestigation = async (shareId) => {
    try {
      const res = await fetch(`/api/investigations/received/${shareId}`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load shared investigation.");
      const state = data.share.state || {};
      setBundle(state.bundle || bundle);
      setView(state.view || "investigation");
      setSearch(state.search || "");
      setActiveTypes(new Set(state.activeTypes || []));
      setActiveLabels(new Set(state.activeLabels || []));
      setDateWindow(state.dateWindow || null);
      setCustomClassification(data.share.classification || state.classification || "");
      setInvName(state.invName || data.share.name || "");
      setInvStatus(state.invStatus || "active");
      setInvNotes(state.invNotes || data.share.notes || "");
      setPinnedIds(new Set(state.pinnedIds || []));
      setEvidenceStatuses(state.evidenceStatuses || {});
      setAnalystLog(state.analystLog || []);
      const selectedObj = (state.selectedId && state.bundle?.objects) ? state.bundle.objects.find((o) => o.id === state.selectedId) : null;
      setSelected(selectedObj || null);
      setPage("viewer");
    } catch (e) {
      alert(e.message);
    }
  };

  const dismissReceivedInvestigation = async (shareId) => {
    if (!window.confirm("Dismiss this shared investigation?")) return;
    try {
      const res = await fetch(`/api/investigations/received/${shareId}`, { method: "DELETE", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not dismiss.");
      await fetchReceivedInvestigations();
    } catch (e) {
      alert(e.message);
    }
  };

  const deleteInvestigation = async (investigationId) => {
    if (!window.confirm("Delete this investigation? This cannot be undone.")) return;
    try {
      const res = await fetch(`/api/investigations/${investigationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed.");
      setSaveStatus("Investigation deleted.");
      await fetchInvestigations();
    } catch (e) {
      setSaveStatus(e.message);
    }
  };

  const createRelationship = useCallback((sourceId, targetId, relType) => {
    const newRel = {
      type: "relationship", spec_version: "2.1",
      id: `relationship--${crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16).padStart(8, "0").slice(-8)}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, "0")}`}`,
      created: new Date().toISOString(), modified: new Date().toISOString(),
      relationship_type: relType, source_ref: sourceId, target_ref: targetId,
    };
    setBundle(b => ({ ...b, objects: [...(b.objects || []), newRel] }));
  }, []);

  const doLoad = useCallback((b) => {
    setBundle(b); setSelected(null); setActiveTypes(new Set()); setActiveLabels(new Set());
    setDateWindow(null); setSearch(""); setPinnedIds(new Set()); setEvidenceStatuses({});
    setAddedGhostIds(new Set()); setAddedGhostMeta(new Map());
    setCurrentInvestigationId(null); setInvName(""); setInvStatus("active"); setInvNotes(""); setAnalystLog([]);
    setSaveStatus(""); setPage("viewer");
  }, []);
  const previewFrom = useRef(null);
  const goPreview = useCallback((b) => {
    previewFrom.current = page === "knowledge" ? window.location.hash : null;
    setPendingBundle(b); setPage("preview");
  }, [page]);
  const leavePreview = () => {
    if (previewFrom.current) window.location.hash = previewFrom.current.replace(/^#/, "");
    else setPage("import");
  };

  const handleAddToBundle = useCallback((newObjs) => {
    const existing = bundle.objects || [];
    const connections = findBundleConnections(newObjs, existing);
    setBundle(prev => ({ ...prev, objects: [...(prev.objects || []), ...newObjs] }));
    setBundleConnections(connections.length > 0 ? connections : null);
    setPage("viewer");
  }, [bundle]);

  const importCommunityGhosts = useCallback((ghosts) => {
    const existingIds = new Set((bundle.objects || []).map(o => o.id));
    const cleanGhosts = ghosts.filter(g => g.id && !existingIds.has(g.id));
    setCommunityGhosts(prev => {
      const byId = new Map(prev.map(g => [g.id, g]));
      cleanGhosts.forEach(g => byId.set(g.id, g));
      return [...byId.values()];
    });
    setGhostMode("both");
    setGhostOpen(true);
    setPage("viewer");
  }, [bundle]);

  if (!authReady) return <div style={{ color: activeB.text, background: activeB.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading…</div>;
  if (page === "home") return <HomePage user={user} lockdown={lockdown} onLaunch={() => setPage("viewer")} onLogin={() => setPage("login")} onBundles={() => setPage("bundles")} onLogout={handleLogout} onPcap={() => setPage("pcap")} onResumePcap={() => setPage("pcap")} onNavigate={setPage} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} onLoadBundle={loadSavedBundle} onLoadInvestigation={loadSavedInvestigation} onLoadReceived={loadReceivedInvestigation} pcapSession={pcapSession} bundles={savedBundles} investigations={savedInvestigations} receivedInvestigations={receivedInvestigations} currentBundle={bundle} visibleGhostsCount={visibleGhosts.length} communityGhostsCount={communityGhosts.length} workspace={{ name: invName || (currentInvestigationId ? "Saved Investigation" : "Current Investigation"), status: invStatus, notes: invNotes, classification: customClassification, pinnedCount: pinnedIds.size, pinnedObjects: [...pinnedIds].map(id => nonRels.find(o => o.id === id)).filter(Boolean), analystLogCount: analystLog.length }} ui={{ B: activeB, fs: activeFs, HP, AppShell, Logo, TypeIcon, getConf }} />;
  if (page === "settings") return <UserSettingsPage user={user} theme={theme} onThemeChange={handleThemeChange} fontSize={fontScale} onFontSizeChange={handleFontSizeChange} onNavigate={setPage} onLogout={handleLogout} onUserUpdate={setUser} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell }} />;
  if (page === "dashboards") return <DashboardsPage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell, TypeIcon, getConf }} />;
  if (page === "knowledge") return <KnowledgePage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onOpenBundle={goPreview} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell, TypeIcon, getConf }} />;
  if (page === "graph") return (
    <Suspense fallback={<div style={{ color: activeB.textDim, background: activeB.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Satoshi', sans-serif" }}>Loading the graph…</div>}>
      <GraphPage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell, getConf, TypeIcon }} />
    </Suspense>
  );
  if (page === "create") return (
    <Suspense fallback={<div style={{ color: activeB.textDim, background: activeB.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Satoshi', sans-serif" }}>Loading…</div>}>
      <CreatePage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} onOpenBundle={goPreview} lockdown={lockdown} ui={{ B: activeB, fs: activeFs, HP, AppShell, getConf, TypeIcon }} />
    </Suspense>
  );
  if (page === "community") return <CommunityIntelPage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onImportGhosts={importCommunityGhosts} onOpenBundle={goPreview} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell, getConf }} />;
  if (page === "pcap") return <PCAPPage user={user} session={pcapSession} onSessionUpdate={updatePcapSession} onSessionReset={resetPcapSession} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onAddToBundle={handleAddToBundle} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} />;
  if (page === "sandbox") return <MalwareSandboxPage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onAddToBundle={handleAddToBundle} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} />;
  if (page === "darkweb") return <DarkWebPage user={user} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onAddToBundle={handleAddToBundle} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} />;
  if (page === "login") return <LoginPage onNavigate={setPage} user={user} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} onAuth={handleAuth} onResendVerification={handleResendVerification} lockdown={lockdown} />;
  if (page === "import") return <ImportPage onNavigate={setPage} user={user} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} onLoad={goPreview} />;
  if (page === "preview") return <BundlePreviewPage bundle={pendingBundle} onBack={leavePreview} backLabel={previewFrom.current ? "Back to profile" : null} onNavigate={setPage} user={user} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} onLoad={doLoad} />;
  if (page === "bundles") return (
    <>
      <MyBundlesPage user={user} bundles={savedBundles} investigations={savedInvestigations} receivedInvestigations={receivedInvestigations} onNavigate={setPage} onLogin={() => setPage("login")} onLogout={handleLogout} onLoad={loadSavedBundle} onLoadInvestigation={loadSavedInvestigation} onRename={renameBundle} onRenameInvestigation={renameInvestigation} onDelete={deleteBundle} onDeleteInvestigation={deleteInvestigation} onShare={setSharingInvestigation} onLoadReceived={loadReceivedInvestigation} onDismissReceived={dismissReceivedInvestigation} pcapSession={pcapSession} collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)} ui={{ B: activeB, fs: activeFs, HP, AppShell, GroupTabs }} />
      {sharingInvestigation && <ShareModal investigation={sharingInvestigation} onClose={() => setSharingInvestigation(null)} ui={{ B: activeB, fs: activeFs }} />}
    </>
  );
  if (detailObj) return <ObjectDetailPage obj={detailObj} objects={objects} relationships={rels} markingDefs={markingDefs} onBack={() => setDetailObj(null)} onNavigate={(o) => setDetailObj(o)} />;
  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="viewer" onNavigate={setPage} user={user} onLogin={() => setPage("login")} onLogout={handleLogout} pcapSession={pcapSession}
        collapsed={!sidebarOpen} onToggleCollapse={() => setSidebarOpen(p => !p)}
        categories={CATEGORIES} activeTypes={activeTypes} onToggleType={toggleType} objectCounts={counts} view={view} onSetView={setView} />
      <div style={{ ...ST.root, height: "100%", flex: 1, minWidth: 0 }}>
      <style>{`@import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');`}</style>
      <GroupTabs page="viewer" onNavigate={setPage} />

      <div style={ST.header}>
        <div style={{ fontSize: fs(11), color: B.textDim, fontWeight: 500 }}>
          {view.charAt(0).toUpperCase() + view.slice(1)} <span style={{ color: B.textDim, opacity: 0.5 }}>· {filtered.length} objects</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button onClick={() => fileRef.current?.click()} style={ST.loadBtn}>Load Bundle</button>
          {/* Saving, publishing and AI need an account and the server's AI key: not in the public preview. */}
          {!(lockdown && !user) && (<>
          <button onClick={saveBundle} style={{ ...ST.loadBtn, background: `linear-gradient(135deg, ${B.accent}, ${B.primary})`, color: B.white }}>Save Bundle</button>
          <button onClick={() => user ? setPublishOpen(true) : setPage("login")} style={{ ...ST.loadBtn, borderColor: "#14b8a655", color: "#14b8a6" }}>Publish Intel</button>
          {view !== "investigation" && <button onClick={saveInvestigation} style={{ ...ST.loadBtn, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, color: B.white }}>Save Investigation</button>}
          </>)}
          <input ref={fileRef} type="file" accept=".json" onChange={loadFile} style={{ display: "none" }} />
          <div style={{ position: "relative" }}>
            <ExportMenu objects={objects} bundle={bundle} />
          </div>
        </div>
      </div>
      <div style={{ ...ST.toolbar, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
          <AutocompleteInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            onSelect={s => setSearch(s)}
            suggestions={nonRels.map(o => o.name || o.id).filter(Boolean)}
            placeholder="Search..."
            style={ST.searchWrap}
            inputStyle={ST.searchInput}
            prefix={<span style={{ color: B.textDim, fontSize: fs(13) }}>⌕</span>}
          />
          {allLabels.map(l => {
            const on = activeLabels.has(l);
            return <button key={l} onClick={() => toggleLabel(l)} style={{ fontSize: fs(10), padding: "3px 10px", borderRadius: 10, border: `1px solid ${on ? B.accent : B.border}`, background: on ? B.accent + "18" : "transparent", color: on ? B.accent : B.textDim, cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>{l}</button>;
          })}
          {dateWindow && <span style={{ fontSize: fs(10), padding: "3px 10px", borderRadius: 10, border: `1px solid ${B.primary}44`, background: B.primary + "18", color: B.primary, fontFamily: "'Satoshi', sans-serif" }}>{new Date(dateWindow.start).toLocaleDateString()} - {new Date(dateWindow.end).toLocaleDateString()}</span>}
          {(activeTypes.size > 0 || activeLabels.size > 0 || dateWindow) && <button onClick={() => { setActiveTypes(new Set()); setActiveLabels(new Set()); setDateWindow(null); }} style={ST.clearBtn}>Clear ✕</button>}
        </div>
        <span style={{ fontSize: fs(10), color: B.textDim }}>{filtered.length} of {nonRels.length} objects</span>
        {view === "graph" && <span style={{ fontSize: fs(9), color: B.textDim, fontStyle: "italic" }}>drag to move · double-click to expand · shift+drag to select</span>}
      </div>
      {saveStatus && <div style={{ padding: "0 40px 12px", color: B.accent, fontSize: fs(11) }}>{saveStatus}</div>}

      {bundleConnections && bundleConnections.length > 0 && (() => {
        const addRelationships = () => {
          const newRels = bundleConnections.map(c => ({
            type: "relationship",
            spec_version: "2.1",
            id: `relationship--${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`,
            created: new Date().toISOString(),
            modified: new Date().toISOString(),
            relationship_type: "related-to",
            source_ref: c.sourceId,
            target_ref: c.targetId,
            description: `Auto-detected connection via shared term: ${c.reason}`,
          }));
          setBundle(b => ({ ...b, objects: [...(b.objects || []), ...newRels] }));
          setBundleConnections(null);
        };
        return (
          <div style={{ margin: "0 0 0 0", padding: "10px 20px", background: "linear-gradient(90deg, rgba(139,92,246,0.12), rgba(59,130,246,0.08))", borderBottom: `1px solid rgba(139,92,246,0.3)`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: fs(13), fontWeight: 700, color: "#a78bfa" }}>&#9889; {bundleConnections.length} new connection{bundleConnections.length !== 1 ? "s" : ""} detected</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
              {bundleConnections.slice(0, 4).map((c, i) => (
                <span key={i} style={{ fontSize: fs(11), padding: "2px 8px", borderRadius: 6, background: "rgba(139,92,246,0.15)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.25)" }}>
                  <span style={{ fontWeight: 600 }}>{c.sourceName.length > 20 ? c.sourceName.slice(0, 20) + "…" : c.sourceName}</span>
                  <span style={{ color: "#7c3aed", margin: "0 4px" }}>↔</span>
                  <span style={{ fontWeight: 600 }}>{c.targetName.length > 20 ? c.targetName.slice(0, 20) + "…" : c.targetName}</span>
                  <span style={{ color: "#6d28d9", marginLeft: 4, fontStyle: "italic" }}>"{c.reason}"</span>
                </span>
              ))}
              {bundleConnections.length > 4 && <span style={{ fontSize: fs(11), color: "#7c3aed" }}>+{bundleConnections.length - 4} more</span>}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={addRelationships} style={{ fontSize: fs(12), padding: "5px 14px", borderRadius: 6, border: "1px solid rgba(139,92,246,0.5)", background: "rgba(139,92,246,0.2)", color: "#c4b5fd", cursor: "pointer", fontWeight: 700, fontFamily: "'Satoshi', sans-serif" }}>
                + Add {bundleConnections.length} Relationship{bundleConnections.length !== 1 ? "s" : ""}
              </button>
              <button onClick={() => setBundleConnections(null)} style={{ fontSize: fs(12), padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(139,92,246,0.2)", background: "none", color: "#7c3aed", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>Dismiss</button>
            </div>
          </div>
        );
      })()}

      <div style={ST.body}>
        <div style={ST.main}>
          {view === "graph" && (
            <Suspense fallback={<div style={{ padding: 24, color: B.textDim, fontSize: fs(12) }}>Loading the graph…</div>}>
              <StixGraph objects={graphObjects} ui={{ B, fs, HP, getConf }} style={{ position: "absolute", inset: 0 }}
                selectedId={selected?.id || null} onSelect={sel}
                onCreateRelationship={createRelationship} renderRelModal={(m) => <RelCreateModal source={m.source} target={m.target} onConfirm={m.onConfirm} onClose={m.onClose} />}
                ghostObjects={ghostMode !== "panel" ? visibleGhosts : []} ghostOpacity={ghostOpacity} onGhostClick={addGhostToBundle} ghostTarget={ghostTarget} toolbarRight={visibleGhosts.length ? 170 : 0}
                onExpand={(objs) => setBundle(b => ({ ...b, objects: [...(b.objects || []), ...objs] }))} />
            </Suspense>
          )}
          {view === "graph" && visibleGhosts.length > 0 && (() => {
            return (
              <>
                {/* Ghost toggle button — floats over top-right of graph */}
                <button
                  onClick={() => setGhostOpen(o => !o)}
                  style={{ position: "absolute", top: 14, right: ghostOpen ? 288 : 14, zIndex: 30, display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, fontSize: fs(13), fontWeight: 800, fontFamily: "'Satoshi', sans-serif", cursor: "pointer", transition: "right 0.25s ease", background: ghostOpen ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.22)", color: "rgba(255,255,255,0.82)", backdropFilter: "blur(8px)", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
                >
                  <span style={{ opacity: 0.78, fontSize: fs(17), lineHeight: 1 }}>◌</span> Ghosts {visibleGhosts.length > 0 && <span style={{ background: "rgba(255,255,255,0.16)", borderRadius: 12, padding: "2px 9px", fontSize: fs(11) }}>{visibleGhosts.length}</span>}
                </button>

                {/* Ghost panel */}
                {ghostOpen && (
                  <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 264, zIndex: 20, display: "flex", flexDirection: "column", background: "rgba(10,10,18,0.55)", backdropFilter: "blur(18px)", borderLeft: "1px solid rgba(255,255,255,0.09)", transition: "opacity 0.2s" }}>

                    {/* Panel header */}
                    <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: fs(13), fontWeight: 800, color: "rgba(255,255,255,0.85)", letterSpacing: 0.3 }}>Ghost Objects</span>
                        <span style={{ fontSize: fs(10), color: "rgba(255,255,255,0.35)", marginLeft: "auto" }}>{visibleGhosts.length} available</span>
                      </div>
                      {/* Mode toggle */}
                      <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                        {[["panel","Panel only"],["both","Both"],["graph","Graph only"]].map(([m, l]) => (
                          <button key={m} onClick={() => setGhostMode(m)} style={{ flex: 1, padding: "4px 2px", fontSize: fs(9), borderRadius: 5, border: `1px solid rgba(255,255,255,${ghostMode === m ? "0.35" : "0.1"})`, background: ghostMode === m ? "rgba(255,255,255,0.11)" : "transparent", color: `rgba(255,255,255,${ghostMode === m ? "0.85" : "0.35"})`, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontWeight: ghostMode === m ? 700 : 400, transition: "all 0.15s" }}>
                            {l}
                          </button>
                        ))}
                      </div>
                      {/* Opacity slider */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: fs(10), color: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}>Visibility</span>
                        <input
                          type="range" min={10} max={100} value={Math.round(ghostOpacity * 100)}
                          onChange={e => setGhostOpacity(Number(e.target.value) / 100)}
                          style={{ flex: 1, accentColor: "rgba(255,255,255,0.5)", cursor: "pointer" }}
                        />
                        <span style={{ fontSize: fs(10), color: "rgba(255,255,255,0.35)", width: 28, textAlign: "right" }}>{Math.round(ghostOpacity * 100)}%</span>
                      </div>
                    </div>

                    {/* Ghost cards list — hidden in graph-only mode */}
                    {ghostMode === "graph" ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 20 }}>
                        <span style={{ fontSize: fs(24), opacity: 0.18 }}>◌</span>
                        <span style={{ fontSize: fs(11), color: "rgba(255,255,255,0.3)", textAlign: "center", lineHeight: 1.5 }}>Ghosts are visible on the canvas. Click to preview, or drag them around their related node.</span>
                      </div>
                    ) : (
                    <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                      {visibleGhosts.map(g => {
                        const conf = getConf(g.type);
                        const isPreviewed = ghostPreviewed === g.id;
                        return (
                          <div
                            key={g.id}
                            onClick={() => setGhostPreviewed(isPreviewed ? null : g.id)}
                            style={{ opacity: ghostOpacity, border: `1px dashed ${conf.color}66`, borderRadius: 8, padding: isPreviewed ? "12px 12px" : "9px 12px", cursor: "pointer", background: isPreviewed ? `${conf.color}0d` : "rgba(255,255,255,0.03)", transition: "all 0.18s", position: "relative", overflow: "hidden" }}
                          >
                            {/* Ghost shimmer line */}
                            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${conf.color}55, transparent)` }} />

                            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: isPreviewed ? 8 : 0 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: conf.color, flexShrink: 0, boxShadow: `0 0 6px ${conf.color}88` }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: fs(11), fontWeight: 700, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</div>
                                <div style={{ fontSize: fs(9), color: conf.color + "aa", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 1 }}>{conf.label}</div>
                              </div>
                              <span style={{ fontSize: fs(9), color: "rgba(255,255,255,0.22)", flexShrink: 0 }}>{isPreviewed ? "▲" : "▼"}</span>
                            </div>

                            {isPreviewed && (
                              <>
                                <div style={{ fontSize: fs(9), color: "rgba(255,255,255,0.35)", marginBottom: 6, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
                                  From: <span style={{ color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>{g.fromBundle}</span>
                                </div>
                                <div style={{ fontSize: fs(11), color: "rgba(255,255,255,0.55)", lineHeight: 1.5, marginBottom: 10 }}>{g.description}</div>
                                <button
                                  onClick={e => { e.stopPropagation(); addGhostToBundle(g); }}
                                  style={{ width: "100%", padding: "7px 0", borderRadius: 6, border: `1px solid ${conf.color}66`, background: `${conf.color}22`, color: conf.color, fontSize: fs(11), fontWeight: 700, fontFamily: "'Satoshi', sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                                >
                                  + Add &amp; Auto-Relate
                                </button>
                              </>
                            )}

                            {!isPreviewed && (
                              <div style={{ fontSize: fs(9), color: "rgba(255,255,255,0.22)", marginTop: 4 }}>from {g.fromBundle}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    )}

                    {/* Panel footer hint */}
                    <div style={{ padding: "8px 14px", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: fs(9), color: "rgba(255,255,255,0.22)", lineHeight: 1.5 }}>
                      Click a ghost to preview · Drag canvas ghosts to reposition them · Add &amp; Auto-Relate wires up the relationship automatically
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {view === "list" && <div style={ST.listView}>{filtered.map(obj => { const c = getConf(obj.type), isSel = selected?.id === obj.id; const markings = getMarkings(obj, markingDefs); const mark = markings[0]; return (
            <div key={obj.id} onClick={() => sel(obj)} style={{ ...ST.listItem, ...(isSel ? { borderColor: c.color, background: c.color + "06" } : {}) }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <TypeIcon type={obj.type} color={c.color} size={13} />
                <span style={{ fontSize: fs(10), color: c.color, fontWeight: 500 }}>{c.label}</span>
                {mark && <span style={{ marginLeft: "auto", fontSize: fs(9), padding: "1px 6px", borderRadius: 4, background: mark.color + "22", color: mark.color, fontWeight: 700 }}>{mark.name}</span>}
                {(() => { const ci = getConfidenceInfo(obj.confidence); return ci ? <span style={{ fontSize: fs(9), padding: "1px 6px", borderRadius: 4, background: ci.color + "22", color: ci.color, fontWeight: 700 }}>{ci.label}</span> : null; })()}
              </div>
              <div style={{ fontSize: fs(13), fontWeight: 600, color: B.text }}>{getDisplayName(obj)}</div>
              {obj.description && <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 4, lineHeight: 1.4 }}>{obj.description.slice(0, 110)}…</div>}
            </div>
          ); })}
          {rels.filter(r => !search || r.relationship_type.toLowerCase().includes(search.toLowerCase())).map(r => {
            const srcObj = objects.find(o => o.id === r.source_ref);
            const tgtObj = objects.find(o => o.id === r.target_ref);
            const isSel = selected?.id === r.id;
            const rc = getConf("relationship");
            const mark = getMarkings(r, markingDefs)[0];
            return (
              <div key={r.id} onClick={() => sel(r)} style={{ ...ST.listItem, ...(isSel ? { borderColor: rc.color, background: rc.color + "06" } : {}) }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <TypeIcon type="relationship" color={rc.color} size={13} />
                  <span style={{ fontSize: fs(10), color: rc.color, fontWeight: 500 }}>{rc.label}</span>
                  {mark && <span style={{ marginLeft: "auto", fontSize: fs(9), padding: "1px 6px", borderRadius: 4, background: mark.color + "22", color: mark.color, fontWeight: 700 }}>{mark.name}</span>}
                </div>
                <div style={{ fontSize: fs(12), fontWeight: 600, color: B.text }}>
                  <span style={{ color: B.textMuted }}>{srcObj?.name || r.source_ref}</span>
                  <span style={{ color: rc.color }}> → {r.relationship_type} → </span>
                  <span style={{ color: B.textMuted }}>{tgtObj?.name || r.target_ref}</span>
                </div>
                {r.description && <div style={{ fontSize: fs(11), color: B.textMuted, marginTop: 4, lineHeight: 1.4 }}>{r.description.slice(0, 110)}…</div>}
              </div>
            );
          })}
          </div>}
          {view === "timeline" && <TimelineView objects={filtered} scrubberObjects={baseFiltered} onSelect={sel} selectedId={selected?.id} dateWindow={dateWindow} onDateWindowChange={setDateWindow} />}
          {view === "reports" && <ReportsView objects={filtered} allObjects={objects} onSelect={sel} selectedId={selected?.id} />}
          {view === "dashboard" && <DashboardView objects={filtered} allObjects={objects} relationships={rels} markingDefs={markingDefs} onSelect={sel} />}
          {view === "mitre" && <MITREView objects={filtered} onSelect={sel} />}
          {view === "geo" && <Suspense fallback={<div style={{ padding: 24, color: B.textDim, fontSize: fs(12) }}>Loading the map…</div>}><GeoView objects={filtered} relationships={rels} onSelect={sel} ui={{ B, fs }} /></Suspense>}
          {view === "cases" && <CasesView objects={filtered} relationships={rels} markingDefs={markingDefs} onSelect={sel} />}
          {view === "investigation" && <InvestigationView objects={objects} markingDefs={markingDefs} invName={invName} setInvName={setInvName} invStatus={invStatus} setInvStatus={setInvStatus} invNotes={invNotes} setInvNotes={setInvNotes} pinnedIds={pinnedIds} evidenceStatuses={evidenceStatuses} onSetEvidenceStatus={setEvidenceStatus} onUnpin={unpinObj} analystLog={analystLog} setAnalystLog={setAnalystLog} customClassification={customClassification} setCustomClassification={setCustomClassification} onSave={saveInvestigation} onShare={shareCurrentInvestigation} onSelect={sel} user={user} />}
        </div>
        {selected && <DetailPanel obj={selected} objects={objects} relationships={rels} markingDefs={markingDefs} onSelect={sel} onClose={() => setSelected(null)} onViewFull={(o) => setDetailObj(o)} onPin={pinObj} isPinned={pinnedIds.has(selected?.id)} onUpdateLabels={updateObjectLabels} user={user} isGhostOrigin={addedGhostIds.has(selected?.id)} onRevertGhost={handleRevertGhost} />}
      </div>

      {/* Floating AI button */}
      {!chatOpen && !lockdown && (
        <button onClick={() => setChatOpen(true)} style={ST.aiBtn}>
          <span style={{ fontSize: fs(18) }}>✦</span>
          <span style={{ fontSize: fs(11), fontWeight: 700 }}>Ask AI</span>
        </button>
      )}

      {chatOpen && (
        <AIChatPanel objects={objects} relationships={rels} onClose={() => setChatOpen(false)} />
      )}
      {sharingInvestigation && <ShareModal investigation={sharingInvestigation} onClose={() => setSharingInvestigation(null)} ui={{ B, fs }} />}
      {publishOpen && <PublishIntelModal bundle={bundle} filteredObjects={filtered} classification={customClassification} onClose={() => setPublishOpen(false)} onPublished={() => setSaveStatus("Intel published to the community feed.")} ui={{ B, fs }} />}
      </div>
    </div>
  );
}

// Getter-based ST: each property re-reads module-level B at access time,
// so all components (inside and outside App) always see the current theme.
const ST = {
  get root() { return { fontFamily: "'Satoshi', sans-serif", background: B.bg, color: B.text, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }; },
  get header() { return { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${B.border}`, background: B.bgPanel }; },
  get title() { return { fontSize: fs(18), fontWeight: 900, color: B.white, letterSpacing: 0.5 }; },
  get subtitle() { return { fontSize: fs(11), color: B.textDim, marginTop: 1, letterSpacing: 2, textTransform: "uppercase" }; },
  get tabGroup() { return { display: "flex", background: B.bgCard, borderRadius: 6, padding: 2, gap: 1 }; },
  get tab() { return { background: "transparent", border: "none", color: B.textDim, padding: "6px 16px", borderRadius: 5, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }; },
  get tabActive() { return { background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, color: B.white }; },
  get loadBtn() { return { background: "transparent", border: `1px solid ${B.border}`, color: B.textMuted, padding: "7px 16px", borderRadius: 6, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }; },
  get toolbar() { return { padding: "10px 20px", borderBottom: `1px solid ${B.border}`, display: "flex", gap: 10, alignItems: "center" }; },
  get searchWrap() { return { display: "flex", alignItems: "center", gap: 6, background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 6, padding: "0 12px" }; },
  get searchInput() { return { background: "transparent", border: "none", color: B.text, padding: "8px 0", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif", width: 200, outline: "none" }; },
  get clearBtn() { return { background: "none", border: `1px solid ${B.accent}33`, color: B.accent, fontSize: fs(12), padding: "4px 12px", borderRadius: 12, cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }; },
  get body() { return { flex: 1, display: "flex", overflow: "hidden" }; },
  get sidebar() { return { background: B.bgPanel, borderRight: `1px solid ${B.border}`, overflow: "hidden", flexShrink: 0, transition: "width 0.2s ease", display: "flex", flexDirection: "column" }; },
  get main() { return { flex: 1, position: "relative", overflow: "hidden" }; },
  get listView() { return { padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12, overflow: "auto", height: "100%" }; },
  get listItem() { return { background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, padding: 16, cursor: "pointer" }; },
  get detailPanel() { return { width: 440, background: B.bgPanel, borderLeft: `1px solid ${B.border}`, padding: 24, overflow: "auto", position: "relative", flexShrink: 0 }; },
  get closeBtn() { return { position: "absolute", top: 14, right: 16, background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(20), fontFamily: "'Satoshi', sans-serif" }; },
  get detailName() { return { fontSize: fs(20), fontWeight: 700, color: B.white, margin: "0 0 10px 0" }; },
  get detailDesc() { return { fontSize: fs(14), color: B.textMuted, lineHeight: 1.7, margin: 0 }; },
  get codeBox() { return { background: B.bg, border: `1px solid ${B.border}`, borderRadius: 6, padding: 14, marginTop: 12 }; },
  get miniLabel() { return { fontSize: fs(11), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10, fontWeight: 700 }; },
  get propRow() { return { padding: "9px 0", borderBottom: `1px solid ${B.border}` }; },
  get propKey() { return { display: "block", color: B.textDim, textTransform: "capitalize", fontSize: fs(11), letterSpacing: 0.8, fontWeight: 600, marginBottom: 4 }; },
  get propVal() { return { display: "block", color: B.textMuted, fontSize: fs(14), wordBreak: "break-word" }; },
  get relRow() { return { display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderRadius: 6, cursor: "pointer", marginBottom: 4, background: B.bg }; },
  get aiBtn() { return { position: "fixed", bottom: 24, right: 24, background: `linear-gradient(135deg, ${B.primary}, ${B.accent})`, border: "none", borderRadius: 24, color: B.white, padding: "12px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "'Satoshi', sans-serif", boxShadow: "0 4px 20px rgba(168,85,247,0.35)", zIndex: 99 }; },
};

